// ============================================
// Phone / SMS System
// Replaces PhoneOverlayController.cs + MessagesApp.cs
// ============================================

import { events, Events } from '../engine/events.js';
import { UITags, SPEAKER_DISPLAY_NAMES } from '../engine/constants.js';
import { variableManager } from '../engine/variables.js';
import { inventoryManager } from '../systems/inventory.js';

// Comfort-training gear tags (4 lines x 3 tiers). Ownership of any of these
// is the real signal — the old owns_*_tN Ink VARs never existed.
const COMFORT_TRAINING_TAGS = [
    'waist_trainer_starter', 'waist_trainer_advanced', 'waist_trainer_expert',
    'nipple_clamps_light', 'nipple_clamps_padded', 'nipple_clamps_heavy',
    'wrist_cuffs_pink', 'wrist_cuffs_locking', 'wrist_cuffs_heavy',
    'wrist_cuffs_leather', 'ankle_cuffs_leather',
    'heels_kitten', 'heels_stiletto', 'heels_platform_locking',
];

// ── Phone-app stat helpers (module-level: every app builder method uses
// them, per-scope closures caused an addFame ReferenceError in
// _buildSissySocialPost/_buildSissySocialDMs). Route gains through the Ink
// helpers so perk bonuses, clamps and diminishing returns apply — raw
// story-var adds bypassed fame_gain/fem_gain entirely (foundby:Alpha Rabbit
// + 0.2.8 audit finding 16).
function _appAddFame(iv, setVar, n) {
    if (!(n > 0)) return;
    const bonus = iv('perk_social_butterfly') ? 1 : 0;
    setVar('sissy_fame', Math.min(100, iv('sissy_fame') + n + bonus));
}
function _appAddFem(iv, addStat, n) {
    if (!(n > 0)) return;
    const r = variableManager.evalSafe('fem_gain', [n]);
    if (r === undefined) { addStat('Femininity', n); return; }
    events.emit(Events.STAT_CHANGED, 'Femininity', iv('Femininity'));
}
function _appAddSub(iv, addStat, n) {
    if (!(n > 0)) return;
    const r = variableManager.evalSafe('sub_gain', [n]);
    if (r === undefined) { addStat('Submissiveness', n); return; }
    events.emit(Events.STAT_CHANGED, 'Submissiveness', iv('Submissiveness'));
}
function _appAddShame(iv, addStat, n) {
    if (!(n > 0)) return;
    const r = variableManager.evalSafe('shame_gain', [n]);
    if (r === undefined) { addStat('Shame', n); return; }
    events.emit(Events.STAT_CHANGED, 'Shame', iv('Shame'));
}
import { L } from '../systems/localization.js';
import { isPat } from '../engine/pgate.js';
import { getImageManifest, getImageCache } from './story-content.js';
import { achievementManager, AchievementCategory } from '../systems/achievements.js';
import { getStorylineData } from '../systems/storylines.js';
import { renderProgressCard, renderStorylineGroup } from './components/progress-card.js';
import { escapeHtml } from '../utils/escape.js';
import { galleryUI } from './gallery.js';

/**
 * Write a variable to Ink story state only.
 *
 * Observed vars (OBSERVED_VARIABLES in variables.js) auto-sync to
 * variableManager.baseVar/.flags via the inkjs observer. Non-observed
 * vars stay in Ink only. Writing them via variableManager.setValue
 * pollutes baseVar, which then persists across save/load and resurrects
 * stale values (the DareMe counter regression v0.2.5.1, dare flags,
 * grindr_profile_type drift). See .claude/rules/save-state-drift.md
 * rule 1 + tools/lint_save_state.py.
 */
function syncVar(story, name, value) {
    try { story.variablesState[name] = value; } catch(e) {}
}

// ── NPC contact configurations ──────────────────────────────────────────
const NPC_CONFIGS = {
    master:      { defaultName: 'Master',       color: '#9b59b6', bubbleClass: 'sms-bubble-master',     avatarLetter: 'M', avatar: 'Portraits/master_normal' },
    girlfriend:  { defaultName: 'Girlfriend',    color: '#e88cb8', bubbleClass: 'sms-bubble-girlfriend', avatarLetter: 'G', avatar: 'Girlfriend/girlfriend_arrival_door' },
    sugardaddy:  { defaultName: 'Daddy',         color: '#e8c860', bubbleClass: 'sms-bubble-sugar',     avatarLetter: 'R' },
    bully:       { defaultName: 'Beach Bully',   color: '#e05050', bubbleClass: 'sms-bubble-bully',     avatarLetter: 'B', avatar: 'Beach/beach_bully_alone' },
    fan:         { defaultName: 'Hot_Fan_69',    color: '#5bc0de', bubbleClass: 'sms-bubble-fan',       avatarLetter: 'F' },
    sasha:       { defaultName: 'Sasha',         color: '#c470e0', bubbleClass: 'sms-bubble-sasha',     avatarLetter: 'S', avatar: 'Portraits/sasha' },
    mira:        { defaultName: 'Mira',          color: '#d4789a', bubbleClass: 'sms-bubble-default',   avatarLetter: 'M', avatar: 'Portraits/mira' },
    unknown_number: { defaultName: 'Unknown',    color: '#888',    bubbleClass: 'sms-bubble-unknown',   avatarLetter: '?' },
    dating_match:   { defaultName: 'Match',      color: '#e06878', bubbleClass: 'sms-bubble-dating',    avatarLetter: 'D' },
    vip_guest:      { defaultName: 'VIP Guest',  color: '#c470e0', bubbleClass: 'sms-bubble-vip',       avatarLetter: 'V' },
    // GD-04 v0.2.6: Independent-path inner-note speaker (no NPC, no portrait,
    // self-talk style). Used for the Day 10/17/25 self-reflection beats so
    // they show up in the SMS thread as a journal-style entry rather than
    // routing to "Unknown".
    self_note:      { defaultName: 'You',        color: '#7a8090', bubbleClass: 'sms-bubble-unknown',   avatarLetter: '✎' },
};

// Phone apps for the home screen grid
const PHONE_APPS = [
    { id: 'messages',    name: 'Messages',    l10n: 'phone.messages',   icon: '\uD83D\uDCE8', iconImg: 'icon_messages',    alwaysVisible: true },
    { id: 'completion',  name: 'Progress',    l10n: 'phone.progress',   icon: '\uD83D\uDCCA', iconImg: 'icon_completion',   alwaysVisible: true },
    { id: 'appstore',    name: 'App Store',   l10n: 'phone.app_store',  icon: '\uD83D\uDED2', iconImg: 'icon_appstore',     alwaysVisible: true },
    { id: 'grindr',      name: 'GrindrR',     icon: '\uD83D\uDFE1', iconImg: 'icon_grindr',       inkVar: 'grindr_installed' },
    { id: 'hypno',       name: 'HypnoMind',   icon: '\uD83C\uDF00', iconImg: 'icon_hypno',        inkVar: 'hypno_app_installed', storeDesc: 'hypno_app.desc' },
    { id: 'onlyfems',    name: 'OnlyFems',    icon: '\uD83D\uDC8E', iconImg: 'icon_onlyfems',     inkVar: 'onlyfems_installed', storeDesc: 'onlyfems.desc', reqFem: 20 },
    { id: 'sissysocial', name: 'SissySocial', icon: '\uD83E\uDD33', iconImg: 'icon_sissysocial',  inkVar: 'sissysocial_installed' },
    { id: 'jobfinder',   name: 'JobFinder',   icon: '\uD83D\uDCBC', iconImg: 'icon_jobfinder',    inkVar: 'jobfinder_installed' },
    { id: 'dareme',      name: 'DareMe',      icon: '\uD83D\uDD25', iconImg: 'icon_dareme',       inkVar: 'dareme_installed', storeDesc: 'dareme.desc', reqFem: 20, onInstall: 'dareme' },
    { id: 'gallery',     name: 'Gallery',     icon: '\uD83D\uDDBC\uFE0F', iconImg: 'icon_gallery',    alwaysVisible: true },
    { id: 'cheats',      name: 'Cheats',      l10n: 'phone.cheats',    icon: '\uD83D\uDD27', iconImg: 'icon_console',      inkVar: 'debug_console_installed', patreonOnly: true },
];

class PhoneUI {
    constructor() {
        /** @type {Map<string, Array<object>>} NPC id -> messages array */
        this._threads = new Map();

        /** @type {Set<string>} NPC ids with unread messages */
        this._unread = new Set();

        /** @type {string|null} Currently viewed chat npcId */
        this._activeChat = null;

        /** @type {'home'|'contacts'|'chat'} Current phone view */
        this._view = 'home';

        /** @type {boolean} Whether phone overlay is visible */
        this._isOpen = false;

        /** @type {HTMLElement|null} Cached root container */
        this._root = null;

        /** @type {number} Current day (updated from Ink) */
        this._currentDay = 1;

        /** @type {number} Current time slot (0-3) */
        this._currentTimeSlot = 0;

        /** @type {Map<string, string>} Dynamic name overrides (master -> MasterName, etc.) */
        this._dynamicNames = new Map();

        /** @type {ReturnType<typeof events.createScope>} Scoped event subs; destroy() detaches all. */
        this._scope = events.createScope('phone');
        this._scope.on(Events.FORCED_EVENT_STARTED, () => this.close());

        this._bindEvents();
    }

    /** Detach all event listeners. Called by tests + a future re-init path;
     *  production singleton lives for the browser tab's lifetime. */
    destroy() {
        this._scope.destroy();
    }

    // ── Public API ──────────────────────────────────────────────────────

    /** Initialize and build DOM inside the overlay container. */
    init() {
        this._root = document.getElementById('overlay-phone');
        if (!this._root) {
            this._root = this._createOverlayContainer();
        }
        this._buildPhoneUI();
    }

    /** Open the phone overlay. */
    open() {
        if (!this._root) this.init();
        this._isOpen = true;
        this._root.classList.remove('hidden');
        this._root.classList.add('phone-open');
        document.body.classList.add('phone-active');
        this._showView('home');
        events.emit(Events.OVERLAY_OPEN, 'overlay-phone');
    }

    /** Close the phone overlay. */
    close() {
        this._isOpen = false;
        document.body.classList.remove('phone-active');
        if (this._root) {
            this._root.classList.remove('phone-open');
        }
        this._activeChat = null;
        // Let layout.closeOverlay handle hiding + backdrop
        events.emit(Events.OVERLAY_CLOSE, 'overlay-phone');
    }

    /** Get total unread count. */
    getUnreadCount() {
        return this._unread.size;
    }

    /** Update dynamic NPC name (e.g. MasterName from Ink). */
    setDynamicName(npcId, name) {
        this._dynamicNames.set(npcId, name);
    }

    /** Update time context for message separators. */
    setTimeContext(day, timeSlot) {
        this._currentDay = day;
        this._currentTimeSlot = timeSlot;
    }

    // ── Event Bindings ──────────────────────────────────────────────────

    _bindEvents() {
        this._scope.on(Events.TAG_SMS, (data) => this._handleSMS(data));
        this._scope.on(Events.TAG_SMS_REPLY, (data) => this._handleSMSReply(data));

        // Clean up body class when layout closes phone (e.g. closeAllOverlays on load)
        this._scope.on(Events.OVERLAY_CLOSE, (id) => {
            if (id === 'overlay-phone') {
                this._isOpen = false;
                document.body.classList.remove('phone-active');
            }
        });

        this._scope.on(Events.VARIABLE_CHANGED, (name, value) => {
            if (name === 'Day') this._currentDay = value;
            if (name === 'TimeSlot') this._currentTimeSlot = value;
            if (name === 'MasterName') this._dynamicNames.set('master', value);
            if (name === 'GirlfriendName') this._dynamicNames.set('girlfriend', value);
            if (name === 'PlayerName') this._dynamicNames.set('player', value);
        });

        // Strings (PlayerName/MasterName/GirlfriendName) only fire NAME_CHANGED,
        // not VARIABLE_CHANGED, the editor's pushVariablesToInk path emits
        // NAME_CHANGED for typeof string in _triggerObserver. Without this
        // listener the SMS contact label shows the defaultName ('Master')
        // instead of the player-chosen name. (foundby:kaneeden v0.2.5.7.3)
        this._scope.on(Events.NAME_CHANGED, (name, value) => {
            if (name === 'MasterName') this._dynamicNames.set('master', value);
            else if (name === 'GirlfriendName') this._dynamicNames.set('girlfriend', value);
            else if (name === 'PlayerName') this._dynamicNames.set('player', value);
        });

        this._scope.on(Events.LOAD_COMPLETED, () => {
            // Restore threads from saved data if available
        });
    }

    // ── SMS Handling ────────────────────────────────────────────────────

    _handleSMS(data) {
        const { npcId, text, image, type, label, goto } = data;

        // Dedup guard: skip if last message from this NPC has identical text/image/label
        const thread = this._getThread(npcId);
        if (thread.length > 0) {
            const last = thread[thread.length - 1];
            const newText = L.interpolate(text) || null;
            if (last.from === 'npc' && last.text === newText && last.image === (image || null) && last.label === (label || null)) {
                return; // Duplicate SMS, skip
            }
        }

        // Read Day/TimeSlot from Ink directly (observers may be blocked during refreshView)
        let smsDay = this._currentDay;
        let smsSlot = this._currentTimeSlot;
        try {
            const story = window._sm?.currentStory;
            if (story) {
                const inkDay = story.variablesState['Day'];
                const inkSlot = story.variablesState['TimeSlot'];
                if (typeof inkDay === 'number') smsDay = inkDay;
                if (typeof inkSlot === 'number') smsSlot = inkSlot;
            }
        } catch(e) {}

        const msg = {
            id: Date.now() + Math.random(),
            type: type || 'sms',
            from: 'npc',
            text: L.interpolate(text) || null,
            image: image || null,
            label: label || null,
            goto: goto || null,
            day: smsDay,
            timeSlot: smsSlot,
            timestamp: Date.now(),
        };

        this._addMessage(npcId, msg);
        this._unread.add(npcId);
        this._updateBadge();
        this._showNotification(npcId, msg.text);

        // UX-05: spam-hint when ≥3 NPC SMS arrive on the same Day-slot.
        // Players with 5 active chains (Master/Sarah/Bully/Sugar/Fan) often hit
        // 4-5 morning SMS pile-ups and quit before discovering the mute toggles
        // (Settings > Notifications, 3 UI layers deep). One-shot per save.
        try {
            const slotKey = `d${smsDay}_t${smsSlot}`;
            if (this._smsSlotCount?.key !== slotKey) {
                this._smsSlotCount = { key: slotKey, n: 0 };
            }
            this._smsSlotCount.n++;
            if (this._smsSlotCount.n >= 3) {
                let shown = false;
                try { shown = localStorage.getItem('PnO_smsSpamHintShown') === 'true'; } catch(e) {}
                if (!shown) {
                    try { localStorage.setItem('PnO_smsSpamHintShown', 'true'); } catch(e) {}
                    const hint = (typeof L !== 'undefined' && L.get && L.get('phone.spam_hint'))
                        || 'Tip: Mute SMS chains in Settings → Notifications.';
                    events.emit(Events.TOAST_REQUEST, hint, 'pno-toast pno-toast-info');
                }
            }
        } catch(e) { /* hint is non-critical */ }

        if (this._isOpen && this._activeChat === npcId) {
            this._renderChatView(npcId);
        } else if (this._isOpen && this._view === 'contacts') {
            this._renderContactList();
        }
    }

    _handleSMSReply(data) {
        const { npcId, options } = data;
        // Parse reply options: "Reply>>Response;;Reply>>Response"
        // First part without ">>" is NPC context message, not a reply button
        const parsed = this._parseReplyOptions(options);

        // If first option has no npcResponse, it's an NPC message, not a reply
        if (parsed.length > 1 && parsed[0].npcResponse === null) {
            const contextMsg = parsed.shift();
            const msg = {
                id: Date.now() + Math.random(),
                type: 'sms',
                from: 'npc',
                text: contextMsg.playerText,
                image: null,
                day: this._currentDay,
                timeSlot: this._currentTimeSlot,
                timestamp: Date.now(),
            };
            this._addMessage(npcId, msg);
            this._unread.add(npcId);
            this._updateBadge();
        }

        // Store pending replies on thread (persists until player clicks).
        // FIFO queue, NOT a plain overwrite: two #smsreply for the SAME sender
        // in one drain (e.g. the morning cage-inspection SMS + the Master task
        // list, both #smsreply:master) must each stay answerable. Pre-fix this
        // was `thread._pendingReplies = parsed`, so the later set clobbered the
        // earlier one — the player could only answer the last SMS and the first
        // reply's [goto:] arc (PublicChallenges_Begin, etc.) silently died.
        // foundby:Nigyl 0.3.0.6 ("cage inspection supersedes the task, can't
        // reply yes to the tasks"). The active set stays in _pendingReplies;
        // later sets wait in _replyQueue and promote as each is answered
        // (_promoteQueuedReplies). See BUG_PATTERNS § SMS reply clobber.
        const thread = this._getThread(npcId);
        if (thread._pendingReplies && thread._pendingReplies.length > 0) {
            // An unanswered reply-set is already active in this thread — defer.
            if (!thread._replyQueue) thread._replyQueue = [];
            thread._replyQueue.push(parsed);
            this._unread.add(npcId);
            this._updateBadge();
            return;
        }
        thread._pendingReplies = parsed;

        if (this._isOpen && this._activeChat === npcId) {
            // Already viewing this chat, render buttons immediately
            this._renderReplyButtons(npcId, parsed);
        } else {
            // Auto-open phone so the reply buttons are reachable, but keep the
            // NPC unread so the left-panel badge still shows a new-message
            // count. _openChat clears unread; re-add it after so the player
            // gets a visible "you have a new message" hint even if they walk
            // away from the auto-opened chat without picking a reply.
            if (!this._isOpen) this.open();
            this._openChat(npcId);
            this._unread.add(npcId);
            this._updateBadge();
        }
    }

    _parseReplyOptions(raw) {
        return raw.split(';;').map(opt => {
            const parts = opt.split('>>');
            const playerText = parts[0].trim();
            const npcResponse = parts.length > 1 ? parts[1].trim() : null;
            // Interpolate {MasterName}/{PlayerName}/{GirlfriendName} etc. here.
            // inkjs does NOT evaluate {var} inside tag payloads, and unlike
            // _handleSMS (which L.interpolate's message text) the reply path
            // rendered the raw token straight into the button + the recorded
            // player message + the NPC response. foundby player 0.2.7.2: the
            // cage-inspection reply button showed "Yes {MasterName}" instead of
            // the Master's name. Doing it at parse time covers all three sinks.
            return {
                playerText: L.interpolate(playerText) || playerText,
                npcResponse: npcResponse ? (L.interpolate(npcResponse) || npcResponse) : null,
            };
        }).filter(o => o.playerText);
    }

    _processResponseTokens(response) {
        if (!response) return { text: '', goto: null, sets: [], image: null };

        let text = response;
        let goto = null;
        const sets = [];
        let image = null;

        // Extract [goto:KnotName] (knot names contain underscores)
        const gotoMatch = text.match(/\[goto:([A-Za-z0-9_]+)\]/);
        if (gotoMatch) {
            goto = gotoMatch[1];
            text = text.replace(gotoMatch[0], '').trim();
        }

        // Extract [set:var=value], [set:var+value], [set:var-value]
        const setRegex = /\[set:([A-Za-z0-9_]+)([+\-=])([^\]]+)\]/g;
        let setMatch;
        while ((setMatch = setRegex.exec(text)) !== null) {
            const numVal = Number(setMatch[3]);
            sets.push({
                variable: setMatch[1],
                op: setMatch[2],
                value: isNaN(numVal) ? setMatch[3] : numVal,
            });
        }
        text = text.replace(/\[set:[^\]]+\]/g, '').trim();

        // Extract [img:imageName] (image names contain underscores)
        const imgMatch = text.match(/\[img:([A-Za-z0-9_]+)\]/);
        if (imgMatch) {
            image = imgMatch[1];
            text = text.replace(imgMatch[0], '').trim();
        }

        return { text, goto, sets, image };
    }

    // ── Message Storage ─────────────────────────────────────────────────

    _getThread(npcId) {
        if (!this._threads.has(npcId)) {
            this._threads.set(npcId, []);
        }
        return this._threads.get(npcId);
    }

    _addMessage(npcId, msg) {
        const thread = this._getThread(npcId);
        thread.push(msg);
    }

    // ── Notification ────────────────────────────────────────────────────

    _showNotification(npcId, preview) {
        // Queue limit: max 3 pending notifications
        const existing = document.querySelectorAll('.sms-notification');
        if (existing.length >= 3) {
            // Remove oldest
            existing[0].remove();
        }

        const name = this._resolveNPCName(npcId);
        const truncated = preview && preview.length > 60
            ? preview.substring(0, 57) + '...'
            : preview || 'New message';

        // Create slide-in notification element
        const notif = document.createElement('div');
        notif.className = 'sms-notification';
        notif.dataset.npc = npcId;
        notif.innerHTML = `
            ${this._buildAvatarHtml(npcId, 'sms-notif-avatar')}
            <div class="sms-notif-content">
                <div class="sms-notif-name">${escapeHtml(name)}</div>
                <div class="sms-notif-preview">${escapeHtml(truncated)}</div>
            </div>
        `;
        notif.addEventListener('click', () => {
            notif.remove();
            this.open();
            this._openChat(npcId);
        });

        document.body.appendChild(notif);

        // Animate in
        requestAnimationFrame(() => notif.classList.add('sms-notif-show'));

        // Auto-dismiss after 6 seconds
        setTimeout(() => {
            notif.classList.remove('sms-notif-show');
            notif.classList.add('sms-notif-hide');
            setTimeout(() => notif.remove(), 400);
        }, 6000);

        events.emit(Events.PHONE_NOTIFICATION, name, truncated);
    }

    _updateBadge() {
        const count = this._unread.size;

        // Update topbar phone badge
        const badge = document.getElementById('phone-badge');
        if (badge) {
            badge.textContent = count;
            badge.classList.toggle('hidden', count === 0);
        }

        // Update phone home screen Messages app badge
        const appBadge = document.getElementById('phone-app-messages-badge');
        if (appBadge) {
            appBadge.textContent = count;
            appBadge.classList.toggle('hidden', count === 0);
        }
    }

    // ── NPC Resolution ──────────────────────────────────────────────────

    _resolveNPCName(npcId) {
        // Dynamic names first
        if (this._dynamicNames.has(npcId)) {
            return this._dynamicNames.get(npcId);
        }
        const cfg = NPC_CONFIGS[npcId];
        if (cfg) return cfg.defaultName;

        // Fallback from speaker display names
        const display = SPEAKER_DISPLAY_NAMES[npcId];
        if (display) return display;

        // Title case the id
        return npcId.charAt(0).toUpperCase() + npcId.slice(1);
    }

    _getNPCColor(npcId) {
        const cfg = NPC_CONFIGS[npcId];
        return cfg ? cfg.color : '#887ca0';
    }

    _getBubbleClass(npcId) {
        const cfg = NPC_CONFIGS[npcId];
        return cfg ? cfg.bubbleClass : 'sms-bubble-default';
    }

    _getAvatarLetter(npcId) {
        const cfg = NPC_CONFIGS[npcId];
        if (cfg) return cfg.avatarLetter;
        return npcId.charAt(0).toUpperCase();
    }

    /** Boyfriend start (0.3.1 BOYFRIEND_START): the `girlfriend` SMS channel is
     *  shared by both partner routes — render-time read, no cached state, so
     *  no NAME_CHANGED listener needed (web-modules rule: one-shot reads may
     *  hit story.variablesState directly). */
    _isBoyfriendRoute() {
        try { return this._story?.variablesState?.['partner_is_male'] === true; }
        catch (e) { return false; }
    }

    /** Build avatar HTML, portrait image if available, letter fallback.
     *  letter/color are escaped before being interpolated into onerror to prevent
     *  template-literal injection if either ever flows from Ink-emitted SMS-IDs. */
    _buildAvatarHtml(npcId, cssClass = 'sms-contact-avatar') {
        const cfg = NPC_CONFIGS[npcId];
        const color = this._getNPCColor(npcId);
        const letter = this._getAvatarLetter(npcId);
        // Strip everything but alnum/space/dash/dot/#, covers letters, hex colors, words.
        // Anything more exotic isn't a valid avatar letter or CSS color anyway.
        const safeLetter = String(letter).replace(/[^\w \-.]/g, '');
        const safeColor = String(color).replace(/[^\w #(),.%-]/g, '');
        // Partner-aware avatar: the girlfriend channel shows Jesse on the
        // boyfriend route.
        const avatarPath = (npcId === 'girlfriend' && this._isBoyfriendRoute())
            ? 'Portraits/boyfriend_casual' : cfg?.avatar;
        if (avatarPath) {
            // Use image-loader for tar-mode compatibility
            const resolve = window._pno_resolveImage;
            const imgName = avatarPath.split('/').pop(); // e.g. "master_normal" from "Portraits/master_normal"
            const resolved = resolve ? resolve(avatarPath.split('/')[0], imgName) : null;
            const src = resolved || `assets/images/${avatarPath}.png`;
            return `<div class="${cssClass} sms-avatar-img"><img src="${src}" alt="" onerror="var p=this.parentElement;if(p){p.textContent='${safeLetter}';p.classList.remove('sms-avatar-img');p.style.background='${safeColor}';}"></div>`;
        }
        return `<div class="${cssClass}" style="background:${safeColor}">${safeLetter}</div>`;
    }

    // ── DOM Construction ────────────────────────────────────────────────

    _createOverlayContainer() {
        const el = document.createElement('div');
        el.id = 'overlay-phone';
        el.className = 'overlay-container hidden';
        document.body.appendChild(el);
        return el;
    }

    _buildPhoneUI() {
        this._root.innerHTML = `
            <div class="phone-frame">
                <div class="phone-header">
                    <button class="phone-back-btn hidden" id="phone-back">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                    </button>
                    <span class="phone-title">Phone</span>
                    <button class="phone-close-btn" id="phone-close">&times;</button>
                </div>
                <div class="phone-body" id="phone-body"></div>
            </div>
        `;

        this._root.querySelector('#phone-close')?.addEventListener('click', () => this.close());
        this._root.querySelector('#phone-back')?.addEventListener('click', () => this._handleBack());
    }

    _showView(view) {
        this._view = view;
        const backBtn = this._root?.querySelector('#phone-back');
        const title = this._root?.querySelector('.phone-title');
        if (!backBtn || !title) return;

        // Same blank-view protection as openApp: the render fns clear
        // #phone-body before rebuilding, so an uncaught throw leaves the
        // Messages list/chat permanently empty.
        try {
            switch (view) {
                case 'home':
                    backBtn.classList.add('hidden');
                    title.textContent = L.get('phone.title');
                    this._renderHomeScreen();
                    break;
                case 'contacts':
                    backBtn.classList.remove('hidden');
                    title.textContent = L.get('phone.messages');
                    this._renderContactList();
                    break;
                case 'chat':
                    backBtn.classList.remove('hidden');
                    title.textContent = this._resolveNPCName(this._activeChat);
                    this._renderChatView(this._activeChat);
                    break;
            }
        } catch (e) {
            console.warn(`[Phone] _showView(${view}) render failed:`, e?.message);
            const body = this._root?.querySelector('#phone-body');
            if (body) body.innerHTML = `<div style="padding:24px;text-align:center;font-size:12px;color:var(--text-muted)">Something went wrong. Close and reopen the phone.</div>`;
        }
    }

    _handleBack() {
        switch (this._view) {
            case 'chat':
                this._activeChat = null;
                this._showView('contacts');
                break;
            case 'contacts':
            case 'progress':
                // If NPC profile is expanded, close it first
                if (this._expandedNPC >= 0) {
                    this._expandedNPC = -1;
                    this._showProgressView();
                    return;
                }
                this._showView('home');
                return;
            case 'appstore':
            case 'grindr':
            case 'onlyfems':
            case 'sissysocial':
            case 'jobfinder':
            case 'dareme':
            case 'hypno':
            case 'cheats':
                this._showView('home');
                break;
            default:
                this.close();
                break;
        }
    }

    /** Set story reference for reading Ink variables. */
    setStoryRef(story) {
        this._story = story;
    }

    /** Set a fallback getter so phone can recover if _story reference goes stale. */
    setStoryGetter(getter) {
        this._storyGetter = getter;
    }

    // ==========================================================
    // COMPLETION APP, Progress & People (matches Unity CompletionApp.cs)
    // ==========================================================

    /** Storyline-card click handler. Phase 4 minimal: if the storyline has a
     *  linked NPC, swap to the People tab + expand that profile (path-cards
     *  render inline thanks to linkedStoryline). Otherwise no-op. A richer
     *  per-storyline detail view is a future Phase. */
    _openStorylineDetail(sl) {
        if (!sl || !sl.name) return;
        try {
            const story = this._story;
            const iv = (v) => { try { return story ? (Number(story.variablesState[v]) || 0) : 0; } catch(e) { return 0; } };
            const ib = (v) => { try { return story ? !!story.variablesState[v] : false; } catch(e) { return false; } };
            const is = (v) => { try { return story ? story.variablesState[v] : ''; } catch(e) { return ''; } };
            const npcs = this._evaluateNPCs(iv, ib, is);
            // Match against canonical storyline key (`sl.key`) or the storyline name
            // (e.g. 'Girlfriend' → Sarah's `linkedStoryline: 'girlfriend'`).
            const slKey = sl.key || sl.name.toLowerCase().replace(/\s.*/, '');
            const idx = npcs.findIndex(n => n.linkedStoryline === slKey);
            if (idx < 0) return;
            this._progressTab = 0; // People
            this._expandedNPC = idx;
            this._showProgressView();
        } catch (e) { console.warn('[phone] storyline detail open failed:', e); }
    }

    _showProgressView(tab) {
        this._view = 'progress';
        if (tab !== undefined) this._progressTab = tab;
        if (this._progressTab === undefined) this._progressTab = 0; // 0 = People (default), 1 = Progress
        if (this._showSpoilers === undefined) this._showSpoilers = true;
        if (this._expandedNPC === undefined) this._expandedNPC = -1;
        if (this._activeTagFilter === undefined) this._activeTagFilter = null;
        if (this._filtersExpanded === undefined) this._filtersExpanded = false;

        const backBtn = this._root.querySelector('#phone-back');
        const title = this._root.querySelector('.phone-title');
        if (backBtn) backBtn.classList.remove('hidden');
        if (title) title.textContent = L.get('phone.progress');

        const body = this._root.querySelector('#phone-body');
        body.innerHTML = '';

        const s = this._story;
        const iv = (v) => { try { return s ? (Number(s.variablesState[v]) || 0) : 0; } catch(e) { return 0; } };
        const ib = (v) => { try { if (!s) return false; const val = s.variablesState[v]; return val === true || val === 1; } catch(e) { return false; } };
        const is = (v) => { try { return s ? (s.variablesState[v] || '').toString() : ''; } catch(e) { return ''; } };

        // -- Tab bar (People | Progress | Achievements) --
        const tabBar = document.createElement('div');
        tabBar.className = 'completion-tab-bar';
        const _tabPeople = L.get('completion.tab_people'); const _tabProgress = L.get('completion.tab_progress'); const _tabAch = L.get('completion.tab_achievements');
        const tabLabels = [
            _tabPeople !== 'completion.tab_people' ? _tabPeople : 'People',
            _tabProgress !== 'completion.tab_progress' ? _tabProgress : 'Progress',
            _tabAch !== 'completion.tab_achievements' ? _tabAch : 'Achievements',
        ];
        tabLabels.forEach((name, i) => {
            const t = document.createElement('div');
            t.textContent = name;
            t.className = 'completion-tab' + (i === this._progressTab ? ' completion-tab-active' : '');
            t.addEventListener('click', () => {
                this._expandedNPC = -1;
                this._showProgressView(i);
            });
            tabBar.appendChild(t);
        });
        body.appendChild(tabBar);

        // -- Spoiler toggle --
        const spoilerRow = document.createElement('div');
        spoilerRow.className = 'completion-spoiler-row';
        const spoilerToggle = document.createElement('div');
        spoilerToggle.className = 'completion-spoiler-toggle' + (this._showSpoilers ? ' completion-spoiler-active' : '');
        spoilerToggle.textContent = this._showSpoilers ? `\u25BC ${L.get('completion.spoilers_on')}` : `\u25B6 ${L.get('completion.spoilers')}`;
        spoilerToggle.addEventListener('click', () => { this._showSpoilers = !this._showSpoilers; this._showProgressView(); });
        spoilerRow.appendChild(spoilerToggle);
        body.appendChild(spoilerRow);

        // -- Evaluate data (shared) --
        const storylines = this._evaluateStorylines(iv, ib);
        const npcs = this._evaluateNPCs(iv, ib, is);

        // -- Tab content --
        const scroll = document.createElement('div');
        scroll.className = 'completion-scroll';

        if (this._progressTab === 0) {
            this._buildPeopleTab(scroll, npcs);
        } else if (this._progressTab === 2) {
            this._buildAchievementsTab(scroll);
        } else {
            // Overall progress card
            const totalComplete = storylines.filter(sl => sl.status === 'complete').length;
            const totalVisible = storylines.filter(sl => sl.status !== 'hidden').length;
            const totalAll = storylines.length;
            const metCount = npcs.filter(n => n.met).length;
            const totalNPCs = npcs.length;

            const overallCard = document.createElement('div');
            overallCard.className = 'completion-card completion-card-overall';
            const _opTitle = L.get('completion.overall_progress');
            const _opCompleted = L.get('completion.completed_label');
            const _opDiscovered = L.get('completion.discovered_label');
            const _opChars = L.get('completion.characters_label');
            const achUnlocked = achievementManager.getUnlockedCount();
            const achTotal = achievementManager.getTotalCount();
            const _opAch = L.get('completion.achievements_label');
            overallCard.innerHTML = `
                <div class="completion-card-title completion-overall-title">${_opTitle !== 'completion.overall_progress' ? _opTitle : 'Overall Progress'}</div>
                ${this._wideProgressRowHTML(_opCompleted !== 'completion.completed_label' ? _opCompleted : 'Completed', totalComplete, totalAll, '#8040a0')}
                ${this._wideProgressRowHTML(_opDiscovered !== 'completion.discovered_label' ? _opDiscovered : 'Discovered', totalVisible, totalAll, '#4a4a70')}
                ${this._wideProgressRowHTML(_opChars !== 'completion.characters_label' ? _opChars : 'Characters Met', metCount, totalNPCs, '#6a4a80')}
                ${this._wideProgressRowHTML(_opAch !== 'completion.achievements_label' ? _opAch : 'Achievements', achUnlocked, achTotal, '#c08040')}
            `;
            scroll.appendChild(overallCard);

            this._buildProgressTab(scroll, storylines, iv, ib);
        }
        body.appendChild(scroll);
    }

    // ==========================================================
    // EVALUATE STORYLINES (matches Unity CompletionApp.EvaluateStorylines)
    // ==========================================================

    _evaluateStorylines(iv, ib) {
        const mr = iv('MasterRelationship'), fem = iv('Femininity'), sub = iv('Submissiveness'), day = iv('Day');
        const mi = iv('master_intensity');
        const orderChastity = iv('master_order_chastity'), orderPlug = iv('master_order_plug');
        const orderLaser = iv('master_order_laser'), orderHair = iv('master_order_hair'), orderMakeup = iv('master_order_makeup');
        const ordersComplete = (orderChastity >= 3 ? 1 : 0) + (orderPlug >= 3 ? 1 : 0) + (orderLaser >= 2 ? 1 : 0) + (orderHair >= 2 ? 1 : 0) + (orderMakeup >= 2 ? 1 : 0);
        const ordersStarted = (orderChastity >= 1 ? 1 : 0) + (orderPlug >= 1 ? 1 : 0) + (orderLaser >= 1 ? 1 : 0) + (orderHair >= 1 ? 1 : 0) + (orderMakeup >= 1 ? 1 : 0);
        const ordersHidden = ordersStarted === 0;

        // Canonical storyline data, Girlfriend is fully owned by the module;
        // Office / Master / Endgame / Velvet share their multi-path sub-tree
        // (paths + decisionUnlocked) from the same source.
        const _slData = getStorylineData(this._story);
        const gfSL = _slData.girlfriend;
        const gfDone = gfSL.status === 'complete';
        const gfProgress = gfSL.val;
        const gfDesc = gfSL.desc;
        const gfHint = gfSL.hint;
        const gfWarning = gfSL.warning;
        const gfMilestone = gfSL.milestoneCurrent;
        const gfPaths = gfSL.paths;
        const gfDecisionUnlocked = gfSL.decisionUnlocked;

        // Endgame
        const dreamsDone = [ib('endgame_dream_1'), ib('endgame_dream_2'), ib('endgame_dream_3')].filter(Boolean).length;
        const endTriggered = ib('endgame_triggered');
        const dreamsHidden = dreamsDone === 0 && fem < 60;
        let endWarning = null;
        if (fem >= 50 && sub > 50 && mr <= 7) endWarning = 'Growing obedience may close the independence path...';

        // Club
        const clubAccess = ib('club_access'), clubVip = ib('club_vip_access'), clubJob = ib('club_job_unlocked');
        let clubProg = (clubAccess ? 1 : 0) + (clubJob ? 1 : 0) + (clubVip ? 1 : 0) + (iv('club_shifts_worked') >= 10 ? 1 : 0);
        if (iv('club_regular_marco') >= 2 || iv('club_regular_diego') >= 2 || iv('club_regular_lily') >= 2) clubProg++;
        let clubHint;
        if (!clubAccess) clubHint = 'Need Fem 25 \u2014 the bouncer only lets certain types in';
        else if (!clubJob) clubHint = 'Dance, drink, explore \u2014 the manager is watching';
        else if (!clubVip) clubHint = 'Build fame to 60 for VIP access';
        else clubHint = 'Work shifts and get to know the regulars';

        // Maid
        const maidHired = ib('maid_hired'), maidStage = iv('maid_stage'), maidClient = iv('maid_client_stage');
        const maidProg = (maidHired ? 1 : 0) + Math.min(maidStage, 4) + (maidClient >= 5 ? 1 : 0);
        const maidDiscovered = ib('maid_discovered');
        const maidHidden = !maidDiscovered && !maidHired;
        let maidHint;
        if (maidHidden) maidHint = 'Apply via JobFinder, requires feminine outfit for orientation';
        else if (!maidHired) maidHint = 'Apply for the cleaning position';
        else if (maidClient < 5) maidHint = 'Keep working shifts \u2014 Victor is watching...';
        else maidHint = '';

        // Office
        const officeStage = iv('office_stage'), bossStage = iv('office_boss_stage');
        const officeProg = Math.min(officeStage, 4) + (bossStage >= 5 ? 1 : 0);
        const officeHidden = officeStage === 0;
        let officeHint;
        if (officeHidden) officeHint = 'Apply via JobFinder, needs feminine outfit or a suit';
        else if (officeStage < 4) officeHint = 'Work shifts to get promoted';
        else if (bossStage < 5) officeHint = 'Mr. Hartwell is paying attention...';
        else officeHint = '';

        // Streaming
        const streamRep = iv('stream_reputation'), knowStreaming = iv('Knowledge_streaming');
        const streamMilestone = ib('stream_milestone_5') ? 5 : ib('stream_milestone_4') ? 4 : ib('stream_milestone_3') ? 3 : ib('stream_milestone_2') ? 2 : ib('stream_milestone_1') ? 1 : iv('streams_completed') > 0 ? 1 : 0;

        // OnlyFems
        const onlyfemsInstalled = ib('onlyfems_installed'), onlyfemsTier = iv('onlyfems_tier_unlocked'), onlyfemsSubs = iv('onlyfems_subscribers');

        // Photographer
        const photoCard = ib('photographer_card'), photoShoots = iv('photographer_shoots');

        // Sugar Daddy
        const sugarArr = iv('sugar_arrangement');

        // The Gilded Cage cabaret (0.3; FREED 0.3.1, gate audit 2026-07-23)
        const cabDiscovered = ib('cabaret_discovered'), cabStage = iv('cabaret_stage'), exhibFem = iv('exhib_fem');
        const cabLateShow = cabStage >= 3 && exhibFem >= 50;
        const cabProg = Math.min(cabStage, 3) + (cabLateShow ? 1 : 0);
        let cabaretHint;
        if (!cabDiscovered) cabaretHint = 'A stage door in the Red Light District, after dark';
        else if (cabStage < 2) cabaretHint = 'Watch the show and work the floor \u2014 Maxime runs auditions when something worth lighting walks in';
        else if (!cabLateShow) cabaretHint = 'Keep taking the stage \u2014 the late show is for performers who own the room';
        else cabaretHint = 'Volunteer for the late show after your set';


        // SMS side-chains (one-shot, 'Ignore' choice locks at stage 3)
        const smsFanStage = iv('sms_fan_stage');
        const smsFanOffered = iv('sms_fan_stage_offered');
        const smsCuckoldStage = iv('sms_cuckold_stage');
        const smsCuckoldOffered = iv('sms_cuckold_stage_offered');
        const smsBullyStage = iv('sms_bully_stage');
        const smsBullyOffered = iv('sms_bully_stage_offered');
        const smsSugarStage = iv('sms_sugar_stage');
        const smsSugarOffered = iv('sms_sugar_stage_offered');
        const smsMasterTraining = iv('sms_master_training');
        const smsMasterTrainingOffered = iv('sms_master_training_offered');

        // Sasha custom hypno file (escalation arc, independent of hypno tier)
        const customFileStage = iv('custom_file_stage');
        const customFileReceived = ib('custom_file_received');

        // Glory Hole regular (face-recognition arc, separate from compliance counter)
        const ghRegularStage = iv('gloryhole_regular_stage');

        // Connections
        const grindrInstalled = ib('grindr_installed');
        const datTyler = Math.min(iv('dating_tyler'), 3), datKai = Math.min(iv('dating_kai'), 3);
        const datRaven = Math.min(iv('dating_raven'), 3), datOwen = Math.min(iv('dating_owen'), 3);
        const datEloise = Math.min(iv('dating_eloise'), 3), datSasha = Math.min(iv('dating_sasha'), 3);
        const datPatrick = Math.min(iv('dating_patrick'), 3);
        const datVincent = Math.min(iv('dating_vincent'), 3);
        const datScar = Math.min(iv('dating_scar'), 3);
        const npcProgress = datTyler + datKai + datRaven + datOwen + datEloise + datSasha + datPatrick + datVincent + datScar;
        const npcMax = 27;
        let npcDesc;
        if (!grindrInstalled) npcDesc = '???';
        else if (npcProgress >= npcMax) npcDesc = 'All connections maxed';
        else {
            // Pre-match visible NPCs (Tyler, Kai, Owen) get a "swipe @ Fem N" hint
            // instead of bare "0/3" so the player knows HOW to reach them. Without
            // this, the Progress card surfaces NPCs by name (good \u2014 gives them a
            // goal) but offers no path (bad \u2014 players see "Kai 0/3" and submit
            // bug reports saying "there should be a way to date Kai"). The
            // hidden-until-matched NPCs (Raven, Elo\u00efse, etc.) keep their existing
            // gate. Foundby:VPS-PT-player v0.2.6.7.
            const _line = (name, dat, matchedFlag, reqFem) => {
                if (ib(matchedFlag)) return `${name} ${dat}/3`;
                if (dat > 0) return `${name} ${dat}/3`;
                const tpl = L.get('phone.progress.swipe_at_fem') || 'swipe in GrindrR @ Fem {0}';
                return `${name} (${tpl.replace('{0}', String(reqFem))})`;
            };
            const parts = [
                _line('Tyler', datTyler, 'matched_tyler', 10),
                _line('Kai', datKai, 'matched_kai', 10),
                _line('Owen', datOwen, 'matched_owen', 15),
            ];
            if (ib('matched_raven')) parts.push(`Raven ${datRaven}/3`);
            if (ib('matched_eloise')) parts.push(`Elo\u00efse ${datEloise}/3`);
            if (ib('matched_sasha')) parts.push(`Sasha ${datSasha}/3`);
            if (ib('matched_patrick')) parts.push(`Patrick ${datPatrick}/3`);
            if (ib('matched_vincent')) parts.push(`Vincent ${datVincent}/3`);
            if (ib('matched_scar')) parts.push(`Scar ${datScar}/3`);
            npcDesc = parts.join(', ');
        }

        // Dating Encounters
        const hasDatingApp = grindrInstalled;
        const datingMet = Math.min(iv('dating_met_strangers'), 5);

        // Beach Bullies
        const bullyStage = iv('beach_bully_stage');

        // Glory Hole
        const visitedGH = ib('visitedGloryHoleOneTime'), ghCompliance = Math.min(iv('gloryhole_compliance'), 10);

        // Laser
        const laserSessions = iv('laser_sessions'), laserSmooth = ib('body_hairless');
        let laserStatus, laserDesc, laserHint, laserWarning = null;
        if (laserSessions === 0 && orderLaser === 0) { laserDesc = '???'; laserHint = 'Master may request this'; laserStatus = 'hidden'; }
        else if (laserSessions >= 5 && laserSmooth) { laserDesc = 'Permanently smooth'; laserHint = ''; laserStatus = 'complete'; }
        else if (laserSessions >= 5) { laserDesc = 'Hair growing back!'; laserHint = 'Visit the Salon!'; laserStatus = 'active'; laserWarning = 'Hair regrowing \u2014 visit salon for maintenance!'; }
        else { laserDesc = `Session ${laserSessions}/5`; laserHint = 'Keep visiting the Salon'; laserStatus = 'active'; }

        // Makeup
        const makeupSkill = iv('makeup_skill');

        // Gym
        const gymMember = ib('gym_member'), trainerStage = iv('trainer_stage'), gymVisits = iv('gym_visits');
        const gymProg = (gymMember ? 1 : 0) + Math.min(trainerStage, 4);
        const gymHidden = !gymMember && gymVisits === 0;
        let gymDesc;
        if (gymHidden) gymDesc = '???';
        else if (trainerStage >= 4) gymDesc = "Malik's star pupil";
        else if (trainerStage > 0) gymDesc = `Trainer stage ${trainerStage}/4`;
        else gymDesc = 'Member';
        let gymHint;
        if (gymHidden) gymHint = "There's a gym in town \u2014 might be worth checking out";
        else if (!gymMember) gymHint = 'Buy a membership ($50)';
        else if (trainerStage === 0) gymHint = 'Work out a few times \u2014 the trainer notices regulars';
        else if (trainerStage < 4) gymHint = 'Malik has more to teach you...';
        else gymHint = '';

        // Clinic
        const clinicRef = ib('clinic_referral'), clinicReg = ib('clinic_registered'), doctorStage = iv('doctor_stage');
        const hrtStage = iv('hrt_stage');
        const clinicProg = (clinicReg ? 1 : 0) + Math.min(doctorStage, 5);
        const clinicHidden = !clinicRef && !clinicReg;
        let clinicDesc;
        if (clinicHidden) clinicDesc = '???';
        else if (doctorStage >= 5) clinicDesc = 'Dr. Mercer knows you well';
        else if (doctorStage > 0) clinicDesc = `Doctor stage ${doctorStage}/5`;
        else if (clinicReg) clinicDesc = 'Registered patient';
        else clinicDesc = 'Have referral';
        let clinicHint;
        if (clinicHidden) clinicHint = 'You might get a referral from certain people...';
        else if (!clinicReg) clinicHint = 'Visit the clinic to register';
        else if (doctorStage === 0) clinicHint = 'Schedule a consultation with Dr. Mercer';
        else if (doctorStage < 5) clinicHint = 'The doctor wants to see you again...';
        else clinicHint = '';
        let clinicWarning = null;
        if (hrtStage >= 1 && iv('hrt_missed_count') >= 2) clinicWarning = "You've been missing injections \u2014 visit the clinic";

        // Bar Outfit
        const barOutfit = iv('bar_outfit');
        const barHidden = barOutfit === 0;
        let barOutfitDesc, barOutfitHint;
        if (barHidden) { barOutfitDesc = '???'; barOutfitHint = 'Get hired at the Bar first'; }
        else if (barOutfit >= 3) { barOutfitDesc = 'Wearing the dress'; barOutfitHint = ''; }
        else if (barOutfit === 2) { barOutfitDesc = 'Polo uniform'; barOutfitHint = 'Keep working shifts \u2014 something more daring awaits...'; }
        else { barOutfitDesc = 'Storage clothes'; barOutfitHint = 'Work a few bar shifts to earn a uniform'; }

        // DareMe
        const daremeInst = ib('dareme_installed');
        const dareDone = [1,2,3,4,5,6,7,8,9,10,11].filter(n => ib(`dare_done_${n}`)).length;
        const dareTier = dareDone >= 7 ? 3 : dareDone >= 4 ? 2 : dareDone > 0 ? 1 : 0;
        let dareDesc, dareHint;
        if (!daremeInst) { dareDesc = '???'; dareHint = 'Install DareMe from the App Store (Fem 20+)'; }
        else if (dareDone >= 11) { dareDesc = 'All dares complete!'; dareHint = ''; }
        else { dareDesc = `${dareDone}/11 dares (Tier ${dareTier})`; dareHint = iv('dare_active') > 0 ? 'Complete your active dare!' : 'Pick a new dare from the app'; }

        // Spa
        const spaDiscovered = ib('spa_discovered'), spaMem = ib('spa_member');
        const masseurSt = iv('masseur_stage'), camilleSt = iv('camille_stage');
        const spaProg = (spaMem ? 1 : 0) + Math.min(masseurSt, 4) + Math.min(camilleSt, 3);
        const spaMax = 8; // member + masseur 4 + camille 3
        const spaHidden = !spaDiscovered;
        let spaDesc, spaHint;
        if (spaHidden) { spaDesc = '???'; spaHint = 'Discover new locations as your femininity grows'; }
        else if (spaProg >= spaMax) { spaDesc = 'Regular at the spa'; spaHint = ''; }
        else if (!spaMem) { spaDesc = 'Discovered'; spaHint = 'Buy a spa membership'; }
        else { spaDesc = `Dominic ${masseurSt}/4, Camille ${camilleSt}/3`; spaHint = masseurSt < 4 ? 'Book a massage with Dominic...' : 'Chat with Camille in the lounge'; }

        // Velvet Lounge
        const velvetSt = iv('velvet_stage'), vivienneRel = iv('vivienne_relationship');
        const velvetProg = Math.min(velvetSt, 3) + Math.min(vivienneRel, 5);
        const velvetMax = 8;
        const velvetHidden = velvetSt === 0 && vivienneRel === 0;
        let velvetDesc, velvetHint;
        if (velvetHidden) { velvetDesc = '???'; velvetHint = 'Explore the Red Light District after dark'; }
        else if (velvetSt >= 3) { velvetDesc = `Worker. Vivienne ${vivienneRel}/5`; velvetHint = vivienneRel < 5 ? 'Talk to Vivienne between shifts' : ''; }
        else if (velvetSt >= 1) { velvetDesc = `Customer. Vivienne ${vivienneRel}/5`; velvetHint = 'Visit the lounge, browse services, talk to Vivienne'; }
        else { velvetDesc = 'Discovered'; velvetHint = 'Enter the Velvet Lounge in the Red Light District'; }

        // Maid Cafe
        const cafeSt = iv('cafe_stage'), hanaRel = iv('hana_relationship');
        const cafeProg = Math.min(cafeSt, 3) + Math.min(hanaRel, 4);
        const cafeMax = 7;
        const cafeHidden = cafeSt === 0 && hanaRel === 0;
        let cafeDesc, cafeHint;
        if (cafeHidden) { cafeDesc = '???'; cafeHint = 'There\'s a cafe in the Red Light District...'; }
        else if (cafeSt >= 3) { cafeDesc = `Worker. Hana ${hanaRel}/4`; cafeHint = hanaRel < 4 ? 'Keep working shifts for Hana' : ''; }
        else if (cafeSt >= 1) { cafeDesc = `Customer. Hana ${hanaRel}/4`; cafeHint = 'Order something, chat with the staff'; }
        else { cafeDesc = 'Discovered'; cafeHint = 'Visit Nyan Nyan in the Red Light District'; }

        // Hypno
        const hypnoTier = iv('hypno_session_tier');
        const hypnoExposure = iv('hypno_exposure');
        const hypnoHidden = hypnoTier === 0 && hypnoExposure === 0;

        // Tattoo (Jade)
        const jadeRapport = iv('jade_rapport'), jadeVisits = iv('jade_visits');
        const tattooDiscovered = ib('tattoo_discovered');
        const tattooHidden = !tattooDiscovered && jadeVisits === 0;
        let tattooDesc, tattooHint;
        if (tattooHidden) { tattooDesc = '???'; tattooHint = 'Walk around the neighbourhood after Day 7'; }
        else if (jadeRapport >= 5) { tattooDesc = 'Close friends'; tattooHint = ''; }
        else if (jadeRapport > 0) { tattooDesc = `Rapport ${jadeRapport}/5`; tattooHint = 'Visit the studio and talk to Jade'; }
        else if (tattooDiscovered) { tattooDesc = 'Discovered the studio'; tattooHint = 'Visit Inkwell in the Red Light District'; }
        else { tattooDesc = 'Visited once'; tattooHint = 'Go back and chat'; }

        // Apartment
        const uBed = iv('upgrade_bed'), uDesk = iv('upgrade_desk'), uVanity = iv('upgrade_vanity'), uDecor = iv('upgrade_decor');
        const uCoffee = iv('upgrade_coffee');
        const totalUpgrades = uBed + uDesk + uVanity + uDecor + uCoffee;
        const upgradesHidden = totalUpgrades === 0 && day < 7;

        // Resort (v0.2.5, Dante + Iris)
        const resortDiscovered = ib('resort_discovered'), resortVisits = iv('resort_visits');
        const danteStage = iv('dante_stage'), irisStage = iv('iris_stage');
        const resortProg = (resortDiscovered ? 1 : 0) + Math.min(resortVisits, 3) + Math.min(danteStage, 5) + Math.min(irisStage, 3);
        const resortMax = 12; // 1 + 3 + 5 + 3
        const resortHidden = !resortDiscovered && resortVisits === 0;
        let resortDesc, resortHint;
        if (resortHidden) { resortDesc = '???'; resortHint = 'A flyer might arrive once you can afford a getaway'; }
        else if (resortProg >= resortMax) { resortDesc = 'Resort regular'; resortHint = ''; }
        else if (resortVisits === 0) { resortDesc = 'Discovered'; resortHint = 'Book a weekend trip via the brochure'; }
        else { resortDesc = `${resortVisits} trips, Dante ${danteStage}/5, Iris ${irisStage}/3`; resortHint = ib('resort_on_site') ? 'Find Dante at the cabana, talk to Iris at the front desk' : 'Book another trip when you have time'; }

        // Weekend Events (v0.2.5)
        const weekendAttended = iv('weekend_event_attended');
        const weekendPending = iv('weekend_event_pending');
        const walkOfShameDone = ib('weekend_walk_of_shame_done');
        const weekendFirsts = [
            !ib('weekend_pool_party_first'), !ib('weekend_foam_night_first'),
            !ib('weekend_beach_bonfire_first'), !ib('weekend_cosplay_first'),
            !ib('weekend_master_social_first'), !ib('weekend_stream_crossover_first'),
            !ib('weekend_loft_rooftop_first'), walkOfShameDone,
        ].filter(Boolean).length;
        const weekendHidden = weekendAttended === 0 && weekendPending === 0;
        let weekendDesc, weekendHint;
        if (weekendHidden) { weekendDesc = '???'; weekendHint = 'Saturday plans turn up once you have a social circle'; }
        else if (weekendFirsts >= 8) { weekendDesc = 'All 8 events seen'; weekendHint = ''; }
        else if (weekendPending > 0) { weekendDesc = `Invite waiting (${weekendFirsts}/8 unique)`; weekendHint = 'Check your phone for the SMS'; }
        else { weekendDesc = `${weekendFirsts}/8 unique, ${weekendAttended} total`; weekendHint = 'Wait for next Saturday'; }

        // HRT (within Clinic, but tracked as its own progression)
        const hrtStartedFlag = ib('taking_hormones'), hrtMissed = iv('hrt_missed_count');
        const hrtHidden = !hrtStartedFlag && hrtStage === 0;
        let hrtDesc, hrtHint, hrtWarning = null;
        if (hrtHidden) { hrtDesc = '???'; hrtHint = 'Talk to Dr. Mercer about hormones'; }
        else if (hrtStage >= 4) { hrtDesc = 'Full transformation'; hrtHint = ''; }
        else { hrtDesc = `Stage ${hrtStage}/4`; hrtHint = 'Keep your injection schedule at the Clinic'; }
        if (hrtMissed >= 2) hrtWarning = `${hrtMissed} missed injections, visit the Clinic`;

        // Tattoo Collection (7 designs)
        const tattooNames = ['star', 'abstract', 'butterfly', 'rose', 'heart', 'tribal', 'wolf'];
        const tattoosOwned = tattooNames.filter(n => ib('has_tattoo_' + n)).length;
        const tattooCollectHidden = tattoosOwned === 0 && !tattooDiscovered;

        return [
            // --- MAIN STORY ---
            // Master Rank caps per route (compute_mr_cap in Globals.ink):
            // Roommate=4, Landlord=7, Owner=10. Show route cap so a Roommate
            // player isn't told "almost there \u2014 keep visiting" at 4/10 when
            // 4 is the actual ceiling.
            { name: 'Master', icon: '\uD83D\uDC51', cat: 'Main Story', val: mr, max: mi >= 3 ? 10 : mi >= 2 ? 7 : mi >= 1 ? 4 : 10,
              desc: mr === 0 ? '???' : (mi >= 1 && mr >= (mi >= 3 ? 10 : mi >= 2 ? 7 : 4) ? (mi >= 3 ? 'Completely his' : mi >= 2 ? 'Landlord arc at cap' : 'Roommate arc at cap') : `Relationship ${mr}/${mi >= 3 ? 10 : mi >= 2 ? 7 : mi >= 1 ? 4 : 10}` + (mi >= 1 ? ` (${['','Roommate','Landlord','Owner'][mi] || ''})` : '')) + (smsMasterTraining > 0 ? ` \u2014 Training SMS ${smsMasterTraining}/3` : ''),
              hint: mi === 0 ? 'You chose independence \u2014 no Master in this playthrough'
                  : mr === 0 ? 'Work a Bar shift \u2014 a regular there takes an interest in you'
                  : mi === 1 && mr >= 4 ? 'Roommate arc capped at MR 4. Casual cohabitation has no further depth'
                  : mi === 2 && mr >= 7 ? 'Landlord arc capped at MR 7. No orders chain on this route'
                  : mr < 3 ? 'Work more Bar shifts and do what he says. He gives orders via SMS'
                  : mr < 4 ? (mi >= 3 ? 'Buy and equip what he ordered (Wardrobe). Inspections start at MR 4' : 'Buy and equip what he ordered \u2014 check Wardrobe')
                  : mr < 6 ? (mi >= 3 ? 'Complete his orders (cage, plug, laser, hair, makeup). Morning inspections are active \u2014 wear what he told you' : 'Complete his orders to advance. Check SMS for reminders')
                  : mr < 7 ? (mi >= 3 ? 'Curfew after dark \u2014 be home by Evening or face punishment. Wear feminine clothes for inspection' : 'Keep following orders. Dress feminine when he asks')
                  : mr < 9 ? (mi >= 3 ? 'He takes 20% of your pay. Keep working shifts and obeying \u2014 MR 9 unlocks full financial control' : 'Stay obedient. The relationship deepens with each visit')
                  : mr < 10 ? (mi >= 3 ? 'Allowance active: $350/day cap. Keep serving him to reach full devotion' : 'Almost there \u2014 keep visiting the Bar')
                  : '',
              spoiler: mi >= 3
                  ? 'Owner Route (cap 10): MR 4 \u2192 inspections, MR 6 \u2192 curfew + clothing, MR 7 \u2192 20% earnings cut, MR 9 \u2192 allowance ($350 cap). Advance via Bar visits + fulfilling orders.'
                  : mi >= 2
                  ? 'Landlord Route (cap 7): rent-driven dynamic, no orders chain. Cap baseline 4 + Day 7/14/21 bonuses.'
                  : mi >= 1
                  ? 'Roommate Route (cap 4): casual cohabitation, no orders chain. Cap baseline 1 + Day 7/14/21 bonuses.'
                  : 'MR grows via Bar visits + orders. Higher intensity = more control mechanics.',
              status: mi === 0 ? 'hidden' : mr === 0 ? 'hidden' : (mi >= 1 && mr >= (mi >= 3 ? 10 : mi >= 2 ? 7 : 4)) ? 'complete' : 'active',
              paths: _slData.master.paths, decisionUnlocked: _slData.master.decisionUnlocked },
            { name: 'Master Orders', icon: '\u2694', cat: 'Main Story', val: ordersComplete, max: 5,
              milestones: ['Cage', 'Plug', 'Laser', 'Hair', 'Makeup'], milestoneCurrent: ordersComplete,
              desc: ordersHidden ? '???' : `${ordersComplete}/5 orders fulfilled`,
              hint: ordersHidden ? 'Follow your Master\u2019s instructions' : ordersComplete < 5 ? "Check your orders \u2014 Master doesn't like waiting" : '',
              spoiler: 'Orders given at MR milestones. Cage/Plug: buy at adult store, equip in wardrobe (stage 1\u21922\u21923). Laser: 5 salon sessions. Hair: salon extensions. Makeup: 3 salon lessons.',
              status: ordersHidden ? 'hidden' : ordersComplete >= 5 ? 'complete' : 'active' },
            { name: 'Girlfriend', icon: '\u2665', cat: 'Main Story', val: gfProgress, max: 6,
              milestones: ['First Visit', 'Dates', 'Suspicion', 'Confession', 'Resolution'], milestoneCurrent: gfMilestone,
              desc: gfDesc, hint: gfHint, warning: gfWarning,
              spoiler: 'Suspicion rises with Femininity during dates. Confession at suspicion 3+. Paths: Accept (Fem < 50 at confession), Cuck (introduce to Master at MR 6+), Breakup (relationship \u2264 -4). Post-accept: 3 new date types.',
              status: gfDone ? 'complete' : 'active',
              paths: gfPaths, decisionUnlocked: gfDecisionUnlocked },
            { name: 'Endgame', icon: '\u2605', cat: 'Main Story', val: dreamsDone + (endTriggered ? 1 : 0), max: 4,
              milestones: ['Dream 1', 'Dream 2', 'Dream 3', 'Ending'], milestoneCurrent: dreamsDone + (endTriggered ? 1 : 0),
              desc: dreamsHidden ? '???' : endTriggered ? 'Journey complete' : `Dreams ${dreamsDone}/3`,
              hint: dreamsHidden ? 'Push your femininity higher...' : 'Keep pushing your limits',
              warning: endWarning,
              spoiler: 'Submission: Fem \u2265 80, Sub \u2265 70, MR \u2265 8, all orders done. Independence: Fem \u2265 60, Sub \u2264 55, MR \u2264 7, $1500+. Dreams at Fem 55/65/70 on Day 14/21/25.',
              status: dreamsHidden ? 'hidden' : endTriggered ? 'complete' : 'active',
              paths: _slData.endgame.paths, decisionUnlocked: _slData.endgame.decisionUnlocked },
            // Chastity arc: surfaces once the player owns/wears a cage; resolves
            // into Removable / Permanent / Walk Away path-cards at the Day-60
            // decision (CHASTITY_REMOVAL.md §3). val = days locked toward the 60
            // milestone; complete on permanent lock OR walking away.
            (() => {
                const ownsCage = ib('owns_chastity'), wearCage = ib('wearing_chastity');
                const permLock = ib('permanent_lock'), walked = ib('chastity_walked_away');
                const daysLocked = iv('chastity_total_days_locked');
                const chastityHidden = !ownsCage && !wearCage && !permLock && !walked;
                const chastityDesc = chastityHidden ? '???'
                    : walked ? L.get('storylines.chastity.walk_away.desc', 'Left the cage behind, on your own terms')
                    : permLock ? L.get('storylines.chastity.permanent.desc', 'Ceremony chosen, no keyway left')
                    : L.get('storylines.chastity.temporary.desc', 'Caged, but the key still turns');
                const chastityHint = walked || permLock ? ''
                    : daysLocked >= 60 ? L.get('storylines.chastity.hint_decide', 'Sixty days in. The permanent choice is on the table.')
                    : L.get('storylines.chastity.temporary.hint', 'Keyholder controls release. Sixty days locked surfaces the permanent choice.');
                return { name: L.get('storylines.chastity.name', 'Chastity'), icon: '🔒', cat: 'Main Story',
                    val: walked ? 1 : Math.min(60, daysLocked), max: walked ? 1 : 60,
                    desc: chastityDesc, hint: chastityHint,
                    spoiler: 'Buy a cage at the Adult Store, equip it. Days locked accrue overnight. At 60 days the keyholder (or you, solo) is offered the permanent lock: accept (Permanent), decline (stay Removable), or on an Owner/Landlord route hand back the key and leave (Walk Away, moves you out).',
                    status: chastityHidden ? 'hidden' : (permLock || walked) ? 'complete' : 'active',
                    paths: _slData.chastity.paths, decisionUnlocked: _slData.chastity.decisionUnlocked };
            })(),

            // --- JOBS & INCOME ---
            { name: 'Club', icon: '\u266B', cat: 'Jobs & Income', val: clubProg, max: 5,
              milestones: ['Access', 'Job', 'VIP', '10 Shifts', 'Inner Circle'], milestoneCurrent: clubProg,
              desc: !clubAccess ? '???' : clubProg >= 5 ? 'Club royalty' : `${clubProg}/5 milestones`,
              hint: clubHint,
              spoiler: 'Entry: Fem 25+ or Allure 15+. Job: Fem 40+, Day 10+, 3+ visits. VIP: fame 60+. Regulars: Marco (bar), Diego (dance, Fem 40+), Lily (dressing room).',
              status: !clubAccess ? 'hidden' : clubProg >= 5 ? 'complete' : 'active' },
            { name: 'Maid Job', icon: '\u2728', cat: 'Jobs & Income', val: maidProg, max: 6,
              milestones: ['Hired', 'Standard', 'Premium', 'VIP', 'Personal', 'Victor'], milestoneCurrent: maidProg,
              desc: maidHidden ? '???' : maidClient >= 5 ? "Victor's personal maid" : `Stage ${maidStage}/4`,
              hint: maidHint,
              spoiler: 'JobFinder listing. Needs EquippedFeminine for orientation. Victor: inspection \u2192 mirror \u2192 apron \u2192 under desk \u2192 arrangement.',
              status: maidHidden ? 'hidden' : maidClient >= 5 ? 'complete' : 'active' },
            { name: 'Office', icon: '\u2692', cat: 'Jobs & Income', val: officeProg, max: 5,
              milestones: ['Mailroom', 'Front Desk', 'Assistant', 'Personal', 'Mr. Hartwell'], milestoneCurrent: officeProg,
              desc: officeHidden ? '???' : bossStage >= 5 ? "Mr. Hartwell's right hand" : `Position ${officeStage}/4`,
              hint: officeHint,
              spoiler: 'JobFinder listing, needs EquippedFeminine. Boss: Touch \u2192 CloseCall \u2192 ClosedDoor \u2192 UnderDesk \u2192 AfterHours. Intermediates: late, dinner, hotel.',
              status: officeHidden ? 'hidden' : officeProg >= 5 ? 'complete' : 'active',
              paths: _slData.office.paths, decisionUnlocked: _slData.office.decisionUnlocked },
            { name: 'Streaming', icon: '\u25B6', cat: 'Jobs & Income', val: Math.min(streamRep, 100), max: 100,
              milestones: ['First Stream', '25 Subs', '100 Subs', '500 Subs', '1000 Subs'], milestoneCurrent: streamMilestone,
              desc: knowStreaming === 0 ? '???' : streamRep >= 100 ? 'Famous streamer' : `Reputation ${streamRep}/100`,
              hint: knowStreaming === 0 ? 'Need Fem 25 \u2014 browse the web at home' : 'Try different categories and actions',
              spoiler: 'Unlock: Fem 25 + browse web. Milestones at 25/100/150/500/1000 subs. Actions: strip, dance, dildo, dare give most rep. Collabs with Lily, Master, or Pink.',
              status: knowStreaming === 0 ? 'hidden' : streamRep >= 100 ? 'complete' : 'active' },
            { name: 'OnlyFems', icon: '\u2606', cat: 'Jobs & Income', val: onlyfemsTier, max: 5,
              milestones: ['T1', 'T2', 'T3', 'T4', 'T5'], milestoneCurrent: onlyfemsTier,
              desc: !onlyfemsInstalled ? '???' : onlyfemsTier >= 5 ? `Top creator \u2014 ${onlyfemsSubs} subs` : `Tier ${onlyfemsTier}/5 \u2014 ${onlyfemsSubs} subs`,
              hint: !onlyfemsInstalled ? 'Install SissySocial first, then OnlyFems' : 'Post bolder content to unlock higher tiers',
              spoiler: 'Requires SissySocial app first. Each tier unlocks more explicit content. Subscriber growth based on tier + post frequency. Passive income.',
              status: !onlyfemsInstalled ? 'hidden' : onlyfemsTier >= 5 ? 'complete' : 'active' },
            { name: 'Photographer', icon: '\u2605', cat: 'Jobs & Income', val: photoShoots, max: 5,
              milestones: ['Outdoor', 'Studio', 'Lingerie', 'Art Nudes', 'Portfolio'], milestoneCurrent: photoShoots,
              desc: !photoCard ? '???' : photoShoots >= 5 ? 'All shoots complete' : `Shoot ${photoShoots}/5`,
              hint: !photoCard ? 'Explore the park \u2014 someone might notice you' : 'Visit the park for your next shoot',
              spoiler: 'Card from park random walk event. Each shoot requires higher Fem. Revenue sharing gives passive income after shoot 3+.',
              status: !photoCard ? 'hidden' : photoShoots >= 5 ? 'complete' : 'active' },
            { name: 'Bar Outfit', icon: '\uD83D\uDC54', cat: 'Jobs & Income', val: barOutfit, max: 3,
              milestones: ['Hired', 'Storage', 'Polo', 'Dress'], milestoneCurrent: barOutfit,
              desc: barOutfitDesc, hint: barOutfitHint,
              spoiler: 'Bar outfit tiers: Storage ($50/shift) \u2192 Polo ($70) \u2192 Dress ($85). Higher tier = better tips + new bar event images. Dress requires Fem 40+.',
              status: barHidden ? 'hidden' : barOutfit >= 3 ? 'complete' : 'active' },
            { name: 'Sugar Daddy', icon: '\u2B50', cat: 'Jobs & Income', val: sugarArr, max: 3,
              milestones: ['First Date', 'Regular', 'Kept'], milestoneCurrent: sugarArr,
              desc: sugarArr === 0 ? '???' : (sugarArr >= 3 ? 'Steady arrangement' : `Stage ${sugarArr}/3`) + (smsSugarStage > 0 ? ` \u2014 SMS ${smsSugarStage}/3` : ''),
              hint: sugarArr === 0 ? 'Check dating apps once Fem 25+' : 'Accept his invitations',
              spoiler: 'GrindrR match. Dates: gala ($200) \u2192 weekend ($350) \u2192 dinner ($250) \u2192 yacht ($300) \u2192 proposal. Stage 3: $200/week retainer.',
              status: sugarArr === 0 ? 'hidden' : sugarArr >= 3 ? 'complete' : 'active' },
            { name: 'The Gilded Cage', icon: '\u266A', cat: 'Jobs & Income', val: cabProg, max: 4,
              milestones: ['First Visit', 'Floor Act', 'Stage Roster', 'Late Show'], milestoneCurrent: cabProg,
              desc: !cabDiscovered ? '???' : cabLateShow ? 'You own the room' : cabStage >= 2 ? `Performer \u2014 exhibition ${exhibFem}/50` : 'Audience',
              hint: cabaretHint,
              spoiler: 'Free venue in the Red Light District (night). Watching + floor work build exhibition nerve, Maxime offers the audition at 35+. Sets pay via the crowd-call minigame; the late show opens at stage 3 + exhibition 50.',
              status: !cabDiscovered ? 'hidden' : cabLateShow ? 'complete' : 'active' },

            // --- SOCIAL ---
            // BNWO (0.3.1 descent rework): canonical entry from storylines.js.
            // desc/hint track discovery, lecture, the 4-session phases with the
            // rest-day cooloff, the doubt walk-away pause, and Kept. Hidden
            // entirely unless the content toggle + Patreon are on.
            { name: _slData.bnwo.name, icon: '\uD83C\uDF93', cat: 'Social', val: _slData.bnwo.val, max: _slData.bnwo.max,
              milestones: _slData.bnwo.milestones, milestoneCurrent: _slData.bnwo.milestoneCurrent,
              desc: _slData.bnwo.desc,
              hint: _slData.bnwo.hint,
              spoiler: _slData.bnwo.spoiler,
              status: _slData.bnwo.status },
            // Kennel / Mira (0.2.6 base + 0.3.1 Pet Play expansion): canonical
            // entry from storylines.js. Expansion milestones render only when
            // v031 is armed + Patreon; the whole card hides when the Pet Play
            // content pref is toggled off (retrofit, KENNEL_EXPANSION.md \u00A710.2).
            { name: _slData.kennel.name, icon: '\uD83D\uDC3E', cat: 'Social', val: _slData.kennel.val, max: _slData.kennel.max,
              milestones: _slData.kennel.milestones, milestoneCurrent: _slData.kennel.milestoneCurrent,
              desc: _slData.kennel.desc,
              hint: _slData.kennel.hint,
              spoiler: _slData.kennel.spoiler,
              status: _slData.kennel.status },
            { name: 'Connections', icon: '\u2764', cat: 'Social', val: npcProgress, max: npcMax,
              desc: npcDesc,
              hint: !grindrInstalled ? 'Install GrindrR from the App Store' : 'Swipe right and accept date invitations',
              spoiler: 'Tyler: Fem 10/20/30. Kai: Fem 10/20/35. Raven: Fem 15/25/40 (wild, public). Owen: Fem 15/25/40. Elo\u00efse: Fem 40/50/60 (Patreon, glory hole). Sasha: Fem 40/50/60 (Patreon, sissy streamer). 1 date/day cooldown.',
              status: !grindrInstalled ? 'hidden' : npcProgress >= npcMax ? 'complete' : 'active' },
            { name: 'Dating Encounters', icon: '\u2764', cat: 'Social', val: datingMet, max: 5,
              desc: !hasDatingApp ? '???' : datingMet >= 5 ? 'Experienced dater' : `Met ${datingMet}/5 strangers`,
              hint: !hasDatingApp ? 'Need Fem 25 \u2014 check the App Store' : 'Swipe, match, and meet',
              spoiler: 'GrindrR swipes + random meets. 1 date per day cooldown.',
              status: !hasDatingApp ? 'hidden' : datingMet >= 5 ? 'complete' : 'active' },
            { name: 'DareMe', icon: '\uD83C\uDFAF', cat: 'Social', val: dareDone, max: 11,
              milestones: ['Tier 1', 'Tier 2', 'Tier 3', 'All Done'], milestoneCurrent: dareDone >= 11 ? 4 : dareTier,
              desc: dareDesc, hint: dareHint,
              spoiler: 'Phone app, Fem 20+. 11 dares across 3 tiers. Locations: Bar, Park, Club, Beach. Tier 2 at dare 5 (Fem 35+), Tier 3 at dare 8 (Fem 50+). Free (all tiers since v0.2.2).',
              status: !daremeInst ? 'hidden' : dareDone >= 11 ? 'complete' : 'active' },
            { name: 'Beach Bullies', icon: '\u26A0', cat: 'Social', val: bullyStage, max: 5,
              milestones: ['Encounter', 'Submission', 'Regular', 'Blackmail', 'Resolved'], milestoneCurrent: bullyStage,
              desc: bullyStage === 0 ? '???' : (bullyStage >= 5 ? 'Power shift' : bullyStage === 4 ? 'Blackmail...' : bullyStage >= 3 ? 'They own you now' : `Stage ${bullyStage}/5`) + (smsBullyStage > 0 ? ` \u2014 SMS ${smsBullyStage}/3` : ''),
              hint: bullyStage === 0 ? 'Walk to the far end of the beach' : bullyStage === 4 ? 'Pay up or find another way out' : 'Return to the beach \u2014 they remember you',
              warning: (bullyStage === 1 && day >= 20) ? "Haven't been back since that day..." : null,
              spoiler: 'Far end beach. Stages 1-3: escalating submission. Stage 4: blackmail (Fem 50+, Sub 40+). Stage 5: resolution \u2014 fame, power shift, or deletion.',
              status: bullyStage === 0 ? 'hidden' : bullyStage >= 5 ? 'complete' : 'active' },
            { name: 'Glory Hole', icon: '\u25CF', cat: 'Social', val: ghCompliance, max: 10,
              desc: !visitedGH ? '???' : `Compliance ${ghCompliance}/10`,
              hint: !visitedGH ? 'Explore the park restrooms' : "Return when you're feeling... restless",
              spoiler: 'Park restroom, needs Fem 10. 12-event rotation. Regular: stranger \u2192 voice \u2192 schedule \u2192 face. Event #12: Elo\u00efse easter egg.',
              status: !visitedGH ? 'hidden' : ghCompliance >= 10 ? 'complete' : 'active' },

            // --- SELF CARE ---
            { name: 'Laser Treatment', icon: '\u2728', cat: 'Self Care', val: laserSessions, max: 5,
              milestones: ['S1', 'S2', 'S3', 'S4', 'S5'], milestoneCurrent: laserSessions,
              desc: laserDesc, hint: laserHint, warning: laserWarning,
              spoiler: '5 salon sessions needed. Hair regrows overnight if sessions < 5 after extended time. Monthly maintenance keeps smooth.',
              status: laserStatus },
            { name: 'Makeup', icon: '\u2728', cat: 'Self Care', val: makeupSkill, max: 3,
              milestones: ['Basics', 'Intermediate', 'Expert'], milestoneCurrent: makeupSkill,
              desc: (makeupSkill === 0 && orderMakeup === 0) ? '???' : makeupSkill >= 3 ? 'Expert' : `Level ${makeupSkill}/3`,
              hint: (makeupSkill === 0 && orderMakeup === 0) ? 'Master may request lessons' : 'Continue lessons at the Salon',
              spoiler: 'Salon lessons. Each level unlocks better makeup. Level 3 = expert, auto-apply with T1+ vanity.',
              status: (makeupSkill === 0 && orderMakeup === 0) ? 'hidden' : makeupSkill >= 3 ? 'complete' : 'active' },
            { name: 'Gym', icon: '\uD83C\uDFCB', cat: 'Self Care', val: gymProg, max: 5,
              milestones: ['Membership', 'Intro', 'Training', 'Private Sessions', 'Complete'], milestoneCurrent: gymProg,
              desc: gymDesc, hint: gymHint,
              spoiler: 'Membership $50. Trainer Malik: intro after 3+ visits. Stages: Intro \u2192 Training \u2192 Private Sessions \u2192 Complete. Locker room events (7-cycle). Fem reduction via workouts.',
              status: gymHidden ? 'hidden' : gymProg >= 5 ? 'complete' : 'active' },
            { name: 'Clinic', icon: '\u2695', cat: 'Self Care', val: clinicProg, max: 6,
              milestones: ['Registered', 'Consultation', 'Blood Work', 'HRT Started', 'Advanced', 'Personal'], milestoneCurrent: clinicProg,
              desc: clinicDesc, hint: clinicHint, warning: clinicWarning,
              spoiler: 'Referral from pharmacy or Lily. Dr. Mercer: 6 stages. HRT: 4-stage body changes. Prostate therapy unlocks at doctor stage 3+. Injection schedule \u2014 missing doses has consequences.',
              status: clinicHidden ? 'hidden' : clinicProg >= 6 ? 'complete' : 'active' },
            { name: 'Spa', icon: '\uD83D\uDEC1', cat: 'Self Care', val: spaProg, max: spaMax,
              milestones: ['Member', 'Dominic Intro', 'Dominic Regular', 'Dominic Private', 'Dominic Complete', 'Camille Met', 'Camille Friends', 'Camille Close'], milestoneCurrent: spaProg,
              desc: spaDesc, hint: spaHint,
              spoiler: 'Discover via Fem 25+. Membership required. Dominic (masseur): 4 stages, escalating intimacy. Camille: 3 stages, lounge regular. Body mods: lips, hips, breasts at Fem 50+.',
              status: spaHidden ? 'hidden' : spaProg >= spaMax ? 'complete' : 'active' },
            { name: 'Velvet Lounge', icon: '\uD83C\uDF39', cat: 'Jobs & Income', val: velvetProg, max: velvetMax,
              milestones: ['First Visit', 'Regular', 'Worker', 'Vivienne 3', 'Vivienne 5'], milestoneCurrent: velvetProg >= velvetMax ? 5 : Math.min(velvetSt, 3) + (vivienneRel >= 3 ? 1 : 0) + (vivienneRel >= 5 ? 1 : 0),
              desc: velvetDesc, hint: velvetHint,
              spoiler: 'Red Light District, evening only. Customer: massage $50, private $100, premium $200 (Fem 20/30/40). Worker: Fem 50+, Vivienne rapport 3+. Vivienne: 5-stage relationship, builds through visits and conversation.',
              status: velvetHidden ? 'hidden' : velvetProg >= velvetMax ? 'complete' : 'active',
              paths: _slData.velvet.paths, decisionUnlocked: _slData.velvet.decisionUnlocked },
            { name: 'Maid Cafe', icon: '\uD83C\uDF80', cat: 'Jobs & Income', val: cafeProg, max: cafeMax,
              milestones: ['Customer', 'Regular', 'Worker', 'Hana Close'], milestoneCurrent: cafeProg >= cafeMax ? 4 : Math.min(cafeSt, 3) + (hanaRel >= 4 ? 1 : 0),
              desc: cafeDesc, hint: cafeHint,
              spoiler: 'Red Light District, evening only. Nyan Nyan Maid Cafe. Order parfait or "special tea". Worker: maid uniform, 8-event rotation, tips. Hana: 4-stage relationship.',
              status: cafeHidden ? 'hidden' : cafeProg >= cafeMax ? 'complete' : 'active',
              paths: _slData.cafe.paths, decisionUnlocked: _slData.cafe.decisionUnlocked },
            { name: 'Tattoo (Jade)', icon: '\u2710', cat: 'Social', val: jadeRapport, max: 5,
              milestones: ['Studio Found', 'First Ink', 'Regular', 'Beer Talk', 'Close'], milestoneCurrent: jadeRapport,
              desc: tattooDesc, hint: tattooHint,
              spoiler: 'Walk around neighbourhood Day 7+ to discover Inkwell. 7 tattoo designs (fem/masc/neutral). Jade: rapport builds through visits and conversation. Beer scene at rapport 4.',
              status: tattooHidden ? 'hidden' : jadeRapport >= 5 ? 'complete' : 'active' },
            { name: 'Hypno', icon: '\uD83C\uDF00', cat: 'Self Care', val: hypnoTier, max: 4,
              milestones: ['Beginner', 'Acceptance', 'Deep Conditioning', 'Surrender'], milestoneCurrent: hypnoTier,
              desc: hypnoHidden ? '???' : hypnoTier >= 4 ? 'Fully conditioned' : `Tier ${hypnoTier}/4`,
              hint: hypnoHidden ? 'Browse the web at your computer' : hypnoTier < 2 ? 'Try different sessions on the hypno site' : hypnoTier < 3 ? `Exposure ${hypnoExposure}/30 for Deep Conditioning` : hypnoTier < 4 ? `Exposure ${hypnoExposure}/50 for Surrender Protocol` : '',
              spoiler: 'Computer \u2192 Hypno Sites. Tier 1: Beginner/Sissy Acceptance. Tier 2: Total Obedience/USB Trance. Tier 3: Deep Conditioning (exposure 30+). Tier 4: Surrender Protocol (exposure 50+).',
              status: hypnoHidden ? 'hidden' : hypnoTier >= 4 ? 'complete' : 'active' },
            { name: 'Apartment', icon: '\u2302', cat: 'Self Care', val: totalUpgrades, max: 13,
              desc: upgradesHidden ? '???' : `Bed ${uBed}/3, Desk ${uDesk}/3, Vanity ${uVanity}/3, Decor ${uDecor}/3, Kitchen ${uCoffee}/1`,
              hint: upgradesHidden ? 'Save up some money first' : 'Upgrade at home when you can afford it',
              spoiler: 'Bed: +max energy (110/115/120). Desk: streaming quality. Vanity: auto-makeup/nails. Decor: shame decay speed. Kitchen: daily espresso (+15 Energy).',
              status: upgradesHidden ? 'hidden' : totalUpgrades >= 13 ? 'complete' : 'active' },
            { name: 'Resort', icon: '\ud83c\udfd6', cat: 'Social', val: resortProg, max: resortMax,
              milestones: ['Discovered', 'First Trip', 'Dante Met', 'Dante Bond', 'Iris Met', 'Iris Bond'], milestoneCurrent: Math.min(6, (resortDiscovered?1:0) + (resortVisits>0?1:0) + (danteStage>0?1:0) + (danteStage>=5?1:0) + (irisStage>0?1:0) + (irisStage>=3?1:0)),
              desc: resortDesc, hint: resortHint,
              spoiler: 'Coastal getaway. Discover via SMS. 7 hotspots: cabana, pool, beach, lounge, tiki, midnight pool, cocktail bar. Dante: 5-stage cabana arc. Iris: 3-stage front-desk arc with uniform gifts.',
              status: resortHidden ? 'hidden' : resortProg >= resortMax ? 'complete' : 'active' },
            { name: 'Weekend Events', icon: '\ud83c\udf89', cat: 'Social', val: weekendFirsts, max: 8,
              milestones: ['Pool Party', 'Foam Night', 'Bonfire', 'Cosplay', 'Master Social', 'Stream Crossover', 'Loft Rooftop', 'Walk of Shame'], milestoneCurrent: weekendFirsts,
              desc: weekendDesc, hint: weekendHint,
              spoiler: 'Saturday-evening invites via SMS. 8 unique events with first-time scenes. Walk of Shame triggers on certain morning conditions.',
              status: weekendHidden ? 'hidden' : weekendFirsts >= 8 ? 'complete' : 'active' },
            { name: 'HRT Progression', icon: '\u2697', cat: 'Self Care', val: hrtStage, max: 4,
              milestones: ['Started', 'Mood Swings', 'Soft Skin', 'Curves', 'Full'], milestoneCurrent: hrtStage,
              desc: hrtDesc, hint: hrtHint, warning: hrtWarning,
              spoiler: 'Dr. Mercer prescribes after consults. 4 body-change stages. Missed injections roll back progress and add Irritable mood.',
              status: hrtHidden ? 'hidden' : hrtStage >= 4 ? 'complete' : 'active' },
            { name: 'Tattoo Collection', icon: '\u2710', cat: 'Self Care', val: tattoosOwned, max: 7,
              milestones: ['Star', 'Abstract', 'Butterfly', 'Rose', 'Heart', 'Tribal', 'Wolf'], milestoneCurrent: tattoosOwned,
              desc: tattooCollectHidden ? '???' : tattoosOwned >= 7 ? 'Full sleeve, all 7 designs' : `${tattoosOwned}/7 designs`,
              hint: tattooCollectHidden ? 'Visit Inkwell once you find it' : tattoosOwned < 7 ? 'Pick a new design from Jade' : '',
              spoiler: '7 designs: star (subtle), abstract, butterfly, rose, heart (fem), tribal (masc), wolf (masc). Mix-and-match feminine/masculine ink.',
              status: tattooCollectHidden ? 'hidden' : tattoosOwned >= 7 ? 'complete' : 'active' },

            // --- SMS SIDE-CHAINS (one-shot, can be missed via 'Ignore' choice) ---
            { name: 'Cuckold SMS', icon: '\ud83d\udc8c', cat: 'Main Story', val: smsCuckoldStage, max: 3,
              milestones: ['Stage A', 'Stage B', 'Stage C'], milestoneCurrent: smsCuckoldStage,
              desc: smsCuckoldOffered < 0 ? '???' : smsCuckoldStage >= 3 ? 'Chain resolved' : `Stage ${smsCuckoldStage}/3`,
              hint: smsCuckoldOffered < 0 ? "An anonymous SMS may arrive once you're deep enough" : smsCuckoldStage < 3 ? 'Check your inbox \u2014 reply to advance' : '',
              spoiler: "Anonymous SMS chain. Choosing 'Ignore (skips this content permanently)' locks the chain at stage 3.",
              status: smsCuckoldOffered < 0 ? 'hidden' : smsCuckoldStage >= 3 ? 'complete' : 'active' },
            { name: 'Hot Fan', icon: '\ud83d\udcf1', cat: 'Social', val: smsFanStage, max: 3,
              milestones: ['Coffee', 'Van', 'Live Show'], milestoneCurrent: smsFanStage,
              desc: smsFanOffered < 0 ? '???' : smsFanStage >= 3 ? 'Chain resolved' : `Stage ${smsFanStage}/3`,
              hint: smsFanOffered < 0 ? 'A streaming fan may message you once you build a following' : smsFanStage < 3 ? 'Check your inbox \u2014 reply to advance' : '',
              spoiler: 'SMS chain from a fan who saw your stream. Gated on sissy_fame 30/40/50. Three escalating meets. \'Ignore\' locks at stage 3.',
              status: smsFanOffered < 0 ? 'hidden' : smsFanStage >= 3 ? 'complete' : 'active' },
            { name: 'Glory Hole Regular', icon: '\u25cf', cat: 'Social', val: ghRegularStage, max: 3,
              milestones: ['Voice', 'Schedule', 'Face'], milestoneCurrent: ghRegularStage,
              desc: !visitedGH ? '???' : ghRegularStage >= 3 ? 'Met face-to-face' : `Stage ${ghRegularStage}/3`,
              hint: !visitedGH ? 'Explore the park restrooms' : ghRegularStage < 3 ? 'Keep returning \u2014 the regular notices you' : '',
              spoiler: 'Face-recognition arc parallel to compliance counter. Stranger \u2192 recognized voice \u2192 knows schedule \u2192 face-to-face.',
              status: !visitedGH ? 'hidden' : ghRegularStage >= 3 ? 'complete' : 'active' },
            { name: 'Custom Hypno File', icon: '\ud83c\udfa7', cat: 'Self Care', val: customFileStage, max: 3,
              milestones: ['Received', 'Listened', 'Compulsive'], milestoneCurrent: customFileStage,
              desc: !customFileReceived ? '???' : customFileStage >= 3 ? 'Compulsive listener' : `Stage ${customFileStage}/3`,
              hint: !customFileReceived ? 'Match with Sasha on GrindrR' : customFileStage < 3 ? 'Listen at your computer to deepen the conditioning' : '',
              spoiler: "Sasha's personal hypno track. Independent escalation: received \u2192 listened \u2192 compulsive (the file calls you back).",
              status: !customFileReceived ? 'hidden' : customFileStage >= 3 ? 'complete' : 'active' },
        ];
    }

    // ==========================================================
    // EVALUATE NPCs (matches Unity CompletionApp.EvaluateNPCs)
    // ==========================================================

    _evaluateNPCs(iv, ib, is) {
        const mi = iv('master_intensity');
        const mr = iv('MasterRelationship');
        const masterName = is('MasterName') || 'Alex';
        const gfName = is('GirlfriendName') || 'Sarah';
        const sarahRel = iv('sarah_relationship');
        const gfBroke = ib('girlfriend_broken_up'), gfAccepts = ib('girlfriend_accepts_fem'), gfCucked = ib('girlfriend_cucked');
        const npcs = [];

        // Master (skipped on Independent, master_intensity == 0 means the
        // landlord lives abroad and there is no Master NPC to track).
        if (mi >= 1) {
            const intensityLabel = mi >= 3 ? 'Owner' : mi >= 2 ? 'Landlord / Master' : 'Roommate';
            // Route caps mirror compute_mr_cap() in Globals.ink: Roommate=4,
            // Landlord=7, Owner=10. Showing the route ceiling instead of /10
            // stops the journal from telling a Roommate-route player to keep
            // following orders forever (no orders chain exists on Roommate).
            const mrCap = mi >= 3 ? 10 : mi >= 2 ? 7 : 4;
            const atCap = mr >= mrCap;
            npcs.push({ name: masterName, role: intensityLabel, location: 'Apartment, Bar',
                portrait: 'assets/images/Portraits/master_normal.png',
                tags: ['Dom', 'BDSM', 'Orders'], relCurrent: mr, relMax: mrCap,
                statusText: atCap
                    ? (mi >= 3 ? 'Devoted' : 'Maxed')
                    : (mr >= 8 ? 'Devoted' : mr >= 5 ? 'Obedient' : mr >= 2 ? 'Compliant' : 'Distant'),
                hint: atCap
                    ? (mi >= 3 ? 'He trusts you completely'
                      : mi >= 2 ? 'Landlord arc capped. No deeper progression on this route'
                      : 'Roommate arc capped. Casual cohabitation has no further depth')
                    : (mi >= 3
                        ? (mr < 3 ? 'Follow his orders at the bar' : mr < 6 ? 'Keep following orders' : mr < 8 ? 'Prove your devotion' : 'He trusts you completely')
                        : mi >= 2
                        ? (mr < 3 ? 'Stay on his good side: pay rent, keep showing up at the Bar' : 'Keep working Bar shifts; Day 7/14/21 deepen the dynamic')
                        : (mr < 2 ? 'Casual roommate vibe. See him at the Bar' : 'Day 7/14/21 each nudge the dynamic up one notch')),
                spoiler: mi >= 3
                    ? `MR ${mr}/10 (Owner). Orders: chastity, plug, laser, hair, makeup. Collar reward at MR 8+. Post-collar: 5-event rotation.`
                    : mi >= 2
                    ? `MR ${mr}/7 (Landlord). Rent-driven dynamic, no orders chain. Cap baseline 4 + Day 7/14/21 bonuses.`
                    : `MR ${mr}/4 (Roommate). Casual cohabitation, no orders chain. Cap baseline 1 + Day 7/14/21 bonuses.`,
                linkedStoryline: 'master',
                met: true, color: '#9b59b6', patreonOnly: false, patreonPermanent: false });
        }

        // Girlfriend \u2014 skipped when disabled at character editor (mirrors
        // Master's intensity-0 skip above). Editor sets girlfriend_disabled
        // = true + girlfriend_broken_up = true; the contact card was the
        // last UI surface still rendering Sarah as a tracked NPC on those
        // runs ("ghost Sarah" in the people panel).
        if (!ib('girlfriend_disabled')) {
            // Boyfriend start (0.3.1): same shared state machine, partner-aware copy.
            const isBf = ib('partner_is_male');
            const gfStatus = gfBroke ? 'Broken up' : gfAccepts ? 'Accepts you' : gfCucked ? 'Cucked' : sarahRel <= -2 ? 'Strained' : sarahRel >= 4 ? 'Close' : 'Together';
            npcs.push({ name: gfName, role: isBf ? 'Boyfriend' : 'Girlfriend', location: 'Apartment (weekends)',
                portrait: isBf ? 'assets/images/Portraits/boyfriend_casual.png' : 'assets/images/Portraits/girlfriend_casual.png',
                tags: isBf ? ['Romance', 'Drama'] : ['Romance', 'Cuck', 'Drama'], relCurrent: Math.max(0, Math.min(sarahRel + 10, 20)), relMax: 20,
                statusText: gfStatus,
                hint: gfBroke ? '' : gfAccepts
                    ? (isBf ? "He's on your side now" : "She's on your side now")
                    : (isBf ? 'He visits Saturday \u2014 he notices more than he says' : 'She visits Saturday \u2014 choose your outfit wisely'),
                spoiler: `Relationship ${sarahRel}/10. Breakup at \u2264 -4. Tension ${iv('sarah_tension')}/10. Arc: ${['undecided','acceptance','breakup','cuckold','open','domme'][iv('sarah_arc')] || 'undecided'}.`,
                warning: !gfBroke && sarahRel <= -2 ? 'Relationship at risk' : null,
                // linkedStoryline keys into systems/storylines.js \u2192 the NPC profile
                // detail view embeds the storyline's path-cards inline.
                linkedStoryline: 'girlfriend',
                met: true, color: '#e88cb8', patreonOnly: false, patreonPermanent: false });
        }

        // Jake (Lifeguard)
        const lifeguardStage = iv('lifeguard_stage');
        npcs.push({ name: 'Jake', role: 'Lifeguard', location: 'Beach',
            portrait: 'assets/images/Beach/beach_lifeguard_meet.png',
            tags: ['Hookup', 'Beach'], relCurrent: lifeguardStage, relMax: 3,
            statusText: lifeguardStage >= 3 ? 'Regular hookups' : `Stage ${lifeguardStage}/3`,
            hint: lifeguardStage === 0 ? 'Check out the lifeguard tower at the beach' : 'Visit the tower again...',
            spoiler: 'Stages: chat (Fem 15+) \u2192 sunscreen \u2192 hut. 2 repeat variants.',
            met: lifeguardStage > 0, color: '#5bc0de', patreonOnly: false, patreonPermanent: false });

        // Beach Bullies
        const bullyStage = iv('beach_bully_stage');
        npcs.push({ name: 'Beach Bullies', role: 'Gym Rats', location: 'Beach (far end)',
            portrait: 'assets/images/Portraits/bully.png',
            tags: ['Humiliation', 'Exhib', 'Blackmail'], relCurrent: bullyStage, relMax: 5,
            statusText: bullyStage >= 5 ? 'Power shifted' : bullyStage === 4 ? 'Blackmailing you' : bullyStage >= 3 ? 'They own you' : `Stage ${bullyStage}/5`,
            hint: bullyStage === 0 ? 'The far end of the beach looks quiet...' : bullyStage === 4 ? 'Go to the far end of the beach and pay up. 3 payments total to trigger the resolution' : "They're waiting at the far end of the beach",
            spoiler: 'Far end beach. Stages 1-3: escalating submission. Stage 4: blackmail (Fem 50+, Sub 40+). Visit the beach far end 3 more times to pay, then Stage 5 resolves.',
            met: bullyStage > 0, color: '#e05050', patreonOnly: false, patreonPermanent: false });

        // Photographer
        const photoCard = ib('photographer_card'), photoShoots = iv('photographer_shoots');
        npcs.push({ name: 'The Photographer', role: 'Park Photographer', location: 'Park',
            portrait: 'assets/images/Portraits/photographer.png',
            tags: ['Exhib', 'Modeling'], relCurrent: photoShoots, relMax: 5,
            statusText: photoShoots >= 5 ? 'Portfolio complete' : `Shoot ${photoShoots}/5`,
            hint: !photoCard ? 'Walk in the park \u2014 you might catch someone\'s eye' : "He's got another shoot planned",
            spoiler: 'Park random event gives card. Shoots: outdoor \u2192 studio \u2192 lingerie \u2192 art nudes \u2192 portfolio.',
            met: photoCard, color: '#e8c860', patreonOnly: false, patreonPermanent: false });

        // Sugar Daddy
        const sugarArr = iv('sugar_arrangement');
        npcs.push({ name: 'Richard', role: 'Sugar Daddy', location: 'Dating App / Events',
            portrait: 'assets/images/Portraits/sugar_daddy.png',
            tags: ['Sugar', 'Finance', 'Luxury'], relCurrent: sugarArr, relMax: 3,
            statusText: sugarArr >= 3 ? 'Kept' : `Arrangement ${sugarArr}/3`,
            hint: sugarArr === 0 ? 'Wealthy attention on dating apps (Fem 25+)' : 'Accept his next invitation',
            spoiler: 'GrindrR match at Fem 25+. Dates: gala \u2192 weekend \u2192 dinner \u2192 yacht \u2192 proposal. $200/week retainer at stage 3.',
            met: sugarArr > 0, color: '#e8c860', patreonOnly: false, patreonPermanent: false });

        // Veronica (Clerk)
        const clerkRel = iv('clerk_relationship');
        npcs.push({ name: 'Veronica', role: 'Adult Store Clerk', location: 'Mall',
            portrait: 'assets/images/Mall/mall_clerk_intro.png',
            tags: ['Toys', 'Kink'], relCurrent: clerkRel, relMax: 3,
            statusText: clerkRel >= 3 ? 'Testing partner' : `Stage ${clerkRel}/3`,
            hint: clerkRel === 0 ? 'Visit the adult store a few times' : 'She warms up to regulars',
            spoiler: 'Introduces at visit 3. Discount at visit 6. Back room at visit 10. Patreon for stage 2+.',
            met: clerkRel > 0, color: '#e06878', patreonOnly: false, patreonPermanent: false });

        // Club NPCs
        const clubAccess = ib('club_access');
        const mamaveeRel = iv('club_manager_relationship');
        npcs.push({ name: 'Mama Vee', role: 'Club Manager', location: 'Club',
            portrait: 'assets/images/Club/club_work_closing.png',
            tags: ['Authority', 'Dress Code'], relCurrent: mamaveeRel, relMax: 10,
            statusText: clubAccess ? (mamaveeRel >= 5 ? 'Trusted' : 'Professional') : '???',
            hint: !clubAccess ? 'Get into the club first (Fem 25+)' : 'She decides dress codes and promotions',
            spoiler: `Manager relationship ${mamaveeRel}/10. Controls outfit orders, shift assignments, VIP access.`,
            met: clubAccess, color: '#e05078', patreonOnly: false, patreonPermanent: false });

        const marcoRel = iv('club_regular_marco');
        npcs.push({ name: 'Marco', role: 'Club Regular', location: 'Club (bar)',
            portrait: 'assets/images/Club/club_regular_marco_intimate.png',
            tags: ['VIP', 'Hookup'], relCurrent: marcoRel, relMax: 3,
            statusText: marcoRel >= 3 ? 'Intimate' : `Stage ${marcoRel}/3`,
            hint: marcoRel === 0 ? 'Hang out at the club bar' : 'He buys drinks for his favorites',
            spoiler: 'Businessman. Meet \u2192 advances \u2192 VIP booth.',
            met: marcoRel > 0, color: '#e05078', patreonOnly: false, patreonPermanent: false });

        const diegoRel = iv('club_regular_diego');
        npcs.push({ name: 'Diego', role: 'Club DJ', location: 'Club (dancefloor)',
            portrait: 'assets/images/Club/club_regular_diego_intimate.png',
            tags: ['Hookup', 'Music'], relCurrent: diegoRel, relMax: 3,
            statusText: diegoRel >= 3 ? 'Backstage' : `Stage ${diegoRel}/3`,
            hint: diegoRel === 0 ? 'Dance more \u2014 the DJ notices pretty faces (Fem 40+)' : "He's got backstage access...",
            spoiler: 'Needs Fem 40+ to meet. Introduce \u2192 connection \u2192 backstage.',
            met: diegoRel > 0, color: '#e05078', patreonOnly: false, patreonPermanent: false });

        const lilyRel = iv('club_regular_lily');
        npcs.push({ name: 'Lily', role: 'Fellow Dancer', location: 'Club (dressing room)',
            portrait: 'assets/images/Club/club_regular_lily_meet.png',
            tags: ['Friendship', 'Trans', 'Mentor'], relCurrent: lilyRel, relMax: 4,
            statusText: lilyRel >= 4 ? 'Best friend' : `Stage ${lilyRel}/4`,
            hint: lilyRel === 0 ? "Get the club job \u2014 you'll share a dressing room" : 'The closest thing to a real friend',
            spoiler: 'Trans dancer, mentor. Meet \u2192 friendship \u2192 rooftop \u2192 bonding. Stream collab available.',
            met: lilyRel > 0, color: '#e88cb8', patreonOnly: false, patreonPermanent: false });

        // Victor (Maid client)
        const victorStage = iv('maid_client_stage');
        const maidHired = ib('maid_hired');
        npcs.push({ name: 'Victor Ashford', role: 'Maid Client', location: 'Client Residence',
            portrait: 'assets/images/Maid/maid_victor_desk.png',
            tags: ['Service', 'Dom', 'Wealthy'], relCurrent: victorStage, relMax: 5,
            statusText: victorStage >= 5 ? 'Personal maid' : `Stage ${victorStage}/5`,
            hint: !maidHired ? 'Get the maid job first' : victorStage === 0 ? 'Work more shifts' : 'Victor expects perfection',
            spoiler: 'Stages: inspection \u2192 mirror \u2192 apron \u2192 under desk \u2192 arrangement.',
            met: victorStage > 0, color: '#5bc0de', patreonOnly: false, patreonPermanent: false });

        // Mr. Hartwell
        const officeStage = iv('office_stage'), bossStage = iv('office_boss_stage');
        npcs.push({ name: 'Mr. Hartwell', role: 'Managing Partner', location: 'Office',
            portrait: 'assets/images/Office/office_boss_office.png',
            tags: ['Office', 'Dom', 'Slow Burn'], relCurrent: bossStage, relMax: 5,
            statusText: bossStage >= 5 ? 'His right hand' : `Boss stage ${bossStage}/5`,
            hint: officeStage === 0 ? 'Check JobFinder for office listings' : 'He notices everything',
            spoiler: 'Boss chain: Touch \u2192 CloseCall \u2192 ClosedDoor \u2192 UnderDesk \u2192 AfterHours.',
            met: officeStage > 0, color: '#7a8090', patreonOnly: false, patreonPermanent: false });

        // Rachel
        const rachelRel = iv('rachel_relationship');
        npcs.push({ name: 'Rachel', role: 'Office Receptionist', location: 'Office',
            portrait: 'assets/images/Portraits/rachel_friendly.png',
            tags: ['Friendship', 'Support'], relCurrent: rachelRel, relMax: 3,
            statusText: officeStage === 0 ? '???' : rachelRel >= 3 ? 'Inner circle' : rachelRel >= 2 ? 'Lunch buddy' : rachelRel >= 1 ? 'Friendly' : 'Coworker',
            hint: officeStage === 0 ? 'Get the office job first' : 'She warns you about office politics',
            spoiler: 'Supportive, non-romantic. Lunch companion. Warns about Derek.',
            met: officeStage > 0, color: '#7a8090', patreonOnly: false, patreonPermanent: false });

        // Derek
        const derekRel = iv('derek_relationship');
        npcs.push({ name: 'Derek', role: 'Office Rival', location: 'Office',
            portrait: 'assets/images/Portraits/derek_smug.png',
            // relMax caps at 3 for free players (the rel 3->5 office hookup arc is is_patreon()-gated);
            // patrons see the full 0-5 bar. Avoids a free tracker parked at 3/5 reading as "stuck/broken".
            tags: ['Rival', 'Tension'], relCurrent: derekRel, relMax: isPat() ? 5 : 3,
            statusText: officeStage === 0 ? '???' : derekRel >= 5 ? 'His, in the office' : derekRel >= 4 ? 'Behind closed doors' : derekRel >= 3 ? 'Tense alliance' : derekRel >= 2 ? 'Backed off' : derekRel >= 1 ? 'Smug' : 'Mocking',
            hint: officeStage === 0 ? 'Get the office job first' : 'Jealous of your relationship with the boss',
            spoiler: 'Antagonist. Flirty with an edge. Attitude shifts as you feminize.',
            met: officeStage > 0, color: '#7a8090', patreonOnly: false, patreonPermanent: false });

        // Malik (Gym Trainer)
        const trainerStage = iv('trainer_stage');
        const gymMember = ib('gym_member');
        npcs.push({ name: 'Malik', role: 'Personal Trainer', location: 'Gym',
            portrait: 'assets/images/Gym/gym_trainer_intro.png',
            tags: ['Fitness', 'Dom', 'Slow Burn'], relCurrent: trainerStage, relMax: 4,
            statusText: trainerStage >= 4 ? 'Complete' : trainerStage >= 3 ? 'Private sessions' : trainerStage >= 2 ? 'Training' : trainerStage >= 1 ? 'Introduced' : '???',
            hint: !gymMember ? 'Join the gym first' : trainerStage === 0 ? 'Work out regularly \u2014 the trainer notices dedicated members' : trainerStage < 4 ? "He's got a special training plan for you..." : "You're his favorite client",
            spoiler: 'Stages: Intro (3+ visits) \u2192 Training \u2192 Private Sessions \u2192 Complete. Locker room events include Malik encounters.',
            met: trainerStage > 0, color: '#7090c0', patreonOnly: false, patreonPermanent: false });

        // Dr. Mercer (Clinic)
        const doctorStage = iv('doctor_stage');
        const clinicReg = ib('clinic_registered');
        const hrtStage = iv('hrt_stage');
        npcs.push({ name: 'Dr. Mercer', role: 'Physician', location: 'Clinic',
            portrait: 'assets/images/Clinic/clinic_doctor_intro.png',
            tags: ['Medical', 'HRT', 'Intimate'], relCurrent: doctorStage, relMax: 5,
            statusText: doctorStage >= 5 ? 'Personal' : doctorStage >= 4 ? 'Advanced' : doctorStage >= 3 ? 'HRT started' : doctorStage >= 2 ? 'Blood work' : doctorStage >= 1 ? 'Consultation' : '???',
            hint: !clinicReg ? 'Get a clinic referral first' : doctorStage === 0 ? 'Schedule your first consultation' : doctorStage < 5 ? 'Follow the treatment plan...' : 'She knows everything about you',
            spoiler: `6 stages: Consultation \u2192 Blood Work \u2192 HRT Started \u2192 Advanced \u2192 Personal. HRT stage: ${hrtStage}/4. Prostate therapy at stage 3+.`,
            warning: (hrtStage >= 1 && iv('hrt_missed_count') >= 2) ? 'Missing HRT injections' : null,
            met: doctorStage > 0, color: '#60c0a0', patreonOnly: false, patreonPermanent: false });

        // GrindrR Dating NPCs
        const datingNPCDefs = [
            { id: 'tyler', name: 'Tyler', tags: ['Jock', 'FWB', 'Curious'], matchVar: null, relVar: 'dating_tyler', color: '#e06878', patreonOnly: false, patreonPermanent: false },
            { id: 'kai', name: 'Kai', tags: ['Romance', 'Tender', 'Femboy'], matchVar: null, relVar: 'dating_kai', color: '#e06878', patreonOnly: false, patreonPermanent: false },
            { id: 'raven', name: 'Raven', tags: ['Exhib', 'Public', 'Wild Card'], matchVar: 'matched_raven', relVar: 'dating_raven', color: '#e06878', patreonOnly: false, patreonPermanent: false },
            { id: 'owen', name: 'Owen', tags: ['Neighbor', 'Dom', 'Slow Burn'], matchVar: null, relVar: 'dating_owen', color: '#e06878', patreonOnly: false, patreonPermanent: false },
            { id: 'eloise', name: 'Elo\u00efse', tags: ['BDSM', 'Shibari', 'Femdom', 'by BlueOwl'], matchVar: 'matched_eloise', relVar: 'dating_eloise', color: '#c470e0', patreonOnly: true, patreonPermanent: true },
            { id: 'sasha', name: 'Sasha', tags: ['Sissy', 'Streamer', 'Hypno', 'by Sasha Black'], matchVar: 'matched_sasha', relVar: 'dating_sasha', color: '#c470e0', patreonOnly: true, patreonPermanent: true },
            { id: 'patrick', name: 'Patrick', tags: ['Goth', 'Pleasure Dom', 'Poly', 'by Lee McG'], matchVar: 'matched_patrick', relVar: 'dating_patrick', color: '#c470e0', patreonOnly: true, patreonPermanent: true },
            { id: 'vincent', name: 'Vincent', tags: ['Degradation', 'Transactional', 'Rough'], matchVar: 'matched_vincent', relVar: 'dating_vincent', color: '#c470e0', patreonOnly: true, patreonPermanent: true },
            { id: 'scar', name: 'Scar', tags: ['Genderfluid', 'Switch', 'Tailor', 'by ScarletMemory'], matchVar: 'matched_scar', relVar: 'dating_scar', color: '#c470e0', patreonOnly: true, patreonPermanent: true },
            { id: 'rachel', name: 'Rachel', tags: ['Romance', 'Office', 'PC Tops'], matchVar: 'matched_rachel', relVar: 'dating_rachel', color: '#e06878', patreonOnly: false, patreonPermanent: false },
            { id: 'mara', name: 'Mara', tags: ['Romance', 'Gym', 'PC Tops'], matchVar: 'matched_mara', relVar: 'dating_mara', color: '#e06878', patreonOnly: false, patreonPermanent: false },
            { id: 'cole', name: 'Cole', tags: ['Bi', 'Masc', 'PC Tops'], matchVar: 'matched_cole', relVar: 'dating_cole', color: '#e06878', patreonOnly: false, patreonPermanent: false },
        ];
        for (const d of datingNPCDefs) {
            const stage = Math.min(iv(d.relVar), 3);
            const matched = d.matchVar ? ib(d.matchVar) : stage > 0 || ib('grindr_installed');
            npcs.push({ name: d.name, role: 'GrindrR Match', location: stage >= 3 ? 'Regular' : matched ? 'Dating' : 'GrindrR',
                portrait: `assets/images/Computer/dating_${d.id}_profile.png`,
                tags: d.tags, relCurrent: stage, relMax: 3,
                statusText: stage >= 3 ? 'Maxed' : `Date ${stage}/3`,
                hint: !matched ? 'Swipe on GrindrR' : `Check messages from ${d.name}`,
                spoiler: `GrindrR NPC. 3 date stages.`,
                met: stage > 0, color: d.color,
                patreonOnly: d.patreonOnly, patreonPermanent: d.patreonPermanent });
        }

        // Vivienne (Velvet Lounge), composite: trust (0-5) + worker stage (0-3),
        // matches storyline tracker formula so a full-green tile requires
        // BOTH maxed trust AND worker promotion. Foundby:synaps 2026-05-11.
        const vivRel = iv('vivienne_relationship');
        const velvetStNpc = iv('velvet_stage');
        const vivProg = Math.min(velvetStNpc, 3) + Math.min(vivRel, 5);
        npcs.push({ name: 'Vivienne', role: 'Lounge Owner', location: 'Velvet Lounge',
            portrait: 'assets/images/Portraits/vivienne.png',
            tags: ['French', 'Protective', 'Elegant'], relCurrent: vivProg, relMax: 8,
            statusText: vivRel >= 5 ? 'Protector' : vivRel >= 3 ? 'Trusts you' : vivRel >= 1 ? 'Acquainted' : '???',
            hint: velvetStNpc === 0 ? 'Visit the Velvet Lounge in the Red Light District' : velvetStNpc < 3 && vivRel >= 3 ? 'Apply for work, talk to Vivienne about the dressing room' : vivRel < 3 ? 'Browse services, talk to her between visits' : velvetStNpc < 3 ? 'Apply for work to keep her storyline moving' : vivRel < 5 ? 'Keep working shifts' : '',
            spoiler: 'French hostess, mid-40s. 5-stage trust + 3-stage worker path. Rapport 3+ unlocks worker application. Protective mentor figure.',
            met: vivRel > 0 || velvetStNpc > 0, color: '#c4a0d0', patreonOnly: false, patreonPermanent: false });

        // Hana (Maid Cafe)
        const hanaRelNpc = iv('hana_relationship');
        const cafeStNpc = iv('cafe_stage');
        npcs.push({ name: 'Hana', role: 'Cafe Owner', location: 'Maid Cafe',
            portrait: 'assets/images/Portraits/hana_portrait.png',
            tags: ['Kawaii', 'Manager', 'Strict'], relCurrent: hanaRelNpc, relMax: 4,
            statusText: hanaRelNpc >= 4 ? 'Favorite' : hanaRelNpc >= 2 ? 'Friendly' : hanaRelNpc >= 1 ? 'Met' : '???',
            hint: cafeStNpc === 0 ? 'Visit Nyan Nyan in the Red Light District' : 'Order food, chat, work shifts',
            spoiler: 'Cafe manager. 4-stage relationship. Hires workers after rapport 2+.',
            met: hanaRelNpc > 0, color: '#e88cb8', patreonOnly: false, patreonPermanent: false });

        // Jade (Tattoo)
        const jadeRapportNpc = iv('jade_rapport');
        npcs.push({ name: 'Jade', role: 'Tattoo Artist', location: 'Inkwell Studio',
            portrait: 'assets/images/Portraits/jade_portrait.png',
            tags: ['Artist', 'Chill', 'Real Talk'], relCurrent: jadeRapportNpc, relMax: 5,
            statusText: jadeRapportNpc >= 5 ? 'Close friends' : jadeRapportNpc >= 3 ? 'Regular' : jadeRapportNpc >= 1 ? 'Met' : '???',
            hint: jadeRapportNpc === 0 ? 'Walk around the neighbourhood after Day 7' : jadeRapportNpc < 5 ? 'Visit the studio and talk between tattoos' : '',
            spoiler: 'Non-binary artist. 5-stage rapport. Beer scene at 4. Philosophical conversations about identity.',
            met: jadeRapportNpc > 0, color: '#70b8a0', patreonOnly: false, patreonPermanent: false });

        // v0.2.6 additions
        // Dante (Resort Cabana), 5-stage arc, plus 3 repeat-cycle variants after stage 5
        const danteStNpc = iv('dante_stage');
        const danteRap = iv('dante_rapport');
        npcs.push({ name: 'Dante', role: 'Cabana Bartender', location: 'Cove Resort',
            portrait: 'assets/images/Portraits/dante.png',
            tags: ['Silver Fox', 'Older', 'Slow Burn'], relCurrent: Math.min(danteStNpc, 5) + Math.min(danteRap, 5), relMax: 10,
            statusText: danteStNpc >= 5 ? 'Yacht weekend' : danteStNpc >= 3 ? 'Intimate' : danteStNpc >= 1 ? 'Flirting' : '???',
            hint: !ib('resort_discovered') ? 'Discover the Cove Resort (Fem 30+ SMS)' : danteStNpc === 0 ? 'Visit the cabana row at the resort' : danteStNpc < 5 ? 'Return to the resort and meet him at the cabana' : '',
            spoiler: '5-stage cabana arc. After stage 5: weekend yacht arc invitation. 3 repeat-variants per cycle.',
            met: danteStNpc > 0 || danteRap > 0, color: '#7a96b8', patreonOnly: false, patreonPermanent: false });

        // Iris (Resort Front Desk), 3-stage uniform-gift arc
        const irisStNpc = iv('iris_stage');
        npcs.push({ name: 'Iris', role: 'Front Desk', location: 'Cove Resort',
            portrait: 'assets/images/Portraits/iris.png',
            tags: ['Manager', 'Uniform', 'Warm'], relCurrent: irisStNpc, relMax: 3,
            statusText: irisStNpc >= 3 ? 'Promoted' : irisStNpc >= 1 ? 'Acquainted' : '???',
            hint: !ib('resort_discovered') ? 'Discover the Cove Resort first' : irisStNpc === 0 ? 'Check in at the lobby' : irisStNpc < 3 ? 'Return and chat at the front desk' : '',
            spoiler: '3-stage arc. Uniform-gift progression. Resort regulars status at stage 3.',
            met: irisStNpc > 0, color: '#e0a878', patreonOnly: false, patreonPermanent: false });

        // Dominic (Spa Masseur), 4-stage escalating-intimacy arc
        const masseurStNpc = iv('masseur_stage');
        npcs.push({ name: 'Dominic', role: 'Spa Masseur', location: 'Spa',
            portrait: 'assets/images/Portraits/dominic.png',
            tags: ['Hands', 'Patient', 'Discreet'], relCurrent: masseurStNpc, relMax: 4,
            statusText: masseurStNpc >= 4 ? 'After-hours regular' : masseurStNpc >= 2 ? 'Intimate' : masseurStNpc >= 1 ? 'Met' : '???',
            hint: !ib('spa_member') ? 'Join the Spa (Fem 25+)' : masseurStNpc === 0 ? 'Book a massage' : masseurStNpc < 4 ? 'Return for repeat sessions' : '',
            spoiler: '4-stage masseur arc. Escalating intimacy. After-hours unlock at stage 3.',
            met: masseurStNpc > 0, color: '#9aa090', patreonOnly: false, patreonPermanent: false });

        // Camille (Spa Lounge Regular), 3-stage
        const camStNpc = iv('camille_stage');
        npcs.push({ name: 'Camille', role: 'Spa Regular', location: 'Spa Lounge',
            portrait: 'assets/images/Portraits/camille.png',
            tags: ['Elegant', 'Older', 'Mentor'], relCurrent: camStNpc, relMax: 3,
            statusText: camStNpc >= 3 ? 'Confidante' : camStNpc >= 1 ? 'Lounge friend' : '???',
            hint: !ib('spa_member') ? 'Join the Spa first' : camStNpc === 0 ? 'Visit the lounge area' : camStNpc < 3 ? 'Sit with her between treatments' : '',
            spoiler: '3-stage lounge regular. Mentor figure. Patreon body-mod recommendations at stage 2+.',
            met: camStNpc > 0, color: '#d8a8b0', patreonOnly: false, patreonPermanent: false });

        // Mira (Kennel Keyholder), Patreon Chastity-Deep system
        const miraRel = iv('mira_relationship');
        const miraDevotion = iv('mira_pet_devotion');
        const miraIntroduced = ib('mira_introduced');
        // pref_pet_play (0.3.1 retrofit): card stays (discovered progress is
        // historical), but the next-step hint mutes when the track is toggled
        // off post-arm — hint gate mirrors the real FetishStore entry gate.
        const miraPetPlayOn = !ib('v031_content_enabled') || ib('pref_pet_play');
        if (miraIntroduced) {
            npcs.push({ name: 'Mira', role: 'Kennel Keyholder', location: 'The Kennel',
                portrait: 'assets/images/Portraits/mira.png',
                linkedStoryline: 'kennel',
                tags: ['Domme', 'Patient', 'Kennel'], relCurrent: Math.min(miraRel, 10), relMax: 10,
                statusText: miraRel >= 8 ? 'Owned pet' : miraRel >= 5 ? 'Regular' : miraRel >= 2 ? 'Met' : 'Introduced',
                hint: !miraPetPlayOn ? '' : miraRel < 3 ? 'Book a session at the Kennel' : ib('permanent_lock') ? 'Permanent lock accepted' : miraDevotion < 6 ? 'Continue kitten / puppy / bondage sessions' : '',
                spoiler: 'Femme-dom keyholder. Kitten + puppy + bondage tracks. Devotion stacks across all three. Permanent-lock endgame option.',
                met: true, color: '#a880c0', patreonOnly: false, patreonPermanent: false });
        }

        // Streaming Regulars (5 named, Streaming Rework — free), consolidated card
        const regulars = [
            { id: 'xxfemboi', label: 'xxFemBoi' },
            { id: 'silent_admirer', label: 'Silent Admirer' },
            { id: 'sub_in_training', label: 'SubInTraining' },
            { id: 'the_doctor', label: 'TheDoctor' },
            { id: 'bigspender', label: 'BigSpender' },
        ];
        const regDiscovered = regulars.filter(r => ib('regular_' + r.id + '_discovered'));
        if (regDiscovered.length > 0) {
            let totalLoyalty = 0, totalDM = 0;
            for (const r of regulars) {
                totalLoyalty += Math.min(5, iv('regular_' + r.id + '_loyalty'));
                if (ib('regular_' + r.id + '_dm_unlocked')) totalDM++;
            }
            const namesDiscovered = regDiscovered.map(r => r.label).join(', ');
            npcs.push({ name: 'Streaming Regulars', role: '5 named viewers', location: 'Streaming',
                portrait: null,
                tags: ['Regulars', 'DMs', 'Tips'], relCurrent: totalLoyalty, relMax: 25,
                statusText: totalDM === 5 ? 'All in DMs' : totalDM > 0 ? `${totalDM}/5 in DMs` : `${regDiscovered.length}/5 spotted`,
                hint: regDiscovered.length < 5 ? 'Stream more to surface new regulars' : totalDM < 5 ? 'Build loyalty to unlock private DM channels' : '',
                spoiler: `Discovered: ${namesDiscovered}. Each has loyalty 0-5 + a private DM thread that unlocks at loyalty 3.`,
                met: true, color: '#c47080', patreonOnly: false, patreonPermanent: false });
        }

        // ===== v0.2.7 storyline cards =====
        // Apartment expansions went free in 0.2.8 (owner gate decision
        // 2026-06-10) — patreonOnly flags removed from shop.js items and
        // these tracker cards alike.

        // Basement Workshop (free since 0.2.8)
        const basementOwned = ib('apartment_has_basement');
        const wMasterStage = iv('workshop_master_stage');
        const wVincentStage = iv('workshop_vincent_stage');
        const basementProg = (basementOwned ? 1 : 0) + Math.min(3, wMasterStage) + Math.min(3, wVincentStage);
        npcs.push({ name: 'Basement Workshop', role: 'Apartment expansion', location: 'Apartment',
            portrait: 'assets/images/Apartment/basement_workshop.png',
            tags: ['Apartment', 'Workshop', 'BDSM'], relCurrent: basementProg, relMax: 7,
            statusText: !basementOwned ? 'Not leased' : `Master ${wMasterStage}/3 · Vincent ${wVincentStage}/3`,
            hint: !basementOwned ? 'Lease the basement at Apartment Upgrades (Day 15, $800)'
                : wMasterStage < 3 && iv('master_intensity') >= 1 ? `Invite ${is('MasterName') || 'Master'} down to the workshop`
                : wVincentStage < 3 && iv('dating_vincent') >= 2 ? 'Invite Vincent over for a workshop scene'
                : '',
            spoiler: 'Basement Workshop lease + invite-driven NPC scenes. Master arc (3 stages, master_intensity 1+) comes with the lease, Vincent arc (3 stages) needs Vincent matched on GrindrR (Patreon dating NPC).',
            met: basementOwned, color: '#a070b0', patreonOnly: false, patreonPermanent: false });

        // Playroom (free since 0.2.8)
        const playroomOwned = ib('apartment_has_playroom');
        const pGfStage = iv('playroom_gf_stage');
        const pKaiStage = iv('playroom_kai_stage');
        const pHanaStage = iv('playroom_hana_stage');
        const playroomProg = (playroomOwned ? 1 : 0) + Math.min(3, pGfStage) + Math.min(3, pKaiStage) + Math.min(3, pHanaStage);
        const nestTier_ = iv('apartment_nest_tier');
        npcs.push({ name: 'Playroom', role: 'Apartment expansion', location: 'Apartment',
            portrait: 'assets/images/Apartment/playroom_main.png',
            tags: ['Apartment', 'BDSM', 'Sweet'], relCurrent: playroomProg, relMax: 10,
            statusText: !playroomOwned ? `Nest tier ${nestTier_}/2 to unlock` : `GF ${pGfStage}/3 · Kai ${pKaiStage}/3 · Hana ${pHanaStage}/3`,
            hint: !playroomOwned && nestTier_ < 2 ? 'Buy decor in Apartment Upgrades to reach Nest Tier 2'
                : !playroomOwned ? 'Convert the spare room at Apartment Upgrades ($1200)'
                : pGfStage < 3 && !ib('girlfriend_disabled') && !ib('girlfriend_broken_up') && !ib('partner_is_male') && iv('sarah_arc') !== 5 ? 'Invite your girlfriend to the playroom'
                : pHanaStage < 3 && iv('hana_relationship') >= 3 ? 'Invite Hana to the playroom'
                : pKaiStage < 3 && iv('dating_kai') >= 2 ? 'Invite Kai to the playroom'
                : '',
            spoiler: 'Playroom conversion + invite scenes. GF / Hana / Kai invites unlock with their respective relationships. Sex swing + position training also available.',
            met: playroomOwned, color: '#e88cb8', patreonOnly: false, patreonPermanent: false });

        // Comfort Training (free)
        const ctMastered = ib('comfort_mastery_unlocked');
        const ctW = iv('waist_trainer_count') + iv('waist_trainer_adv_count') + iv('waist_trainer_exp_count');
        const ctN = iv('nipple_clamps_count');
        const ctC = iv('cuffs_count');
        const ctH = iv('heels_count');
        const ctTotal = Math.min(8, ctW) + Math.min(8, ctN) + Math.min(8, ctC) + Math.min(8, ctH);
        const ctOwnsAny = COMFORT_TRAINING_TAGS.some(t => inventoryManager.ownsItem(t));
        if (ctOwnsAny || ctTotal > 0) {
            npcs.push({ name: 'Comfort Training', role: 'Apartment training', location: 'Adult Store / Apartment',
                portrait: 'assets/images/Items/waist_trainer_starter.png',
                tags: ['Training', 'Tolerance', '+10 Energy'], relCurrent: ctTotal, relMax: 32,
                statusText: ctMastered ? 'Mastered (+10 max Energy)' : `Waist ${Math.min(8, ctW)}/8 · Clamps ${Math.min(8, ctN)}/8 · Cuffs ${Math.min(8, ctC)}/8 · Heels ${Math.min(8, ctH)}/8`,
                hint: ctMastered ? '' : !ctOwnsAny ? 'Buy training gear at the Adult Store'
                    : 'Train tolerance in your apartment to make wear comfortable',
                spoiler: '12 training items at the Adult Store (4 lines x 3 tiers). 8 sessions per item to flip Comfort positive. Complete all 4 lines for a permanent +10 max Energy reward.',
                met: ctOwnsAny, color: '#7a8090', patreonOnly: false, patreonPermanent: false });
        }

        // Sarah Domme route (free, only show if signal has started)
        const sArc = iv('sarah_arc');
        const sDomSignal = iv('sarah_dom_signal');
        if (sArc === 5 || sDomSignal >= 2) {
            // sarah_dom_stage never existed as an Ink VAR — pinned this card at
            // "Stage 0/3" forever once sarah_arc == 5. Real depth var is
            // sarah_dom_intensity (1=bedroom, 2=rules, 3=cuck). Sibling of the
            // journal.js Sarah-Domme bar fix (dfc86952, 0.2.9.1.2) that was missed.
            const sDomStage = iv('sarah_dom_intensity');
            npcs.push({ name: 'Sarah Domme Route', role: 'Girlfriend escalation', location: 'Apartment',
                portrait: 'assets/images/Portraits/girlfriend_stern.png',
                tags: ['Girlfriend', 'Femdom', 'Cuck'], relCurrent: sArc === 5 ? Math.min(3, sDomStage) + 1 : 0, relMax: 4,
                statusText: sArc === 5 ? `Stage ${sDomStage}/3 (Domme)` : `Signal ${sDomSignal}/4 building`,
                hint: sArc !== 5 && sDomSignal >= 4 ? 'Accept her offer next Saturday'
                    : sArc !== 5 ? 'Keep saying yes to bedroom dynamic shifts (pegging, cage key, outfit)'
                    : sDomStage < 3 ? 'Continue the Saturday route' : '',
                spoiler: 'Branches off Acceptance after 4+ submission signals. Three-tier escalation: bedroom > week (outfit + salon) > other partners home while you stay in the chair.',
                met: sArc === 5, color: '#c060a0', patreonOnly: false, patreonPermanent: false });
        }

        return npcs;
    }

    // ==========================================================
    // BUILD PROGRESS TAB
    // ==========================================================

    _buildProgressTab(container, storylines, iv, ib) {
        // Finances card
        this._buildFinancesCard(container, iv);

        // Storylines by category
        const categories = [
            { key: 'Main Story', label: 'Main Story' },
            { key: 'Jobs & Income', label: 'Jobs & Income' },
            { key: 'Social', label: 'Social' },
            { key: 'Self Care', label: 'Self Care' },
        ];

        for (const cat of categories) {
            const catSL = storylines.filter(sl => sl.cat === cat.key)
                .sort((a, b) => (a.status === 'active' ? 0 : a.status === 'hidden' ? 1 : 2) - (b.status === 'active' ? 0 : b.status === 'hidden' ? 1 : 2));
            if (catSL.length === 0) continue;

            const anyVisible = catSL.some(sl => sl.status !== 'hidden');
            if (!anyVisible && !this._showSpoilers) continue;

            const catComplete = catSL.filter(sl => sl.status === 'complete').length;
            const catVisible = catSL.filter(sl => sl.status !== 'hidden').length;

            const catHeader = document.createElement('div');
            catHeader.className = 'completion-cat-header';
            catHeader.innerHTML = `<span class="completion-section-title">${cat.label}</span><span class="completion-cat-count">${catComplete}/${catVisible}</span>`;
            container.appendChild(catHeader);

            for (const sl of catSL) {
                if (sl.status === 'hidden' && !this._showSpoilers) continue;
                renderStorylineGroup(container, sl, {
                    showSpoilers: this._showSpoilers,
                    onClick: (clicked) => this._openStorylineDetail && this._openStorylineDetail(clicked),
                });
            }
        }
    }

    // ==========================================================
    // BUILD STORYLINE CARD
    // ==========================================================

    // Thin wrapper around the shared progress-card component for any legacy
    // callers inside this file that still hit _buildStorylineCard directly.
    _buildStorylineCard(container, sl) {
        renderProgressCard(container, sl, { showSpoilers: this._showSpoilers });
    }

    // ==========================================================
    // BUILD FINANCES CARD
    // ==========================================================

    _buildFinancesCard(container, iv) {
        const money = iv('Money'), rentAmount = iv('RentAmount'), day = iv('Day'), rentDue = iv('RentDueDay');
        const daysLeft = Math.max(0, rentDue - day);
        const urgent = daysLeft <= 3 && money < rentAmount;

        const card = document.createElement('div');
        card.className = 'completion-card completion-card-finances';

        const _fin = (k, fb) => { const v = L.get(k); return v !== k ? v : fb; };
        let html = `<div class="completion-card-title">\uD83D\uDCB0 ${_fin('completion.finances', 'Finances')}</div>`;

        // Money row
        const moneyClass = money < rentAmount ? 'completion-value-danger' : 'completion-finance-value-good';
        html += `<div class="completion-finance-row">
            <span class="completion-row-label">${_fin('completion.balance', 'Balance')}</span>
            <span class="completion-finance-value ${moneyClass}">$${money}</span>
        </div>`;

        // Rent row
        const rentClass = urgent ? 'completion-value-danger' : daysLeft <= 3 ? 'completion-value-warning' : '';
        const rentVal = L.get('completion.rent_value');
        const rentLabel = rentVal !== 'completion.rent_value' ? rentVal.replace('{0}', rentAmount).replace('{1}', daysLeft) : `$${rentAmount} in ${daysLeft} days`;
        html += `<div class="completion-finance-row">
            <span class="completion-row-label">${_fin('completion.rent_due', 'Rent Due')}</span>
            <span class="completion-finance-value ${rentClass}">${rentLabel}</span>
        </div>`;

        // Income sources
        if (iv('sugar_arrangement') >= 3) {
            html += `<div class="completion-finance-row completion-finance-row-sub"><span class="completion-row-label">${_fin('completion.sugar_daddy', 'Sugar Daddy')}</span><span class="completion-row-value">$200/week</span></div>`;
        }
        const ofEarnings = iv('onlyfems_earnings');
        if (ofEarnings > 0) {
            const pendingLabel = _fin('completion.pending', 'pending');
            html += `<div class="completion-finance-row completion-finance-row-sub"><span class="completion-row-label">${_fin('completion.onlyfems', 'OnlyFems')}</span><span class="completion-row-value">$${ofEarnings} ${pendingLabel}</span></div>`;
        }

        card.innerHTML = html;
        container.appendChild(card);
    }

    // ==========================================================
    // BUILD ACHIEVEMENTS TAB
    // ==========================================================

    _buildAchievementsTab(container) {
        const all = achievementManager.getAll();
        const unlocked = new Set(achievementManager.getUnlockedIds());

        // Header card with overall + per-category counts
        const overall = document.createElement('div');
        overall.className = 'completion-card completion-card-overall';
        const total = all.length;
        const unlockedCount = unlocked.size;
        const cats = [AchievementCategory.Story, AchievementCategory.Gameplay, AchievementCategory.Milestone];
        const catLabel = (c) => {
            const k = 'completion.cat_' + c.toLowerCase();
            const v = L.get(k);
            return v !== k ? v : c;
        };
        let header = `<div class="completion-card-title completion-overall-title">${L.get('completion.tab_achievements') !== 'completion.tab_achievements' ? L.get('completion.tab_achievements') : 'Achievements'} ${unlockedCount}/${total}</div>`;
        for (const c of cats) {
            const { unlocked: u, total: t } = achievementManager.getCategoryCount(c);
            header += this._wideProgressRowHTML(catLabel(c), u, t, '#c08040');
        }
        overall.innerHTML = header;
        container.appendChild(overall);

        // Filter pills (All / unlocked-only / per-category)
        if (this._achFilter === undefined) this._achFilter = 'all';
        const filterRow = document.createElement('div');
        filterRow.className = 'completion-filter-row';
        const filters = [
            ['all', L.get('completion.filter_all') !== 'completion.filter_all' ? L.get('completion.filter_all') : 'All'],
            ['unlocked', L.get('completion.filter_unlocked') !== 'completion.filter_unlocked' ? L.get('completion.filter_unlocked') : 'Unlocked'],
            ['locked', L.get('completion.filter_locked') !== 'completion.filter_locked' ? L.get('completion.filter_locked') : 'Locked'],
        ];
        for (const [id, label] of filters) {
            const pill = document.createElement('div');
            pill.className = 'completion-filter-pill' + (this._achFilter === id ? ' completion-filter-pill-active' : '');
            pill.textContent = label;
            pill.addEventListener('click', () => { this._achFilter = id; this._showProgressView(); });
            filterRow.appendChild(pill);
        }
        container.appendChild(filterRow);

        // Group by category
        for (const cat of cats) {
            const items = all.filter(a => a.category === cat);
            const visible = items.filter(a => {
                const isU = unlocked.has(a.id);
                if (this._achFilter === 'unlocked') return isU;
                if (this._achFilter === 'locked') return !isU;
                return true;
            });
            if (visible.length === 0) continue;

            const section = document.createElement('div');
            section.className = 'completion-ach-section';
            const heading = document.createElement('div');
            heading.className = 'completion-ach-heading';
            const cct = achievementManager.getCategoryCount(cat);
            heading.textContent = `${catLabel(cat)}, ${cct.unlocked}/${cct.total}`;
            section.appendChild(heading);

            for (const a of visible) {
                const row = document.createElement('div');
                const isU = unlocked.has(a.id);
                row.className = 'completion-ach-row' + (isU ? ' completion-ach-unlocked' : ' completion-ach-locked');

                const icon = document.createElement('div');
                icon.className = 'completion-ach-icon';
                icon.textContent = isU ? '★' : (a.hidden ? '❔' : '☆');
                row.appendChild(icon);

                const txt = document.createElement('div');
                txt.className = 'completion-ach-text';
                let title, desc;
                if (isU) {
                    title = achievementManager.getLocalizedTitle(a);
                    desc = achievementManager.getLocalizedDesc(a);
                } else if (a.hidden) {
                    title = '???';
                    desc = L.get('completion.ach_hidden_desc') !== 'completion.ach_hidden_desc'
                        ? L.get('completion.ach_hidden_desc') : 'Hidden, unlock to reveal';
                } else {
                    title = achievementManager.getLocalizedTitle(a);
                    desc = achievementManager.getLocalizedDesc(a);
                }
                txt.innerHTML = `<div class="completion-ach-title">${title}</div><div class="completion-ach-desc">${desc}</div>`;
                row.appendChild(txt);
                section.appendChild(row);
            }
            container.appendChild(section);
        }
    }

    // ==========================================================
    // BUILD PEOPLE TAB
    // ==========================================================

    _buildPeopleTab(container, npcs) {
        // If fullscreen profile is open, show it
        if (this._expandedNPC >= 0 && this._expandedNPC < npcs.length) {
            this._buildFullscreenProfile(container, npcs[this._expandedNPC]);
            return;
        }

        // Tag filter -- collapsible
        const allTags = new Set();
        for (const npc of npcs) {
            if (npc.tags) {
                for (const t of npc.tags) {
                    if (!t.startsWith('by ')) allTags.add(t);
                }
            }
        }

        let filterLabel = this._filtersExpanded ? '\u25BC Filter' : '\u25B6 Filter';
        if (this._activeTagFilter) filterLabel += `: ${this._activeTagFilter}`;
        const filterToggle = document.createElement('div');
        filterToggle.className = 'completion-filter-toggle';
        filterToggle.textContent = filterLabel;
        filterToggle.addEventListener('click', () => { this._filtersExpanded = !this._filtersExpanded; this._showProgressView(); });
        container.appendChild(filterToggle);

        if (this._filtersExpanded) {
            const filterRow = document.createElement('div');
            filterRow.className = 'completion-people-tag-row completion-filter-row';

            // "All" pill
            const allPill = document.createElement('span');
            allPill.className = 'completion-filter-tag' + (this._activeTagFilter === null ? ' completion-filter-tag-active' : '');
            allPill.textContent = L.get('phone.filter_all');
            allPill.addEventListener('click', () => { this._activeTagFilter = null; this._showProgressView(); });
            filterRow.appendChild(allPill);

            for (const tag of [...allTags].sort()) {
                const pill = document.createElement('span');
                pill.className = 'completion-filter-tag' + (this._activeTagFilter === tag ? ' completion-filter-tag-active' : '');
                pill.textContent = tag;
                pill.addEventListener('click', () => {
                    this._activeTagFilter = this._activeTagFilter === tag ? null : tag;
                    this._showProgressView();
                });
                filterRow.appendChild(pill);
            }
            container.appendChild(filterRow);
        }

        // Apply tag filter
        let indexed = npcs.map((npc, idx) => ({ npc, idx }));
        if (this._activeTagFilter) {
            indexed = indexed.filter(x => x.npc.tags && x.npc.tags.includes(this._activeTagFilter));
        }

        // Split into 3 sections: Patreon Exclusive (permanent), New This Patch (temporary patreon), Free
        const patreonPermanent = indexed.filter(x => x.npc.patreonOnly && x.npc.patreonPermanent)
            .sort((a, b) => (a.npc.met === b.npc.met) ? a.npc.name.localeCompare(b.npc.name) : (a.npc.met ? -1 : 1));
        const patreonNew = indexed.filter(x => x.npc.patreonOnly && !x.npc.patreonPermanent)
            .sort((a, b) => (a.npc.met === b.npc.met) ? a.npc.name.localeCompare(b.npc.name) : (a.npc.met ? -1 : 1));
        const freeNPCs = indexed.filter(x => !x.npc.patreonOnly)
            .sort((a, b) => (a.npc.met === b.npc.met) ? a.npc.name.localeCompare(b.npc.name) : (a.npc.met ? -1 : 1));

        // Patreon Exclusive section
        if (patreonPermanent.length > 0) {
            const metCount = patreonPermanent.filter(x => x.npc.met).length;
            const title = document.createElement('div');
            title.className = 'completion-section-title completion-section-title-patreon';
            title.textContent = `${L.get('completion.patreon')} (${metCount}/${patreonPermanent.length})`;
            container.appendChild(title);

            const grid = document.createElement('div');
            grid.className = 'completion-people-grid';
            for (const { npc, idx } of patreonPermanent) grid.appendChild(this._buildNPCCard(npc, idx));
            container.appendChild(grid);
        }

        // New This Patch section
        if (patreonNew.length > 0) {
            const metCount = patreonNew.filter(x => x.npc.met).length;
            const title = document.createElement('div');
            title.className = 'completion-section-title completion-section-title-new';
            title.textContent = `${L.get('completion.new_patch') || 'New This Patch'} (${metCount}/${patreonNew.length})`;
            container.appendChild(title);

            const grid = document.createElement('div');
            grid.className = 'completion-people-grid';
            for (const { npc, idx } of patreonNew) grid.appendChild(this._buildNPCCard(npc, idx));
            container.appendChild(grid);
        }

        // Free section
        if (freeNPCs.length > 0) {
            const metCount = freeNPCs.filter(x => x.npc.met).length;
            const title = document.createElement('div');
            title.className = 'completion-section-title';
            title.textContent = `${L.get('completion.free') || 'Free'} (${metCount}/${freeNPCs.length})`;
            container.appendChild(title);

            const grid = document.createElement('div');
            grid.className = 'completion-people-grid';
            for (const { npc, idx } of freeNPCs) grid.appendChild(this._buildNPCCard(npc, idx));
            container.appendChild(grid);
        }
    }

    // ==========================================================
    // NPC GRID CELL (portrait image + name overlay + tags)
    // ==========================================================

    _buildNPCCard(npc, npcIndex) {
        const cell = document.createElement('div');
        cell.className = 'completion-people-cell';

        const done = npc.relCurrent >= npc.relMax && npc.relMax > 0;

        // Portrait container
        const portrait = document.createElement('div');
        portrait.className = 'completion-people-portrait' + (!npc.met ? ' completion-people-portrait-locked' : '');

        // Portrait image
        if (npc.portrait) {
            const img = document.createElement('img');
            img.src = npc.portrait;
            img.className = 'completion-people-portrait-img';
            img.loading = 'lazy';
            img.alt = npc.met ? npc.name : L.get('phone.unknown');
            // Fallback to colored letter avatar
            img.onerror = () => {
                img.remove();
                const fallback = document.createElement('div');
                fallback.className = 'completion-people-portrait-fallback';
                fallback.style.background = npc.color || '#6a4a80';
                fallback.textContent = npc.name.charAt(0).toUpperCase();
                portrait.insertBefore(fallback, portrait.firstChild);
            };
            portrait.appendChild(img);
        }

        // Patreon badge (top-right)
        if (npc.patreonOnly) {
            const badge = document.createElement('span');
            badge.className = 'completion-patreon-badge';
            badge.textContent = L.get('phone.patreon');
            portrait.appendChild(badge);
        }

        // Warning badge (top-left)
        if (npc.warning) {
            const warnBadge = document.createElement('span');
            warnBadge.className = 'completion-people-warn-badge';
            warnBadge.textContent = '\u26A0';
            portrait.appendChild(warnBadge);
        }

        // Name + tags overlay at bottom
        const overlay = document.createElement('div');
        overlay.className = 'completion-people-overlay';

        const nameLabel = document.createElement('div');
        nameLabel.className = 'completion-people-name';
        nameLabel.textContent = npc.met ? npc.name : L.get('phone.unknown');
        overlay.appendChild(nameLabel);

        if (npc.tags && npc.tags.length > 0) {
            const tagRow = document.createElement('div');
            tagRow.className = 'completion-people-tag-row';
            for (const t of npc.tags) {
                if (t.startsWith('by ')) continue; // skip credit tags in grid
                const tag = document.createElement('span');
                tag.className = 'completion-people-tag';
                tag.textContent = t;
                tagRow.appendChild(tag);
            }
            overlay.appendChild(tagRow);
        }

        portrait.appendChild(overlay);
        cell.appendChild(portrait);

        // Mini progress bar below portrait
        if (npc.met && npc.relMax > 0) {
            const pct = Math.min(100, Math.max(0, (npc.relCurrent / npc.relMax) * 100));
            const barColor = done ? '#5cd89c' : (npc.color || '#6a4a80');
            const miniTrack = document.createElement('div');
            miniTrack.className = 'completion-people-mini-bar';
            miniTrack.innerHTML = `<div class="completion-people-mini-fill" style="width:${pct}%;background:${barColor};"></div>`;
            cell.appendChild(miniTrack);
        }

        // Click to expand to fullscreen profile
        cell.addEventListener('click', (evt) => {
            evt.stopPropagation();
            this._expandedNPC = this._expandedNPC === npcIndex ? -1 : npcIndex;
            this._showProgressView();
        });

        return cell;
    }

    // ==========================================================
    // FULLSCREEN NPC PROFILE
    // ==========================================================

    _buildFullscreenProfile(container, npc) {
        const done = npc.relCurrent >= npc.relMax && npc.relMax > 0;

        // Hero image
        const hero = document.createElement('div');
        hero.className = 'completion-profile-hero' + (!npc.met ? ' completion-people-portrait-locked' : '');

        if (npc.portrait) {
            const img = document.createElement('img');
            img.src = npc.portrait;
            img.className = 'completion-profile-hero-img';
            img.alt = npc.met ? npc.name : L.get('phone.unknown');
            img.onerror = () => { img.style.display = 'none'; };
            hero.appendChild(img);
        }

        // Gradient overlay with name + role
        const gradient = document.createElement('div');
        gradient.className = 'completion-profile-gradient';
        gradient.innerHTML = `
            <div class="completion-profile-name">${npc.met ? npc.name : L.get('phone.unknown')}</div>
            <div class="completion-profile-role">${npc.role} \u2014 ${npc.location}</div>
        `;
        hero.appendChild(gradient);

        // Close button
        const closeBtn = document.createElement('div');
        closeBtn.className = 'completion-profile-close';
        closeBtn.textContent = '\u2715';
        closeBtn.addEventListener('click', (evt) => {
            evt.stopPropagation();
            this._expandedNPC = -1;
            this._showProgressView();
        });
        hero.appendChild(closeBtn);

        container.appendChild(hero);

        // Body content
        const body = document.createElement('div');
        body.className = 'completion-profile-body';

        // Build all sections into ONE string, assign innerHTML once.
        // Previously this was 6\u00D7 `body.innerHTML += ...` \u2014 each one parsed
        // existing DOM, concatenated, garbage-collected old nodes, re-parsed.
        // Single assignment is one parse, no GC churn on profile re-render.
        let html = '';

        if (npc.tags && npc.tags.length > 0) {
            html += '<div class="completion-profile-tag-row">';
            for (const t of npc.tags) {
                html += `<span class="completion-profile-tag">${t}</span>`;
            }
            if (npc.patreonOnly) {
                html += `<span class="completion-profile-tag completion-profile-tag-patreon">Patreon</span>`;
            }
            html += '</div>';
        }

        if (npc.met && npc.relMax > 0) {
            const pct = Math.min(100, Math.max(0, (npc.relCurrent / npc.relMax) * 100));
            const barColor = done ? '#5cd89c' : 'rgba(150, 80, 180, 0.8)';
            html += `
                <div class="completion-profile-section">
                    <div class="completion-profile-section-title">${L.get('completion.relationship')}</div>
                    <div class="completion-row">
                        <span class="completion-row-label">${npc.statusText}</span>
                        <span class="completion-row-value">Stage ${npc.relCurrent}/${npc.relMax}</span>
                    </div>
                    <div class="completion-progress-track">
                        <div class="completion-progress-fill" style="width:${pct}%;background:${barColor};"></div>
                    </div>
                </div>`;
        } else if (npc.met) {
            html += `<div class="completion-profile-status">${npc.statusText}</div>`;
        }

        if (npc.hint) {
            html += `<div class="completion-profile-hint">${npc.hint}</div>`;
        }

        if (npc.warning) {
            html += `<div class="completion-profile-warning">\u26A0 ${npc.warning}</div>`;
        }

        if (this._showSpoilers && npc.spoiler) {
            html += `
                <div class="completion-profile-section">
                    <div class="completion-profile-section-title">${L.get('completion.details')}</div>
                    <div class="completion-spoiler-text">${npc.spoiler}</div>
                </div>`;
        }

        body.innerHTML = html;
        container.appendChild(body);

        // Linked storyline, render the multi-path cards inline so the NPC
        // profile shows their arc directly without bouncing to the Storylines
        // tab. Uses the same progress-card component everywhere.
        if (npc.linkedStoryline) {
            const slEntry = getStorylineData(this._story)[npc.linkedStoryline];
            if (slEntry && slEntry.paths) {
                const wrap = document.createElement('div');
                wrap.className = 'completion-profile-section';
                const titleHtml = `<div class="completion-profile-section-title">${L.get('completion.path') !== 'completion.path' ? L.get('completion.path') : 'Path'}</div>`;
                wrap.innerHTML = titleHtml;
                const activePath = slEntry.paths.find(p => p.activeWhen());
                for (const p of slEntry.paths) {
                    const isActive = activePath ? (p === activePath) : false;
                    const isPending = !activePath && slEntry.decisionUnlocked && slEntry.decisionUnlocked();
                    if (!activePath && !isPending) continue; // pre-unlock: no path cards
                    const v = isActive ? p.val() : 0;
                    renderProgressCard(wrap, {
                        name: p.name, icon: p.icon,
                        val: v, max: p.max,
                        desc: p.desc, hint: isActive ? p.hint : '',
                        status: isActive ? (v >= p.max ? 'complete' : 'active') : (isPending ? 'pending' : 'locked'),
                        isPathCard: true,
                    });
                }
                container.appendChild(wrap);
            }
        }
    }

    // ==========================================================
    // SHARED HTML BUILDERS
    // ==========================================================

    _wideProgressRowHTML(label, value, max, color) {
        const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
        return `<div class="completion-wide-row">
            <span class="completion-wide-label">${label}</span>
            <span class="completion-wide-pct">${pct}%</span>
        </div>
        <div class="completion-wide-track">
            <div class="completion-wide-fill" style="width:${pct}%;background:${color};"></div>
        </div>`;
    }

    _showAppStoreView() {
        this._view = 'appstore';
        const backBtn = this._root.querySelector('#phone-back');
        const title = this._root.querySelector('.phone-title');
        if (backBtn) backBtn.classList.remove('hidden');
        if (title) title.textContent = L.get('phone.app_store');

        const body = this._root.querySelector('#phone-body');
        body.innerHTML = '';

        const container = document.createElement('div');
        container.className = 'appstore-list';

        const isPat_ = isPat();
        const availableApps = PHONE_APPS.filter(a => !a.alwaysVisible && a.inkVar);
        const installedIds = new Set();
        for (const app of availableApps) {
            const fromStorage = localStorage.getItem('PnO_App_' + app.id) === 'true';
            const fromInk = this._story ? (this._story.variablesState[app.inkVar] === true || this._story.variablesState[app.inkVar] === 1) : false;
            if (fromStorage || fromInk) installedIds.add(app.id);
        }

        // "Coming soon" is either a static comingSoon flag OR an unflipped
        // kill-switch gateVar (e.g. v029_content_enabled): a gated app reads as
        // a teaser until the feature ships, and openApp refuses to open it.
        const isSoon = (a) => {
            if (a.comingSoon) return true;
            if (a.gateVar) {
                const gv = this._story ? this._story.variablesState[a.gateVar] : false;
                if (!(gv === true || gv === 1)) return true;
            }
            return false;
        };
        // Coming-soon apps render at the end of the list with a visual divider
        // so they don't train players to ignore cards mid-list.
        const liveApps = availableApps.filter(a => !isSoon(a));
        const comingSoonApps = availableApps.filter(a => isSoon(a));
        const orderedApps = [...liveApps, ...comingSoonApps];

        if (availableApps.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'appstore-empty';
            empty.textContent = L.get('phone.no_apps');
            container.appendChild(empty);
        } else {
            for (let i = 0; i < orderedApps.length; i++) {
                const app = orderedApps[i];
                // Insert divider just before the first coming-soon row
                if (isSoon(app) && i === liveApps.length && liveApps.length > 0) {
                    const divider = document.createElement('div');
                    divider.className = 'appstore-divider';
                    const label = document.createElement('span');
                    label.className = 'appstore-divider-label';
                    label.textContent = L.get('phone.coming_soon_section') || 'Coming Soon';
                    divider.appendChild(label);
                    container.appendChild(divider);
                }
                const row = document.createElement('div');
                row.className = 'appstore-row';

                let icon;
                if (app.iconImg) {
                    icon = document.createElement('img');
                    // N3 fix (2026-05-26): use tar blob URL when available so
                    // the initial src= doesn't trigger a 404 fetch before the
                    // MutationObserver interceptor kicks in.
                    const blobUrl = window._pno_resolveImage?.(app.iconImg);
                    icon.src = blobUrl || `assets/images/UI/${app.iconImg}.png`;
                    icon.alt = app.name;
                    icon.className = 'appstore-icon-img';
                } else {
                    icon = document.createElement('span');
                    icon.className = 'appstore-icon-emoji';
                    icon.textContent = app.icon;
                }

                const info = document.createElement('div');
                info.className = 'appstore-info';
                // Coming-soon (incl. kill-switch-gated) apps show a generic teaser line,
                // never their real storeDesc — keeps dormant 0.2.9 blurbs (which can go
                // stale) from spoiling/misdescribing unreleased content. Real desc returns
                // automatically once the app goes live (isSoon -> false).
                const descText = isSoon(app)
                    ? L.get('phone.coming_soon_desc', 'In development. Coming in a future update.')
                    : (app.storeDesc ? L.get(app.storeDesc) : L.get('phone.free_install'));
                const reqText = app.reqFem ? `<div class="appstore-req">Fem \u2265${app.reqFem}</div>` : '';
                info.innerHTML = `<div class="appstore-name">${app.name}</div>
                    <div class="appstore-desc">${descText}</div>${reqText}`;

                const isInstalled = installedIds.has(app.id);
                const isPatLocked = app.patreonOnly && !isPat_;
                // Check fem requirement
                const fem = this._story ? (this._story.variablesState['Femininity'] || 0) : 0;
                const meetsReq = !app.reqFem || fem >= app.reqFem;
                const installBtn = document.createElement('button');
                installBtn.className = 'appstore-install-btn';
                if (isInstalled) {
                    installBtn.textContent = '\u2713';
                    installBtn.disabled = true;
                    installBtn.classList.add('is-installed');
                } else if (isSoon(app)) {
                    installBtn.textContent = L.get('phone.coming_soon') || 'Soon';
                    installBtn.disabled = true;
                    installBtn.classList.add('is-coming-soon');
                    row.classList.add('is-coming-soon');
                } else if (isPatLocked) {
                    installBtn.textContent = '\uD83D\uDD12 Patreon';
                    installBtn.disabled = true;
                    installBtn.classList.add('is-patreon-locked');
                    row.classList.add('is-patreon-locked');
                } else if (!meetsReq) {
                    installBtn.textContent = '\uD83D\uDD12';
                    installBtn.disabled = true;
                    installBtn.classList.add('is-req-locked');
                } else {
                    installBtn.textContent = L.get('phone.get');
                }
                installBtn.addEventListener('click', () => {
                    if (!meetsReq || isPatLocked || isSoon(app)) return;
                    // Persist install flag to BOTH Ink (so it lives in inkState
                    // + the inkVarSnapshot fallback) and localStorage. Prior to
                    // v0.2.5 only localStorage was written for most apps, so a
                    // recovery load or a different-browser import dropped the
                    // home-screen entries to empty (foundby:Jürgen v0.2.4.2).
                    if (this._story && app.inkVar) {
                        syncVar(this._story, app.inkVar, true);
                    } else {
                        events.emit(Events.VARIABLE_CHANGED, app.inkVar, true);
                    }
                    try { localStorage.setItem('PnO_App_' + app.id, 'true'); } catch(e) {}
                    // Custom install hooks
                    if (app.onInstall === 'dareme' && this._story) {
                        const curShame = this._story.variablesState['Shame'] || 0;
                        syncVar(this._story, 'Shame', curShame + 2);
                    }
                    installBtn.textContent = '\u2713';
                    installBtn.disabled = true;
                    installBtn.classList.add('is-installed');
                });

                row.appendChild(icon);
                row.appendChild(info);
                row.appendChild(installBtn);
                container.appendChild(row);
            }
        }

        body.appendChild(container);
    }

    // ── Cheat Console (Patreon Phone App) ────────────────────────────────

    _showCheatsView() {
        this._view = 'cheats';
        const backBtn = this._root.querySelector('#phone-back');
        const title = this._root.querySelector('.phone-title');
        if (backBtn) backBtn.classList.remove('hidden');
        if (title) title.textContent = L.get('phone.cheats');

        const body = this._root.querySelector('#phone-body');
        body.innerHTML = '';

        const scroll = document.createElement('div');
        scroll.style.cssText = 'padding: 12px; display: flex; flex-direction: column; gap: 10px; overflow-y: auto; max-height: 100%;';

        const getVar = (name) => {
            try { return this._variableManager?.getValue?.(name) ?? this._story?.variablesState?.[name] ?? 0; } catch(e) { return 0; }
        };
        const setVar = (name, val) => {
            // F14: write only to Ink. The observer bridge mirrors observed
            // VARs into baseVar/flags automatically and fires STAT_CHANGED /
            // FLAG_CHANGED. Pre-fix code called a non-existent setVariable
            // method and fell back to `baseVar[name] = val`, but baseVar is
            // a Map, so that was a silent no-op. Manual STAT_CHANGED emission
            // would also have polluted bookkeeping for non-observed VARs.
            try {
                if (this._story) this._story.variablesState[name] = val;
            } catch(e) { console.warn('[Cheats] setVar error:', name, e); }
        };
        // Buffed stats (Fem/Sub) MUST read the Ink total: getVar() returns the
        // buff-less base while setVar writes the total, so a +10 built on the
        // base value actually applies (10 - buff), net NEGATIVE once equipment
        // buffs exceed 10 (full femme wardrobe). Same total-vs-base mixup made
        // a hard 100-clamp yank legit >100 totals (base cap + buffs, or the
        // fully_transformed 200 cap) back down.
        const getTotal = (name) => {
            try {
                const v = this._story?.variablesState?.[name];
                if (typeof v === 'number') return v;
            } catch(e) { /* fall through */ }
            return getVar(name);
        };
        const getBuff = (name) => {
            try { return this._variableManager?.buffedVar?.get?.(name) || 0; } catch(e) { return 0; }
        };
        const baseCap = (name) => {
            // Mirrors variables.js _capFor(): Fem lifts to 200 post-capstone.
            try {
                if (name === 'Femininity' && this._story?.variablesState?.['fully_transformed']) return 200;
            } catch(e) { /* default */ }
            return 100;
        };

        const makeSection = (label) => {
            const sec = document.createElement('div');
            sec.style.cssText = 'background: rgba(0,0,0,0.15); border: 1px solid rgba(140,100,200,0.2); border-radius: 8px; padding: 10px;';
            const h = document.createElement('div');
            h.style.cssText = 'font-size: 11px; font-weight: 700; color: var(--orchid); margin-bottom: 6px;';
            h.textContent = label;
            sec.appendChild(h);
            return sec;
        };
        const makeRow = () => {
            const r = document.createElement('div');
            r.style.cssText = 'display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 4px;';
            return r;
        };
        const makeBtn = (text, onClick) => {
            const b = document.createElement('button');
            b.style.cssText = 'font-size: 11px; padding: 4px 10px; border-radius: 6px; border: 1px solid rgba(100,80,140,0.3); background: rgba(255,255,255,0.08); color: var(--text-soft); cursor: pointer; font-weight: 600;';
            b.textContent = text;
            b.addEventListener('click', () => { onClick(); this._showCheatsView(); });
            return b;
        };
        const makeLabel = (text) => {
            const l = document.createElement('div');
            l.style.cssText = 'font-size: 10px; font-weight: 600; color: var(--text-dim); margin-top: 4px;';
            l.textContent = text;
            return l;
        };

        // Money
        const mSec = makeSection('Money ($' + getVar('Money') + ')');
        const mRow = makeRow();
        mRow.appendChild(makeBtn('+100', () => setVar('Money', getVar('Money') + 100)));
        mRow.appendChild(makeBtn('+1000', () => setVar('Money', getVar('Money') + 1000)));
        mRow.appendChild(makeBtn('Max', () => setVar('Money', 9999)));
        mSec.appendChild(mRow);
        scroll.appendChild(mSec);

        // Energy
        const eSec = makeSection('Energy (' + getVar('Energy') + ')');
        const eRow = makeRow();
        eRow.appendChild(makeBtn('Fill', () => setVar('Energy', getVar('max_energy') || 100)));
        eRow.appendChild(makeBtn('+25', () => setVar('Energy', Math.min(getVar('Energy') + 25, getVar('max_energy') || 100))));
        eSec.appendChild(eRow);
        // Unlimited Energy toggle
        const unlimitedOn = !!window._cheatUnlimitedEnergy;
        const toggleRow = makeRow();
        const toggleBtn = document.createElement('button');
        toggleBtn.style.cssText = 'font-size: 11px; padding: 4px 10px; border-radius: 6px; border: 1px solid ' + (unlimitedOn ? 'rgba(80,200,120,0.5)' : 'rgba(100,80,140,0.3)') + '; background: ' + (unlimitedOn ? 'rgba(80,200,120,0.15)' : 'rgba(255,255,255,0.08)') + '; color: ' + (unlimitedOn ? '#5cd89c' : 'var(--text-soft)') + '; cursor: pointer; font-weight: 600;';
        toggleBtn.textContent = unlimitedOn ? '\u2705 Unlimited Energy' : '\u26A1 Unlimited Energy';
        toggleBtn.addEventListener('click', () => {
            window._cheatUnlimitedEnergy = !window._cheatUnlimitedEnergy;
            // ENG-07: clear any prior interval before starting a new one,
            // and also clear when toggling OFF (was a leak, old interval
            // kept polling forever, only short-circuiting via the boolean).
            if (window._cheatEnergyTimer) {
                clearInterval(window._cheatEnergyTimer);
                window._cheatEnergyTimer = null;
            }
            if (window._cheatUnlimitedEnergy) {
                window._cheatEnergyTimer = setInterval(() => {
                    if (!window._cheatUnlimitedEnergy) return;
                    const s = this._story;
                    if (!s) return;
                    const max = s.variablesState['max_energy'] || 100;
                    const cur = s.variablesState['Energy'];
                    if (typeof cur === 'number' && cur < max) {
                        s.variablesState['Energy'] = max;
                        if (this._variableManager?.baseVar) {
                            this._variableManager.baseVar.set('Energy', max);
                            events.emit(Events.STAT_CHANGED, 'Energy', max);
                        }
                    }
                }, 200);
            }
            this._showCheatsView();
        });
        toggleRow.appendChild(toggleBtn);
        eSec.appendChild(toggleRow);
        scroll.appendChild(eSec);

        // Stats
        const sSec = makeSection('Stats');
        for (const [label, varName] of [['Femininity', 'Femininity'], ['Submissiveness', 'Submissiveness'], ['Arousal', 'Arousal']]) {
            sSec.appendChild(makeLabel(label + ' (' + getTotal(varName) + ')'));
            const sRow = makeRow();
            if (varName === 'Arousal') {
                // Arousal gets both directions so the Lovense tester can
                // ramp up AND cool down without fighting the `0` reset
                sRow.appendChild(makeBtn('0', () => setVar(varName, 0)));
                sRow.appendChild(makeBtn('-25', () => setVar(varName, Math.max(getTotal(varName) - 25, 0))));
                sRow.appendChild(makeBtn('-10', () => setVar(varName, Math.max(getTotal(varName) - 10, 0))));
                sRow.appendChild(makeBtn('+10', () => setVar(varName, Math.min(getTotal(varName) + 10, 100))));
                sRow.appendChild(makeBtn('+25', () => setVar(varName, Math.min(getTotal(varName) + 25, 100))));
                sRow.appendChild(makeBtn('Max', () => setVar(varName, 100)));
            } else {
                // No 100-clamp on the + buttons: buffed totals sit legitimately
                // above the base cap. The observer bridge caps the derived base
                // (variables.js _onInkVariableChanged) and writes the corrected
                // total back, so overshoot settles at cap + buff on its own.
                sRow.appendChild(makeBtn('-10', () => setVar(varName, Math.max(getTotal(varName) - 10, getBuff(varName)))));
                sRow.appendChild(makeBtn('+10', () => setVar(varName, getTotal(varName) + 10)));
                sRow.appendChild(makeBtn('+25', () => setVar(varName, getTotal(varName) + 25)));
                sRow.appendChild(makeBtn('Max', () => setVar(varName, baseCap(varName) + getBuff(varName))));
            }
            sSec.appendChild(sRow);
        }
        // Lock Arousal toggle
        const lockArousalOn = !!window._cheatLockArousal;
        const lockArousalRow = makeRow();
        const lockArousalBtn = document.createElement('button');
        lockArousalBtn.style.cssText = 'font-size: 11px; padding: 4px 10px; border-radius: 6px; border: 1px solid ' + (lockArousalOn ? 'rgba(80,200,120,0.5)' : 'rgba(100,80,140,0.3)') + '; background: ' + (lockArousalOn ? 'rgba(80,200,120,0.15)' : 'rgba(255,255,255,0.08)') + '; color: ' + (lockArousalOn ? '#5cd89c' : 'var(--text-soft)') + '; cursor: pointer; font-weight: 600;';
        lockArousalBtn.textContent = lockArousalOn ? '\u2705 Lock Arousal' : '\uD83D\uDD12 Lock Arousal';
        lockArousalBtn.addEventListener('click', () => {
            window._cheatLockArousal = !window._cheatLockArousal;
            // ENG-07: same leak as energy timer, clear before re-create + on toggle-off.
            if (window._cheatArousalTimer) {
                clearInterval(window._cheatArousalTimer);
                window._cheatArousalTimer = null;
            }
            if (window._cheatLockArousal) {
                window._cheatLockedArousalValue = getVar('Arousal');
                window._cheatArousalTimer = setInterval(() => {
                    if (!window._cheatLockArousal) return;
                    const s = this._story;
                    if (!s) return;
                    const cur = s.variablesState['Arousal'];
                    const target = window._cheatLockedArousalValue;
                    if (typeof cur === 'number' && cur !== target) {
                        s.variablesState['Arousal'] = target;
                        if (this._variableManager?.baseVar) {
                            this._variableManager.baseVar.set('Arousal', target);
                            events.emit(Events.STAT_CHANGED, 'Arousal', target);
                        }
                    }
                }, 200);
            }
            this._showCheatsView();
        });
        lockArousalRow.appendChild(lockArousalBtn);
        sSec.appendChild(lockArousalRow);
        scroll.appendChild(sSec);

        // Time
        const tSec = makeSection('Time (Day ' + getVar('Day') + ', Slot ' + getVar('TimeSlot') + ')');
        tSec.appendChild(makeLabel('Day'));
        const dRow = makeRow();
        dRow.appendChild(makeBtn('+1', () => setVar('Day', getVar('Day') + 1)));
        dRow.appendChild(makeBtn('+7', () => setVar('Day', getVar('Day') + 7)));
        tSec.appendChild(dRow);
        tSec.appendChild(makeLabel('TimeSlot'));
        const tsRow = makeRow();
        tsRow.appendChild(makeBtn('Morn', () => setVar('TimeSlot', 0)));
        tsRow.appendChild(makeBtn('Lunch', () => setVar('TimeSlot', 1)));
        tsRow.appendChild(makeBtn('Eve', () => setVar('TimeSlot', 2)));
        tsRow.appendChild(makeBtn('Night', () => setVar('TimeSlot', 3)));
        tSec.appendChild(tsRow);
        scroll.appendChild(tSec);

        // Locations (teleport)
        const lSec = makeSection('Teleport');
        const lRow = makeRow();
        for (const loc of ['Apartment', 'BarEntry', 'MallEntry', 'ParkEntry', 'ClubEntry', 'SalonEntry', 'BeachEntry', 'Office_Entry', 'GymEntry', 'ClinicEntry', 'SpaEntry', 'Maid_Entry']) {
            const label = loc.replace('Entry', '');
            lRow.appendChild(makeBtn(label, () => {
                this.close();
                events.emit('LOCATION_CHANGE', loc);
            }));
        }
        lSec.appendChild(lRow);
        scroll.appendChild(lSec);

        // GrindrR Dates (start any NPC date directly)
        const gSec = makeSection('GrindrR Dates');
        const npcs = this._getGrindrNPCS ? this._getGrindrNPCS() : [];
        for (const npc of npcs) {
            gSec.appendChild(makeLabel(npc.name));
            const nRow = makeRow();
            for (const stage of npc.stages) {
                nRow.appendChild(makeBtn(`S${stage.stage}`, () => {
                    // Ensure NPC is matched
                    if (this._story) {
                        syncVar(this._story, npc.matchedVar, true);
                    }
                    this.close();
                    // Debug GrindrR stage jump — voluntary, bypass SMS throttle.
                    events.emit('phone:forcedEvent', stage.knot, 'PhoneApp');
                }));
            }
            gSec.appendChild(nRow);
        }
        scroll.appendChild(gSec);

        body.appendChild(scroll);
    }

    /** Open the cheats view directly (called from pause menu shortcut). */
    openCheats() {
        this.open();
        this._showCheatsView();
    }

    /** Set variable manager reference for cheat console. */
    setVariableManager(vm) {
        this._variableManager = vm;
    }

    // ── Home Screen ─────────────────────────────────────────────────────

    _renderHomeScreen() {
        // Recover stale story reference
        if (!this._story && this._storyGetter) this._story = this._storyGetter();

        const body = this._root.querySelector('#phone-body');
        body.innerHTML = '';

        const grid = document.createElement('div');
        grid.className = 'phone-app-grid';

        const isPat_ = isPat();
        for (const app of PHONE_APPS) {
            const isLocked = app.patreonOnly && !isPat_;
            if (!isLocked && !app.alwaysVisible && app.inkVar) {
                // Check if app was installed (localStorage for App Store installs, Ink var for story installs)
                const fromStorage = localStorage.getItem('PnO_App_' + app.id) === 'true';
                const fromInk = this._story ? (this._story.variablesState[app.inkVar] === true || this._story.variablesState[app.inkVar] === 1) : false;
                if (!fromStorage && !fromInk) continue;
            }

            const cell = document.createElement('div');
            cell.className = 'phone-app-cell';
            if (isLocked) cell.style.opacity = '0.5';
            const displayName = app.l10n ? L.get(app.l10n) : app.name;
            // N3 fix (2026-05-26): blob URL when in tar mode → no 404 boot spam.
            const iconSrc = app.iconImg
                ? (window._pno_resolveImage?.(app.iconImg) || `assets/images/UI/${app.iconImg}.png`)
                : null;
            const iconContent = app.iconImg
                ? `<img src="${iconSrc}" alt="${displayName}" style="width:64px;height:64px;border-radius:10px">`
                : app.icon;
            cell.innerHTML = `
                <div class="phone-app-icon">${iconContent}${isLocked ? '<span style="position:absolute;top:-2px;right:-2px;font-size:12px">\uD83D\uDD12</span>' : ''}</div>
                <div class="phone-app-name">${displayName}</div>
            `;

            if (isLocked) {
                cell.addEventListener('click', () => {
                    const toast = document.createElement('div');
                    toast.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:rgba(20,15,35,0.95);border:1px solid rgba(255,107,157,0.4);color:#ff6b9d;padding:16px 24px;border-radius:10px;font-size:13px;z-index:99999;text-align:center;max-width:280px;';
                    toast.innerHTML = '<div style="font-weight:700;margin-bottom:6px">\uD83D\uDD12 Patreon Only</div><div style="color:var(--text-soft);font-size:11px">This feature is available for Patreon supporters. Visit patreon.com/PinkedAndOwned</div>';
                    document.body.appendChild(toast);
                    setTimeout(() => toast.remove(), 3000);
                });
            } else if (app.id === 'messages') {
                const badge = document.createElement('span');
                badge.className = 'phone-app-badge' + (this._unread.size > 0 ? '' : ' hidden');
                badge.id = 'phone-app-messages-badge';
                badge.textContent = this._unread.size;
                cell.querySelector('.phone-app-icon').appendChild(badge);
                cell.addEventListener('click', () => this._showView('contacts'));
            } else if (app.id === 'completion') {
                cell.addEventListener('click', () => this._showProgressView());
            } else if (app.id === 'appstore') {
                cell.addEventListener('click', () => this._showAppStoreView());
            } else if (app.id === 'cheats') {
                cell.addEventListener('click', () => this._showCheatsView());
            } else if (app.id === 'gallery') {
                cell.addEventListener('click', () => {
                    // Phone Gallery app delegates to the standalone gallery
                    // overlay (gallery.js), videos, tabs, variant toggles.
                    // Close phone first so only one overlay stacks.
                    this.close();
                    setTimeout(() => galleryUI.open(), 50);
                });
            } else {
                cell.addEventListener('click', () => {
                    events.emit('phone:appOpen', app.id);
                });
            }

            grid.appendChild(cell);
        }

        body.appendChild(grid);
    }

    // ── Contact List ────────────────────────────────────────────────────

    _renderContactList() {
        const body = this._root.querySelector('#phone-body');
        body.innerHTML = '';

        const list = document.createElement('div');
        list.className = 'sms-contact-list';

        // Sort contacts: unread first, then by most recent message
        const contacts = [...this._threads.keys()].sort((a, b) => {
            const aUnread = this._unread.has(a) ? 1 : 0;
            const bUnread = this._unread.has(b) ? 1 : 0;
            if (aUnread !== bUnread) return bUnread - aUnread;

            const aThread = this._threads.get(a);
            const bThread = this._threads.get(b);
            const aTime = aThread.length > 0 ? aThread[aThread.length - 1].timestamp : 0;
            const bTime = bThread.length > 0 ? bThread[bThread.length - 1].timestamp : 0;
            return bTime - aTime;
        });

        if (contacts.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'sms-empty';
            empty.textContent = L.get('phone.no_messages');
            list.appendChild(empty);
        }

        for (const npcId of contacts) {
            const thread = this._threads.get(npcId);
            const lastMsg = thread[thread.length - 1];
            const isUnread = this._unread.has(npcId);
            const name = this._resolveNPCName(npcId);
            const preview = lastMsg
                ? (lastMsg.image ? L.get('phone.image_label') : (lastMsg.text || ''))
                : '';
            const truncatedPreview = preview.length > 45
                ? preview.substring(0, 42) + '...'
                : preview;

            const row = document.createElement('div');
            row.className = 'sms-contact-row' + (isUnread ? ' sms-unread' : '');
            row.innerHTML = `
                ${this._buildAvatarHtml(npcId, 'sms-contact-avatar')}
                <div class="sms-contact-info">
                    <div class="sms-contact-name">${escapeHtml(name)}</div>
                    <div class="sms-contact-preview">${escapeHtml(truncatedPreview)}</div>
                </div>
                ${isUnread ? '<div class="sms-unread-dot"></div>' : ''}
            `;
            row.addEventListener('click', () => this._openChat(npcId));
            list.appendChild(row);
        }

        body.appendChild(list);
    }

    _openChat(npcId) {
        this._activeChat = npcId;
        this._unread.delete(npcId);
        this._updateBadge();
        this._showView('chat');
    }

    // ── Chat View ───────────────────────────────────────────────────────

    _renderChatView(npcId) {
        const body = this._root.querySelector('#phone-body');
        body.innerHTML = '';

        // Chat header with avatar + name
        const header = document.createElement('div');
        header.className = 'sms-chat-header';
        header.innerHTML = `
            ${this._buildAvatarHtml(npcId, 'sms-chat-avatar')}
            <span class="sms-chat-name">${escapeHtml(this._resolveNPCName(npcId))}</span>
        `;
        body.appendChild(header);

        const chatContainer = document.createElement('div');
        chatContainer.className = 'sms-chat-container';

        const thread = this._getThread(npcId);
        // F45: empty-thread placeholder. Reached via NPC tile tap before
        // any SMS has been received, or if the thread got cleared.
        if (!thread || thread.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'sms-chat-empty';
            const emptyKey = L.get('phone.empty_thread');
            empty.textContent = emptyKey !== 'phone.empty_thread'
                ? emptyKey
                : 'No messages yet.';
            chatContainer.appendChild(empty);
            body.appendChild(chatContainer);
            return;
        }
        let lastDay = -1;
        let lastSlot = -1;

        for (const msg of thread) {
            // Day separator
            if (msg.day !== lastDay || msg.timeSlot !== lastSlot) {
                const sep = document.createElement('div');
                sep.className = 'sms-day-separator';
                const slotKeys = ['time.morning', 'time.afternoon', 'time.evening', 'time.night'];
                const slotFallback = ['Morning', 'Afternoon', 'Evening', 'Night'];
                const sk = slotKeys[msg.timeSlot] || slotKeys[0];
                const slotT = L.get(sk);
                const slotName = slotT !== sk ? slotT : (slotFallback[msg.timeSlot] || 'Morning');
                const dayK = L.get('desktop.day');
                const dayStr = dayK !== 'desktop.day' ? dayK.replace('{0}', msg.day) : `Day ${msg.day}`;
                sep.textContent = `${dayStr} \u2022 ${slotName}`;
                chatContainer.appendChild(sep);
                lastDay = msg.day;
                lastSlot = msg.timeSlot;
            }

            const bubble = document.createElement('div');
            const isPlayer = msg.from === 'player';
            bubble.className = `sms-bubble ${isPlayer ? 'sms-bubble-player' : this._getBubbleClass(npcId)}`;

            // smsfile: attachment-row with file icon + filename + caption.
            // Whole row is tappable, opens the resolved image in a fullscreen
            // overlay. Image source uses _pno_resolveImage so the file can
            // live in any subfolder (UI, Computer, SMS, etc.), falls back to
            // the SMS folder for back-compat with existing #mms: assets.
            if (msg.type === 'smsfile' && msg.image && msg.label) {
                // 1-arg form does a manifest lookup so the image can live in
                // any subfolder. Falls back to the SMS folder for back-compat.
                const _ri = window._pno_resolveImage;
                const resolved = _ri ? _ri(msg.image) : null;
                const src = resolved || `assets/images/SMS/${msg.image}.png`;

                const fileRow = document.createElement('button');
                fileRow.className = 'sms-bubble-file';
                fileRow.type = 'button';
                fileRow.setAttribute('aria-label', `Open attachment: ${msg.label}`);

                const icon = document.createElement('span');
                icon.className = 'sms-bubble-file-icon';
                // Different glyph for goto-attachments, they "play" content,
                // not just preview a still. Tape is more honest than paperclip.
                icon.textContent = msg.goto ? '▶' : '📎';

                const meta = document.createElement('span');
                meta.className = 'sms-bubble-file-meta';

                const name = document.createElement('span');
                name.className = 'sms-bubble-file-name';
                name.textContent = msg.label;

                const hint = document.createElement('span');
                hint.className = 'sms-bubble-file-hint';
                if (msg.goto) {
                    hint.textContent = L.get('phone.tap_to_play') || 'Tap to play';
                } else {
                    hint.textContent = L.get('phone.tap_to_view') || 'Tap to view';
                }

                meta.appendChild(name);
                meta.appendChild(hint);
                fileRow.appendChild(icon);
                fileRow.appendChild(meta);

                fileRow.addEventListener('click', () => {
                    if (msg.goto) {
                        // Forced-event navigation, close phone, queue the knot
                        // through the standard FIFO. Same idiom as #smsreply
                        // [goto:] tokens. Variables snapshot + restore handled
                        // by main.js _enqueueForcedEvent → storyManager.
                        this.close();
                        events.emit('phone:forcedEvent', msg.goto);
                    } else {
                        this._showFullscreenImage(src);
                    }
                });
                bubble.appendChild(fileRow);

                if (msg.text) {
                    const text = document.createElement('div');
                    text.className = 'sms-bubble-text sms-bubble-file-caption';
                    text.textContent = msg.text;
                    bubble.appendChild(text);
                }
            } else {
                if (msg.image) {
                    const img = document.createElement('img');
                    img.className = 'sms-bubble-image';
                    img.alt = msg.image;
                    img.loading = 'lazy';
                    img.src = `assets/images/SMS/${msg.image}.png`;
                    img.addEventListener('click', () => this._showFullscreenImage(img.src));
                    bubble.appendChild(img);
                }

                if (msg.text) {
                    const text = document.createElement('div');
                    text.className = 'sms-bubble-text';
                    text.textContent = msg.text;
                    bubble.appendChild(text);
                }

                // Text-only SMS with [goto:KnotName] token. Without this CTA the
                // forced-event link is parsed off the text in _processResponseTokens
                // but has no clickable affordance, players see the SMS but cannot
                // trigger the scene it's supposed to lead to (foundby:adyyxy
                // 2026-05-28 Day 126+, 60-day permanent chastity SMS looped every
                // morning because Permanent_Lock_Offer_seen only flips inside the
                // Offer knot the player could never reach).
                //
                // One-shot rule: the CTA only renders when goto_consumed is falsy.
                // After click we flip the flag + remove the button so the player
                // can't tap a stale message twice for stats. The knot-level guard
                // at Chastity_PermanentLock_Offer is the second safety net for
                // saves that pre-date this field.
                if (msg.goto && !isPlayer && !msg.goto_consumed) {
                    const cta = document.createElement('button');
                    cta.type = 'button';
                    cta.className = 'sms-bubble-goto';
                    cta.setAttribute('aria-label', `Open scene: ${msg.goto}`);
                    const icon = document.createElement('span');
                    icon.className = 'sms-bubble-goto-icon';
                    icon.textContent = '▶';
                    const label = document.createElement('span');
                    label.className = 'sms-bubble-goto-label';
                    label.textContent = L.get('phone.tap_to_continue') || 'Tap to continue';
                    cta.appendChild(icon);
                    cta.appendChild(label);
                    cta.addEventListener('click', () => {
                        msg.goto_consumed = true;
                        cta.disabled = true;
                        cta.remove();
                        this.close();
                        events.emit('phone:forcedEvent', msg.goto);
                    });
                    bubble.appendChild(cta);
                }
            }

            chatContainer.appendChild(bubble);
        }

        body.appendChild(chatContainer);

        // Check for pending replies (keep until player clicks, re-renders must preserve them)
        const threadData = this._threads.get(npcId);
        if (threadData && threadData._pendingReplies) {
            this._renderReplyButtons(npcId, threadData._pendingReplies);
        }

        // Scroll to bottom, phone-body is the scrollable ancestor, not chatContainer
        requestAnimationFrame(() => {
            body.scrollTop = body.scrollHeight;
        });
    }

    _renderReplyButtons(npcId, options) {
        const body = this._root.querySelector('#phone-body');

        // Remove existing reply bar if any
        const existing = body.querySelector('.sms-reply-bar');
        if (existing) existing.remove();

        const bar = document.createElement('div');
        bar.className = 'sms-reply-bar';

        for (const opt of options) {
            const btn = document.createElement('button');
            btn.className = 'sms-reply-btn';
            btn.textContent = opt.playerText;
            btn.addEventListener('click', () => {
                // Clear pending replies so re-renders don't re-show them
                const td = this._threads.get(npcId);
                if (td) delete td._pendingReplies;
                this._selectReply(npcId, opt);
                bar.remove();
                // Promote the next queued reply-set (a second #smsreply that
                // arrived in the same drain) so it becomes answerable now.
                this._promoteQueuedReplies(npcId, opt);
            });
            bar.appendChild(btn);
        }

        body.appendChild(bar);
    }

    // After a reply is answered, surface the next deferred reply-set (if any).
    // If the answered reply routes into a forced event ([goto:]), the phone
    // closes and the promoted buttons re-appear when the player reopens the
    // thread (_renderChatView re-renders _pendingReplies). If it was a plain
    // reply and the chat stays open, render the promoted set immediately.
    _promoteQueuedReplies(npcId, answeredOpt) {
        const td = this._threads.get(npcId);
        if (!td || !td._replyQueue || td._replyQueue.length === 0) return;
        const next = td._replyQueue.shift();
        td._pendingReplies = next;
        if (td._replyQueue.length === 0) delete td._replyQueue;
        this._unread.add(npcId);
        this._updateBadge();
        const hasGoto = !!(answeredOpt && answeredOpt.npcResponse
            && /\[goto:/.test(answeredOpt.npcResponse));
        if (!hasGoto && this._isOpen && this._activeChat === npcId) {
            this._renderReplyButtons(npcId, next);
        }
    }

    _selectReply(npcId, option) {
        // Player engaged with the reply, clear unread so the badge goes away
        // (auto-open from #smsreply intentionally keeps it until the player acts).
        this._unread.delete(npcId);
        this._updateBadge();

        // Add player message
        const playerMsg = {
            id: Date.now() + Math.random(),
            type: 'sms',
            from: 'player',
            text: option.playerText,
            image: null,
            day: this._currentDay,
            timeSlot: this._currentTimeSlot,
            timestamp: Date.now(),
        };
        this._addMessage(npcId, playerMsg);

        // Process NPC response
        if (option.npcResponse) {
            const parsed = this._processResponseTokens(option.npcResponse);

            // Show typing indicator, then response
            this._showTypingIndicator(npcId);

            setTimeout(() => {
                this._removeTypingIndicator();

                // Add NPC image message
                if (parsed.image) {
                    const imgMsg = {
                        id: Date.now() + Math.random(),
                        type: 'mms',
                        from: 'npc',
                        text: null,
                        image: parsed.image,
                        day: this._currentDay,
                        timeSlot: this._currentTimeSlot,
                        timestamp: Date.now(),
                    };
                    this._addMessage(npcId, imgMsg);
                }

                // Add NPC text message
                if (parsed.text) {
                    const npcMsg = {
                        id: Date.now() + Math.random(),
                        type: 'sms',
                        from: 'npc',
                        text: parsed.text,
                        image: null,
                        day: this._currentDay,
                        timeSlot: this._currentTimeSlot,
                        timestamp: Date.now(),
                    };
                    this._addMessage(npcId, npcMsg);
                }

                // Apply [set:] tokens via direct story.variablesState writes.
                // CRITICAL: must NOT route through variableManager.getValue/setValue, 
                // non-observed Ink VARs (SMS chain stages, dating flags, DareMe state)
                // have no JS shadow, so getValue returns the baseVar default (0) instead
                // of the real Ink state and setValue corrupts the shadow on save. Direct
                // story.variablesState writes match phone.js setVar (line ~19). See
                // .claude/rules/save-state-drift.md § Rule #1.
                if (parsed.sets.length > 0) {
                    const story = window._sm?.currentStory;
                    if (story && story.variablesState) {
                        for (const s of parsed.sets) {
                            let current = 0;
                            try {
                                const v = story.variablesState[s.variable];
                                current = (typeof v === 'number') ? v : 0;
                            } catch(e) {}
                            let newVal;
                            switch (s.op) {
                                case '+': newVal = current + s.value; break;
                                case '-': newVal = current - s.value; break;
                                case '=': default: newVal = s.value; break;
                            }
                            try { story.variablesState[s.variable] = newVal; } catch(e) {}
                            console.log(`[Phone] Set ${s.variable} ${s.op} ${s.value} → ${newVal}`);
                        }
                    }
                }

                // Apply [goto:] token -> close phone, trigger forced event after delay
                if (parsed.goto) {
                    const knot = parsed.goto;
                    setTimeout(() => {
                        this.close();
                        events.emit('phone:forcedEvent', knot);
                    }, 800);
                }

                if (this._isOpen && this._activeChat === npcId) {
                    this._renderChatView(npcId);
                }
            }, 1500);
        }

        if (this._isOpen && this._activeChat === npcId) {
            this._renderChatView(npcId);
        }
    }

    _showTypingIndicator(npcId) {
        const chatContainer = this._root.querySelector('.sms-chat-container');
        if (!chatContainer) return;

        const indicator = document.createElement('div');
        indicator.className = `sms-bubble ${this._getBubbleClass(npcId)} sms-typing-indicator`;
        indicator.innerHTML = `
            <div class="sms-typing-dots">
                <span></span><span></span><span></span>
            </div>
        `;
        indicator.id = 'sms-typing';
        chatContainer.appendChild(indicator);
        const body = this._root.querySelector('#phone-body');
        if (body) body.scrollTop = body.scrollHeight;
    }

    _removeTypingIndicator() {
        const typing = this._root.querySelector('#sms-typing');
        if (typing) typing.remove();
    }

    _showFullscreenImage(src) {
        const viewer = document.createElement('div');
        viewer.className = 'sms-image-viewer';
        viewer.innerHTML = `
            <button class="sms-image-viewer-close" aria-label="Close">×</button>
            <img src="${src}" alt="Full image">
        `;
        const close = () => {
            viewer.remove();
            document.removeEventListener('keydown', onKey);
        };
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        // Backdrop tap closes; image tap and close button both close,
        // but stopPropagation keeps an accidental click on the image
        // from also triggering the backdrop handler twice.
        viewer.addEventListener('click', (e) => {
            if (e.target === viewer || e.target.classList.contains('sms-image-viewer-close')) {
                close();
            }
        });
        document.addEventListener('keydown', onKey);
        document.body.appendChild(viewer);
    }

    // ── Serialization (for save/load) ───────────────────────────────────

    serialize() {
        const data = {};
        const pendingReplies = {};
        const replyQueues = {};
        for (const [npcId, msgs] of this._threads.entries()) {
            data[npcId] = msgs.map(m => ({
                type: m.type,
                from: m.from,
                text: m.text,
                image: m.image,
                // label + goto + goto_consumed MUST persist: a one-way #sms:[goto:]
                // (perm-lock, lockpick-consequence) or file-attachment renders its
                // "Tap to continue" / "Tap to play" CTA from msg.goto, and the
                // [goto:] token is stripped out of msg.text at parse time — so if
                // these are dropped on save, the reloaded message has no clickable
                // affordance and its forced event becomes unreachable (foundby
                // Diadan4 0.2.8.1.1: 60-day cage talk, message arrived but couldn't
                // be started after sleeping/reloading). goto_consumed preserves the
                // one-shot so a consumed CTA doesn't reappear on reload.
                label: m.label,
                goto: m.goto,
                goto_consumed: m.goto_consumed,
                day: m.day,
                timeSlot: m.timeSlot,
            }));
            if (msgs._pendingReplies) {
                pendingReplies[npcId] = msgs._pendingReplies;
            }
            // Deferred reply-sets (multiple #smsreply in one drain) must survive
            // save/reload too, or the second SMS's arc dies on the reload that
            // the player uses to escape the "stuck" thread. foundby:Nigyl.
            if (msgs._replyQueue && msgs._replyQueue.length > 0) {
                replyQueues[npcId] = msgs._replyQueue;
            }
        }
        return {
            threads: data,
            unread: [...this._unread],
            pendingReplies,
            replyQueues,
        };
    }

    // ── Phone Apps (P2: GrindrR, OnlyFems, SissySocial, JobFinder) ──

    openApp(appId) {
        // Defense-in-depth (v0.2.7 audit M1): App-Store gates comingSoon on the
        // button, but Ink could still ~ hypno_app_installed = true and the home
        // grid would render the card. Refuse to actually open the app body.
        const appDef = PHONE_APPS.find(a => a.id === appId);
        if (appDef?.comingSoon) {
            console.log(`[Phone] openApp(${appId}) blocked, app is comingSoon`);
            return;
        }
        // Kill-switch gate (e.g. v029_content_enabled): refuse to open a gated
        // app while its feature flag is off, even if Ink somehow set the
        // install flag. Mirrors the comingSoon refusal above.
        if (appDef?.gateVar) {
            const gv = this._story ? this._story.variablesState[appDef.gateVar] : false;
            if (!(gv === true || gv === 1)) {
                console.log(`[Phone] openApp(${appId}) blocked, gate ${appDef.gateVar} is off`);
                return;
            }
        }
        if (!this._root) this.init();
        if (!this._isOpen) this.open();
        this._view = appId;

        // Recover stale story reference (e.g. after import creates a new story)
        if (!this._story && this._storyGetter) {
            this._story = this._storyGetter();
        }

        const backBtn = this._root.querySelector('#phone-back');
        const title = this._root.querySelector('.phone-title');
        if (backBtn) backBtn.classList.remove('hidden');

        const body = this._root.querySelector('#phone-body');
        if (!body) return;
        body.innerHTML = '';

        // Body is cleared above — a throw inside a builder would leave the app
        // permanently blank ("can't open X" reports, same class as the blanked
        // Progress tab, foundby:Wynter). Warn-level so VPS bug-report console
        // captures carry the real exception.
        try {
            switch (appId) {
                case 'grindr': this._buildGrindrR(body, title); break;
                case 'jobfinder': this._buildJobFinder(body, title); break;
                case 'onlyfems': this._buildOnlyFems(body, title); break;
                case 'sissysocial': this._buildSissySocial(body, title); break;
                case 'dareme': this._buildDareMe(body, title); break;
                case 'hypno': this._buildHypnoMind(body, title); break;
            }
        } catch (e) {
            console.warn(`[Phone] openApp(${appId}) render failed:`, e?.message);
            body.innerHTML = `<div style="padding:24px;text-align:center;font-size:12px;color:var(--text-muted)">${L.get('phone.render_error') !== 'phone.render_error' ? L.get('phone.render_error') : 'Something went wrong. Close and reopen the phone.'}</div>`;
        }
    }


    // ── GrindrR (Dating, Male NPC profiles) ──────────────────────────

    // Shared NPC data for GrindrR (used across all screens)
    _getGrindrNPCS() {
        return [
            { id: 'tyler', name: 'Tyler', age: 25, bio: 'Swimmer. Just curious about this app tbh', img: 'dating_tyler_profile',
              relVar: 'dating_tyler', matchedVar: 'matched_tyler', reqFem: 10, gender: 'man',
              stages: [
                { stage: 1, msg: "hey, you seem cool. wanna grab coffee?", label: "Coffee Date", knot: "Dating_Tyler_1", energy: 15, time: 1, reqFem: 10 },
                { stage: 2, msg: "just got back from the pool. you should come over", label: "His Dorm", knot: "Dating_Tyler_2", energy: 25, time: 2, reqFem: 20 },
                { stage: 3, msg: "the usual? \uD83D\uDE0F", label: "Tyler's Place", knot: "Dating_Tyler_3", energy: 25, time: 2, reqFem: 30 },
              ] },
            { id: 'kai', name: 'Kai', age: 23, bio: 'Looking for someone who gets it \u2728', img: 'dating_kai_profile',
              relVar: 'dating_kai', matchedVar: 'matched_kai', reqFem: 10, gender: 'man',
              stages: [
                { stage: 1, msg: "there's this boba place downtown... wanna try it?", label: "Boba Tea", knot: "Dating_Kai_1", energy: 15, time: 1, reqFem: 10 },
                { stage: 2, msg: "movie night at mine? i'll do your nails \uD83D\uDC85", label: "Movie Night", knot: "Dating_Kai_2", energy: 20, time: 1, reqFem: 20 },
                { stage: 3, msg: "sleepover? i miss you \uD83D\uDC95", label: "Sleepover", knot: "Dating_Kai_3", energy: 25, time: 2, reqFem: 35 },
              ] },
            { id: 'raven', name: 'Raven', age: 27, bio: 'Dare you. No rules. No limits.', img: 'dating_raven_profile',
              relVar: 'dating_raven', matchedVar: 'matched_raven', reqFem: 20, gender: 'man',
              stages: [
                { stage: 1, msg: "dive bar tonight. wear something that gets stares.", label: "Dive Bar", knot: "Dating_Raven_1", energy: 20, time: 1, reqFem: 20 },
                { stage: 2, msg: "party at mine. you're the entertainment.", label: "House Party", knot: "Dating_Raven_2", energy: 25, time: 2, reqFem: 30 },
                { stage: 3, msg: "got something special planned. trust me?", label: "Wild Card", knot: "Dating_Raven_3", energy: 30, time: 2, reqFem: 40 },
              ] },
            { id: 'owen', name: 'Owen', age: 44, bio: 'Thought I recognized you from the building...', img: 'dating_owen_profile',
              relVar: 'dating_owen', matchedVar: 'matched_owen', reqFem: 15, gender: 'man',
              stages: [
                { stage: 1, msg: "saw you in the hallway again. coffee at mine?", label: "His Apartment", knot: "Dating_Owen_1", energy: 15, time: 1, reqFem: 15 },
                { stage: 2, msg: "i've been meaning to talk to you about something...", label: "His Apartment", knot: "Dating_Owen_2", energy: 25, time: 2, reqFem: 25 },
                { stage: 3, msg: "door's unlocked. you know where to find me.", label: "Next Door", knot: "Dating_Owen_3", energy: 25, time: 2, reqFem: 40 },
              ] },
            // ── MASC_ROUTE §5 partners (HETERO_PARTNER.md), free, dormant behind
            // mascRoute:true (kill-switch stripped 2026-07-07). gender drives orientation_lean bias.
            // PC is the TOP for the women + tops Cole (the twink). fem_gain(0) in the arcs.
            // unlockVar (audit 2026-06-07): Rachel's profile + stage texts assume the
            // PC works at Hartwell ("you slid me my coffee last Tuesday") — she only
            // appears once he's actually hired. Mara assumes a gym regular ("three
            // days running") — gym membership unlocks her. Cole stays continuity-free.
            { id: 'rachel', name: 'Rachel', age: 27, bio: 'Front desk. Coffee’s on me if you stop pretending you’re not staring.', img: 'dating_rachel_profile',
              relVar: 'dating_rachel', matchedVar: 'matched_rachel', reqFem: 0, gender: 'woman', mascRoute: true, unlockVar: 'office_hired',
              stages: [
                { stage: 1, msg: "coffee after work? you survived another Monday.", label: "Coffee", knot: "Dating_Rachel_1", energy: 15, time: 1, reqFem: 0 },
                { stage: 2, msg: "my place beats the office gossip. come over?", label: "Her Place", knot: "Dating_Rachel_2", energy: 25, time: 2, reqFem: 0 },
                { stage: 3, msg: "same time, handsome?", label: "Rachel’s Place", knot: "Dating_Rachel_3", energy: 25, time: 2, reqFem: 0 },
              ] },
            { id: 'mara', name: 'Mara', age: 26, bio: 'You showed up three days running. Most don’t. Coffee after your set?', img: 'dating_mara_profile',
              relVar: 'dating_mara', matchedVar: 'matched_mara', reqFem: 0, gender: 'woman', mascRoute: true, unlockVar: 'gym_member',
              stages: [
                { stage: 1, msg: "smoothie after we lift? you earned it.", label: "Post-Workout", knot: "Dating_Mara_1", energy: 15, time: 1, reqFem: 0 },
                { stage: 2, msg: "my shower beats the gym one 😏 come by?", label: "Her Place", knot: "Dating_Mara_2", energy: 25, time: 2, reqFem: 0 },
                { stage: 3, msg: "spot me later? and after?", label: "Mara’s Place", knot: "Dating_Mara_3", energy: 25, time: 2, reqFem: 0 },
              ] },
            { id: 'cole', name: 'Cole', age: 24, bio: 'You look like you know what you want. I like that.', img: 'dating_cole_profile',
              relVar: 'dating_cole', matchedVar: 'matched_cole', reqFem: 0, gender: 'man', mascRoute: true,
              stages: [
                { stage: 1, msg: "drink this week? low-key place, my treat.", label: "The Bar", knot: "Dating_Cole_1", energy: 15, time: 1, reqFem: 0 },
                { stage: 2, msg: "my place is quiet tonight. come by?", label: "His Place", knot: "Dating_Cole_2", energy: 25, time: 2, reqFem: 0 },
                { stage: 3, msg: "you know the way 😏", label: "Cole’s Place", knot: "Dating_Cole_3", energy: 25, time: 2, reqFem: 0 },
              ] },
            { id: 'eloise', name: 'Elo\u00efse', age: 26, bio: 'Fuck around and find out. Literally.', img: 'dating_eloise_profile',
              relVar: 'dating_eloise', matchedVar: 'matched_eloise', reqFem: 40, patreon: true,
              stages: [
                { stage: 1, msg: "wine bar tonight. i'm picking the wine. you're picking the outfit.", label: "Wine Bar", knot: "Dating_Eloise_1", energy: 15, time: 1, reqFem: 40 },
                { stage: 2, msg: "my place. bring nothing. i have everything we need.", label: "Her Apartment", knot: "Dating_Eloise_2", energy: 20, time: 1, reqFem: 50 },
                { stage: 3, msg: "i have plans for you tonight. don't eat first.", label: "Elo\u00efse's Plans", knot: "Dating_Eloise_3", energy: 25, time: 2, reqFem: 60 },
              ] },
            // Hank (custom NPC, Patreon, by Hursk) \u2014 KILL-SWITCH-gated dormant:
            // hank_enabled kill-switch stripped 2026-07-07; patreon: true hides him
            // from browse/teasers/counts until armed. See NPC_HANK.md \u00a7Story Arc.
            { id: 'hank', name: 'Hank', age: 45, bio: 'Woodworker. Widower. Not here to play games.', img: 'dating_hank_profile',
              relVar: 'dating_hank', matchedVar: 'matched_hank', reqFem: 20, patreon: true, gender: 'man',
              stages: [
                { stage: 1, msg: "that coffee, then? no rush.", label: "Coffee", knot: "Dating_Hank_1", energy: 15, time: 1, reqFem: 20 },
                { stage: 2, msg: "i want to show you the ranch. pack light.", label: "Redwood Ranch", knot: "Dating_Hank_2", energy: 25, time: 2, reqFem: 25 },
                { stage: 3, msg: "come over. i'll cook. stay as long as you like.", label: "Hank's Home", knot: "Dating_Hank_3", energy: 25, time: 2, reqFem: 30 },
              ] },
            // Tyrone (custom NPC, Patreon, credit "Anonymous" — boxer, 35). DORMANT:
            // unlockVar tyrone_enabled (Globals default false) hides him from browse/
            // teasers/counts until the patron preview approval arms the flag; patreon:
            // true keeps him Patreon-only after arming. See NPC_TYRONE.md.
            { id: 'tyrone', name: 'Tyrone', age: 35, bio: 'Boxer. Looking to take a slut out to party.', img: 'dating_tyrone_profile',
              relVar: 'dating_tyrone', matchedVar: 'matched_tyrone', reqFem: 20, patreon: true, gender: 'man', unlockVar: 'tyrone_enabled',
              stages: [
                { stage: 1, msg: "club tonight. wear something worth staring at. i'll find you, slut 😏", label: "The Club", knot: "Dating_Tyrone_1", energy: 15, time: 1, reqFem: 20 },
                { stage: 2, msg: "fight night. you're my ring girl. look pretty, cheer loud.", label: "Fight Night", knot: "Dating_Tyrone_2", energy: 25, time: 2, reqFem: 25 },
                { stage: 3, msg: "my place. door's open. don't keep me waiting.", label: "Tyrone's Place", knot: "Dating_Tyrone_3", energy: 25, time: 2, reqFem: 30 },
              ] },
            { id: 'sasha', name: 'Sasha', age: 28, bio: 'Sissy streamer, content creator \u2764\uFE0F', img: 'dating_sasha_profile',
              relVar: 'dating_sasha', matchedVar: 'matched_sasha', reqFem: 40, patreon: true,
              stages: [
                { stage: 1, msg: "hiiiii <3 omg you're so cute!! Milano tonight?", label: "Italian Restaurant", knot: "Dating_Sasha_1", energy: 15, time: 1, reqFem: 40 },
                { stage: 2, msg: "sooo... can't stop thinking about you lol. I rented us a room.", label: "Hotel Room", knot: "Dating_Sasha_2", energy: 20, time: 1, reqFem: 50 },
                { stage: 3, msg: "babyyyy come over? I have ideas... and matching outfits.", label: "Sasha's Place", knot: "Dating_Sasha_3", energy: 25, time: 2, reqFem: 60 },
              ] },
            { id: 'patrick', name: 'Patrick', age: 31, bio: 'Poly. Pan. Pleasure dom. No degradation, only worship.', img: 'dating_patrick_profile',
              relVar: 'dating_patrick', matchedVar: 'matched_patrick', reqFem: 30, patreon: true,
              stages: [
                { stage: 1, msg: "industrial club tonight. i'll bring the drinks. you bring yourself.", label: "Industrial Club", knot: "Dating_Patrick_1", energy: 15, time: 1, reqFem: 30 },
                { stage: 2, msg: "my place? fair warning, it's messy. in a good way.", label: "Patrick's Place", knot: "Dating_Patrick_2", energy: 20, time: 1, reqFem: 40 },
                { stage: 3, msg: "i want you to meet some people. trust me?", label: "The Polycule", knot: "Dating_Patrick_3", energy: 25, time: 2, reqFem: 50 },
              ] },
            { id: 'vincent', name: 'Vincent', age: 34, bio: 'Not looking for conversation.', img: 'dating_vincent_profile',
              relVar: 'dating_vincent', matchedVar: 'matched_vincent', reqFem: 25, patreon: true,
              stages: [
                { stage: 1, msg: "my place. 20 minutes.", label: "Vincent's Place", knot: "Dating_Vincent_1", energy: 15, time: 1, reqFem: 25 },
                { stage: 2, msg: "door's unlocked.", label: "Vincent's Apartment", knot: "Dating_Vincent_2", energy: 25, time: 2, reqFem: 35 },
                { stage: 3, msg: "now.", label: "Vincent", knot: "Dating_Vincent_3", energy: 25, time: 2, reqFem: 45 },
              ] },
            { id: 'scar', name: 'Scar', age: 31, bio: 'Tailor. Genderfluid. Hello, pet, have you missed me?', img: 'dating_scar_profile',
              relVar: 'dating_scar', matchedVar: 'matched_scar', reqFem: 20, patreon: true,
              stages: [
                { stage: 1, msg: "ren faire today, pet. tailoring booth by the oaks. come find me.", label: "Renaissance Fair", knot: "Dating_Scar_1", energy: 15, time: 1, reqFem: 20 },
                { stage: 2, msg: "shop's quiet this afternoon. ready to get measured properly?", label: "Scar's Shop", knot: "Dating_Scar_2", energy: 20, time: 2, reqFem: 30 },
                { stage: 3, msg: "come over, pet. i'm working on something for you.", label: "Scar's Place", knot: "Dating_Scar_3", energy: 25, time: 2, reqFem: 40 },
              ] },
            { id: 'richard', name: 'Richard', age: 52, bio: 'CEO. Generous with the right company.', img: 'sugar_daddy',
              relVar: 'sugar_arrangement', matchedVar: 'matched_richard', reqFem: 25,
              stages: [
                { stage: 1, msg: "Opera tonight. I have a spare ticket and no one pretty enough to use it.", label: "Opera Evening", knot: "DatingDate_Sugar", energy: 15, time: 2, reqFem: 25 },
                { stage: 2, msg: "Coastal resort this weekend. Pack light, I\u2019ll provide everything.", label: "Resort Weekend", knot: "SugarDaddy_Date_Weekend", energy: 25, time: 2, reqFem: 30 },
                { stage: 3, msg: "My friends are in town. They\u2019d love to meet you on the yacht.", label: "Yacht Party", knot: "SugarDaddy_Date_Yacht", energy: 35, time: 2, reqFem: 35 },
              ] },
        ];
    }

    /** Count matched GrindrR NPCs from Ink booleans. Derived from the canonical
     *  _getGrindrNPCS() list (NOT a hardcoded var array) so masc-route NPCs
     *  (rachel/mara/cole) and any future NPC are counted automatically. The old
     *  hardcoded list silently dropped the masc NPCs, so hooking up on the masc
     *  path left Connections stuck at 0. foundby:Niya Weiss. Same divergence
     *  class as the candidates/all-matched filters (feedback_grindr_allmatched_dormant_npc). */
    _getGrindrMatchCount() {
        const story = this._story;
        if (!story) return 0;
        return this._getGrindrNPCS()
            .map(n => n.matchedVar)
            .filter(v => { try { return story.variablesState[v] === true || story.variablesState[v] === 1; } catch(e) { return false; } }).length;
    }

    _buildGrindrR(body, title) {
        if (title) title.textContent = 'GrindrR';
        const story = this._story;
        if (!story) return;
        const fem = story.variablesState['Femininity'] || 0;
        const profileType = story.variablesState['grindr_profile_type'] || 0;

        // First open: profile selection
        if (profileType === 0) {
            this._buildGrindrRProfileSelect(body, title);
            return;
        }

        const matchedCount = this._getGrindrMatchCount();
        const energy = story.variablesState['Energy'] || 0;
        const arousal = story.variablesState['Arousal'] || 0;
        // MASC_ROUTE audit 2026-06-07: masc PC sees his type-1 profile as
        // "Direct" (same mechanics, masc framing — mirrors the picker card).
        const profileLabel = profileType === 2
            ? (L.get('dating.profile_sissy') || 'Sissy')
            : (((story.variablesState['masc_path'] || 0) >= 1)
                ? (L.get('dating.profile_direct') !== 'dating.profile_direct' ? L.get('dating.profile_direct') : 'Direct')
                : (L.get('dating.profile_discreet') || 'Discreet'));

        // Home screen with navigation buttons
        const view = document.createElement('div');
        view.className = 'phone-app-view';
        view.innerHTML = `
            <div class="phone-app-header" style="background:#a060d020;border-left:3px solid #a060d0">
                <span class="phone-app-view-icon" style="color:#a060d0">\uD83D\uDC9C</span>
                <div>
                    <div class="phone-app-view-name" style="color:#a060d0">GrindrR</div>
                    <div class="phone-app-view-desc">${matchedCount} ${L.get('phone.connections').toLowerCase()} &mdash; ${profileLabel}</div>
                </div>
            </div>
            <div class="grindr-home-grid">
                <button class="grindr-home-btn" data-screen="browse">
                    <span style="font-size:24px">\uD83D\uDC64</span>
                    <span>${L.get('phone.browse')}</span>
                </button>
                <button class="grindr-home-btn" data-screen="connections">
                    <span style="font-size:24px">\u2764\uFE0F</span>
                    <span>${L.get('phone.connections')} (${matchedCount})</span>
                </button>
                <button class="grindr-home-btn" data-screen="messages">
                    <span style="font-size:24px">\uD83D\uDCE9</span>
                    <span>${L.get('phone.messages')}</span>
                </button>
                <button class="grindr-home-btn" data-screen="profile">
                    <span style="font-size:24px">\uD83D\uDC64</span>
                    <span>${L.get('phone.profile')}</span>
                </button>
            </div>
        `;

        // Quick Hookup button (below grid)
        // MASC_ROUTE audit 2026-06-07: the forced event (DatingApps_QuickHookup)
        // is bottom/oral content with no in-scene backout — never surfaced on a
        // committed masc PC. Read Ink directly (masc_path is non-observed).
        let _mascPath = 0;
        try { _mascPath = story.variablesState['masc_path'] || 0; } catch (e) { /* default 0 */ }
        const hookupSection = document.createElement('div');
        hookupSection.style.cssText = 'padding:8px 12px;';
        if (matchedCount >= 3 && _mascPath < 1) {
            const meetsReqs = arousal >= 15 && fem >= 8;
            const hookupBtn = document.createElement('button');
            hookupBtn.className = 'grindr-home-btn';
            hookupBtn.style.cssText = 'width:100%;padding:12px;margin-top:4px;background:rgba(160,96,208,0.12);border:1px solid rgba(160,96,208,0.3);border-radius:10px;color:#a060d0;font-weight:600;font-size:13px;cursor:pointer;';
            hookupBtn.textContent = meetsReqs ? L.get('phone.quick_hookup') + ' (E:10)' : L.get('phone.quick_hookup');
            if (!meetsReqs) {
                hookupBtn.disabled = true;
                hookupBtn.style.opacity = '0.5';
                hookupBtn.style.cursor = 'default';
                const reason = document.createElement('div');
                reason.style.cssText = 'font-size:9px;color:var(--text-faint);text-align:center;margin-top:4px;';
                reason.textContent = arousal < 15 ? L.get('phone.requires_arousal') : L.get('phone.requires_fem8');
                hookupSection.appendChild(hookupBtn);
                hookupSection.appendChild(reason);
            } else if (energy < 10) {
                hookupBtn.disabled = true;
                hookupBtn.style.opacity = '0.5';
                hookupBtn.style.cursor = 'default';
                const reason = document.createElement('div');
                reason.style.cssText = 'font-size:9px;color:var(--text-faint);text-align:center;margin-top:4px;';
                reason.textContent = L.get('phone.not_enough_energy_10');
                hookupSection.appendChild(hookupBtn);
                hookupSection.appendChild(reason);
            } else {
                hookupBtn.addEventListener('click', () => {
                    // Deduct energy
                    const curE = story.variablesState['Energy'] || 0;
                    syncVar(story, 'Energy', Math.max(0, curE - 10));
                    // Close BEFORE emitting forced event, _enqueueForcedEvent
                    // runs synchronously through ChoosePathString + refreshView;
                    // if anything in there throws, this.close() never runs and
                    // overlay-phone stays mounted with the backdrop covering
                    // the recovered story view.
                    this.close();
                    // Voluntary app action — 'PhoneApp' bypasses the SMS throttle.
                    events.emit('phone:forcedEvent', 'DatingApps_QuickHookup', 'PhoneApp');
                });
                hookupSection.appendChild(hookupBtn);
            }
        } else if (matchedCount > 0) {
            const hintEl = document.createElement('div');
            hintEl.style.cssText = 'font-size:10px;color:var(--text-faint);text-align:center;font-style:italic;';
            hintEl.textContent = `${L.get('phone.quick_hookup_unlock')} (${matchedCount}/3)`;
            hookupSection.appendChild(hintEl);
        }
        view.appendChild(hookupSection);

        view.querySelectorAll('.grindr-home-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const screen = btn.dataset.screen;
                if (!screen) return;
                body.innerHTML = '';
                switch (screen) {
                    case 'browse': this._buildGrindrRBrowse(body, title); break;
                    case 'connections': this._buildGrindrRConnections(body, title); break;
                    case 'messages': this._buildGrindrRMessages(body, title); break;
                    case 'profile': this._buildGrindrRProfile(body, title); break;
                }
            });
        });

        body.appendChild(view);
    }

    // ── GrindrR: Profile Selection (first open) ───────────────────────

    _buildGrindrRProfileSelect(body, title) {
        if (title) title.textContent = 'GrindrR';
        const story = this._story;
        if (!story) return;
        const fem = story.variablesState['Femininity'] || 0;
        // MASC_ROUTE audit 2026-06-07: a committed masc PC gets the same
        // mechanical profile (type 1) under masc framing — "Discreet" reads
        // as down-low; he isn't hiding anything. Sissy card stays visible
        // (Fem-8-locked) — nothing is removed, only reframed.
        let _profMasc = false;
        try { _profMasc = (story.variablesState['masc_path'] || 0) >= 1; } catch (e) { /* default */ }
        const _profName = _profMasc
            ? (L.get('dating.profile_direct') !== 'dating.profile_direct' ? L.get('dating.profile_direct') : 'Direct')
            : (L.get('dating.profile_discreet') || 'Discreet');
        const _profBio = _profMasc
            ? (L.get('dating.profile_direct_bio') !== 'dating.profile_direct_bio' ? L.get('dating.profile_direct_bio') : 'Looking, not hiding. Says what he wants.')
            : 'Keep it low-key. No special requirements.';

        const view = document.createElement('div');
        view.className = 'phone-app-view';
        view.innerHTML = `
            <div class="flintr-profile-select">
                <h3 style="color:#a060d0;text-align:center;margin-bottom:8px">Choose Your Profile</h3>
                <p style="color:var(--text-muted);text-align:center;font-size:11px;margin-bottom:16px">This determines how you appear to others on GrindrR.</p>
                <button class="dating-profile-card flintr-profile-option" data-type="1">
                    <div class="dating-profile-info">
                        <div class="dating-name">${_profName}</div>
                        <div class="dating-bio">${_profBio}</div>
                    </div>
                </button>
                <button class="dating-profile-card flintr-profile-option ${fem < 8 ? 'dating-locked' : ''}" data-type="2" ${fem < 8 ? 'disabled' : ''}>
                    <div class="dating-profile-info">
                        <div class="dating-name">${L.get('dating.profile_sissy') || 'Sissy'}</div>
                        <div class="dating-bio">${fem < 8 ? 'Requires Fem 8' : 'Lean into it. Better match rates. +2 Fem, +3 Shame.'}</div>
                    </div>
                </button>
            </div>
        `;
        view.querySelectorAll('.flintr-profile-option:not(.dating-locked)').forEach(btn => {
            btn.addEventListener('click', () => {
                const type = parseInt(btn.dataset.type);
                syncVar(story, 'grindr_profile_type', type);
                localStorage.setItem('PnO_GrindrR_ProfileSet', '1');
                if (type === 2 && !story.variablesState['grindr_profile_bonus_claimed']) {
                    // Sissy profile: +2 Fem, +3 Shame, FIRST time only.
                    // The profile screen can re-open this picker to switch type,
                    // so gate the grant on the one-shot latch to prevent farming.
                    const curFem = story.variablesState['Femininity'] || 0;
                    syncVar(story, 'Femininity', curFem + 2);
                    const curShame = story.variablesState['Shame'] || 0;
                    syncVar(story, 'Shame', curShame + 3);
                    events.emit(Events.TAG_ACHIEVEMENT, 'sissy_profile');
                    syncVar(story, 'grindr_profile_bonus_claimed', true);
                }
                // Rebuild with profile set
                body.innerHTML = '';
                this._buildGrindrR(body, title);
            });
        });
        body.appendChild(view);
    }

    // ── GrindrR: Browse / Swipe Screen ────────────────────────────────

    // ── GrindrR pass/date timers — Ink-state backed (0.3 rework) ──────
    //
    // Pre-0.3 these lived in localStorage (PnO_GrindrCooldown_<npc> +
    // PnO_GrindrLastDate_<npc>). localStorage is browser-global, NOT part of
    // the save, so the timers survived undo, loading an earlier save (unless
    // strictly future-day — the 2026-05-04 heal) and even New Game. VPS
    // report #392 (0.2.9.0.6 Free): "GrindR progression persists through
    // reverts and loading. Expectation: waiting times would reset."
    // Now the single source of truth is the Ink string VAR grindr_npc_timers
    // ("id:day,id:day") — it travels with the save, reverts with undo/load,
    // and starts empty on New Game. One map covers BOTH the 3-day cooldown
    // and the per-NPC same-day lock (the old two keys were always written
    // together with the same value; I3 audit 2026-05-24 semantics kept: a
    // date with Tyler at 10am doesn't lock Patrick at 8pm, and a PASS starts
    // the same 3-day cooldown).

    _readGrindrTimers() {
        const story = this._story;
        if (!story) return {};
        this._migrateGrindrTimersOnce(story);
        let raw = '';
        try { raw = story.variablesState['grindr_npc_timers'] || ''; } catch (e) { raw = ''; }
        const map = {};
        String(raw).split(',').forEach(pair => {
            const [id, d] = pair.split(':');
            const day = parseInt(d, 10);
            if (id && Number.isFinite(day)) map[id] = day;
        });
        return map;
    }

    _writeGrindrTimers(map) {
        const story = this._story;
        if (!story) return;
        const str = Object.entries(map).map(([id, d]) => `${id}:${d}`).join(',');
        syncVar(story, 'grindr_npc_timers', str);
    }

    /** One-time (per page load) legacy migration: fold pre-0.3 localStorage
     *  timers into the Ink map, then delete the keys. Entries from a "future"
     *  day (older save / new game in the same browser) are dropped — same
     *  judgement call as the old stale-future heal. */
    _migrateGrindrTimersOnce(story) {
        if (this._grindrTimersMigrated) return;
        this._grindrTimersMigrated = true;
        try {
            const currentDay = story.variablesState['Day'] || 1;
            let raw = '';
            try { raw = story.variablesState['grindr_npc_timers'] || ''; } catch (e) { raw = ''; }
            const map = {};
            String(raw).split(',').forEach(pair => {
                const [id, d] = pair.split(':');
                const day = parseInt(d, 10);
                if (id && Number.isFinite(day)) map[id] = day;
            });
            let changed = false;
            const stale = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                const m = /^PnO_Grindr(?:Cooldown|LastDate)_(.+)$/.exec(key || '');
                if (!m) continue;
                stale.push(key);
                const day = parseInt(localStorage.getItem(key), 10);
                if (!Number.isFinite(day) || day > currentDay) continue;
                if (!(map[m[1]] >= day)) { map[m[1]] = day; changed = true; }
            }
            stale.forEach(k => { try { localStorage.removeItem(k); } catch (e) {} });
            if (changed) this._writeGrindrTimers(map);
        } catch (e) { /* storage blocked (itch iframe) — Ink map stands alone */ }
    }

    /**
     * Check if an NPC is on GrindrR cooldown (3 Ink days).
     * @param {string} npcId - e.g. 'tyler'
     * @param {number} currentDay - current Ink Day value
     * @returns {{ onCooldown: boolean, daysLeft: number }}
     */
    _getGrindrCooldown(npcId, currentDay) {
        const map = this._readGrindrTimers();
        const storedDay = map[npcId];
        if (!Number.isFinite(storedDay)) return { onCooldown: false, daysLeft: 0 };
        // Future-day guard, belt-and-suspenders: with Ink-state timers this
        // should be unreachable (undo/load revert the map together with Day),
        // but prune rather than wedge if it ever happens.
        if (currentDay < storedDay) {
            delete map[npcId];
            this._writeGrindrTimers(map);
            return { onCooldown: false, daysLeft: 0 };
        }
        const elapsed = currentDay - storedDay;
        if (elapsed >= 3) return { onCooldown: false, daysLeft: 0 };
        return { onCooldown: true, daysLeft: 3 - elapsed };
    }

    /**
     * Set GrindrR cooldown for an NPC (stores current Ink day; also serves
     * as the per-NPC same-day "already met today" marker).
     * @param {string} npcId
     * @param {number} currentDay
     */
    _setGrindrCooldown(npcId, currentDay) {
        const map = this._readGrindrTimers();
        map[npcId] = currentDay;
        this._writeGrindrTimers(map);
    }

    _getGrindrLastDateDay(npcId) {
        const map = this._readGrindrTimers();
        const day = map[npcId];
        return Number.isFinite(day) ? day : 0;
    }

    _buildGrindrRBrowse(body, title) {
        if (title) title.textContent = L.get('phone.grindr_browse');
        const story = this._story;
        if (!story) return;
        const fem = story.variablesState['Femininity'] || 0;
        const energy = story.variablesState['Energy'] || 0;
        // perk_dating_bonus ("Dating Boost"): swipes cost 3 Energy instead of 5,
        // an ongoing discount for the whole run (mirrors perk_extra_rest flag-read).
        const swipeCost = (story.variablesState['perk_dating_bonus'] === true || story.variablesState['perk_dating_bonus'] === 1) ? 3 : 5;
        const isPatreon = isPat();
        const day = story.variablesState['Day'] || 1;
        const NPCS = this._getGrindrNPCS();

        // Filter to unmatched NPCs that meet fem requirements
        const candidates = NPCS.filter(npc => {
            if (npc.patreon && !isPatreon) return false;
            // Continuity unlock (audit 2026-06-07): partner appears once the PC
            // has actually entered her world (office hire / gym membership).
            if (npc.unlockVar && !(story.variablesState[npc.unlockVar] === true || story.variablesState[npc.unlockVar] === 1)) return false;
            const matched = story.variablesState[npc.matchedVar] === true || story.variablesState[npc.matchedVar] === 1;
            if (matched) return false;
            if (fem < npc.reqFem) return false;
            const { onCooldown } = this._getGrindrCooldown(npc.id, day);
            if (onCooldown) return false;
            return true;
        });

        const view = document.createElement('div');
        view.className = 'phone-app-view';

        if (candidates.length === 0) {
            // No profiles available \u2014 pick the right hint based on WHY:
            // (a) all visible NPCs already matched \u2192 point at Connections tab
            // (b) some on cooldown \u2192 "check back later"
            // (c) Fem too low \u2192 "raise your stats"
            // Mirror the candidates visibility predicate EXACTLY (patreon + dormant
            // masc-route), or dormant rachel/mara/cole stay permanently unmatched and
            // allMatched never goes true → the "matched everyone" hint can never fire.
            const visibleNpcs = NPCS.filter(n => !(n.patreon && !isPatreon)
                && !(n.unlockVar && !(story.variablesState[n.unlockVar] === true || story.variablesState[n.unlockVar] === 1)));
            const allMatched = visibleNpcs.every(n =>
                story.variablesState[n.matchedVar] === true ||
                story.variablesState[n.matchedVar] === 1);
            const hint = allMatched
                ? (L.get('phone.no_profiles_all_matched') !== 'phone.no_profiles_all_matched'
                    ? L.get('phone.no_profiles_all_matched')
                    : 'All available connections matched \u2014 check the Connections tab!')
                : L.get('phone.no_profiles_hint');
            view.innerHTML = `
                <div style="text-align:center;padding:24px 12px;color:var(--text-muted)">
                    <div style="font-size:36px;margin-bottom:12px">\uD83D\uDC64</div>
                    <div style="font-weight:600;margin-bottom:8px">${L.get('phone.no_profiles')}</div>
                    <div style="font-size:11px">${hint}</div>
                </div>
            `;

            // Show cooldown NPCs
            for (const npc of NPCS) {
                if (npc.patreon && !isPatreon) continue;
                // unlockVar (audit 2026-06-07 r4): teaser loops must respect the same
                // job/membership gate as the browse filter — Mara leaked "available
                // in N days" to non-gym-members through this loop.
                if (npc.unlockVar && !(story.variablesState[npc.unlockVar] === true || story.variablesState[npc.unlockVar] === 1)) continue;
                const matched = story.variablesState[npc.matchedVar] === true || story.variablesState[npc.matchedVar] === 1;
                if (matched) continue;
                const { onCooldown, daysLeft } = this._getGrindrCooldown(npc.id, day);
                if (onCooldown && fem >= npc.reqFem) {
                    const cdEl = document.createElement('div');
                    cdEl.style.cssText = 'font-size:10px;color:var(--text-faint);text-align:center;font-style:italic;margin-top:2px;';
                    cdEl.textContent = `${npc.name} - available in ${daysLeft} day${daysLeft > 1 ? 's' : ''}`;
                    view.appendChild(cdEl);
                }
            }

            // Show locked teasers
            for (const npc of NPCS) {
                if (npc.patreon && !isPatreon) continue;
                if (npc.unlockVar && !(story.variablesState[npc.unlockVar] === true || story.variablesState[npc.unlockVar] === 1)) continue;
                const matched = story.variablesState[npc.matchedVar] === true || story.variablesState[npc.matchedVar] === 1;
                if (matched) continue;
                if (fem >= npc.reqFem) continue;
                const teaserEl = document.createElement('div');
                teaserEl.style.cssText = 'font-size:10px;color:var(--text-faint);text-align:center;margin-top:2px;';
                teaserEl.textContent = `??? - Requires Fem ${npc.reqFem}`;
                view.appendChild(teaserEl);
            }

            const backBtn = document.createElement('button');
            backBtn.className = 'grindr-home-btn';
            backBtn.style.cssText = 'margin:16px auto;display:block;padding:8px 24px;';
            backBtn.textContent = L.get('phone.back');
            backBtn.addEventListener('click', () => { body.innerHTML = ''; this._buildGrindrR(body, title); });
            view.appendChild(backBtn);
            body.appendChild(view);
            return;
        }

        // Pick a candidate to show as a swipe card. orientation_lean (-5..+5,
        // negative=men, positive=women) BIASES the pick frequency, never suppresses
        // (MASC_ROUTE §5): every candidate stays in the pool; the aligned gender is
        // just weighted higher. gender is on the NPC def; eloise counts as woman.
        let npc;
        const orientationLean = Number(story.variablesState['orientation_lean'] || 0);
        const mascPath = Number(story.variablesState['masc_path'] || 0);
        if ((orientationLean !== 0 || mascPath >= 1) && candidates.length > 1) {
            const isWomanNpc = (n) => n.gender === 'woman' || n.id === 'eloise';
            const weighted = [];
            for (const c of candidates) {
                const aligned = isWomanNpc(c) ? (orientationLean > 0) : (orientationLean < 0);
                let w = aligned ? 1 + Math.min(Math.abs(orientationLean), 5) : 1;
                // MASC_ROUTE: on a committed masc PC, surface the masc-route partners
                // (Rachel/Mara/Cole, mascRoute:true) over the feminizing dates
                // (Tyler/Kai/Owen/Raven). Keyed on mascRoute, NOT gender, so Cole (a
                // man, the bi masc partner) is boosted, not suppressed. The feminizing
                // dates stay at weight 1, reachable if deliberately swiped, never removed.
                if (mascPath >= 1) {
                    if (c.mascRoute) w += 2;
                    else w = 1;
                }
                for (let i = 0; i < w; i++) weighted.push(c);
            }
            npc = weighted[Math.floor(Math.random() * weighted.length)];
        } else {
            npc = candidates[Math.floor(Math.random() * candidates.length)];
        }

        view.innerHTML = `
            <div style="text-align:center;padding:16px 12px;">
                <div class="swipe-card" style="border-top:3px solid #a060d0;max-width:280px;margin:0 auto;">
                    <div class="swipe-photo"><img src="assets/images/Computer/${npc.img}.png" alt="${npc.name}" onerror="if(this.parentElement)this.parentElement.innerHTML='<span style=font-size:48px>\uD83D\uDC64</span>'"></div>
                    <div class="swipe-info">
                        <div class="swipe-name">${npc.name}, ${npc.age}</div>
                        <div class="swipe-bio">${npc.bio}</div>
                    </div>
                    <div class="swipe-actions">
                        <button class="swipe-btn swipe-pass" title="Pass">\u274C</button>
                        <button class="swipe-btn swipe-like" title="Like" style="background:rgba(160,96,208,0.12);border-color:#a060d0">\u2764\uFE0F</button>
                    </div>
                </div>
                <div style="font-size:10px;color:var(--text-faint);margin-top:8px;">${swipeCost < 5 ? `Swipe costs ${swipeCost} Energy` : (L.get('dating.swipe_cost') || 'Swipe costs 5 Energy')}</div>
            </div>
        `;

        // Pass button: skip this NPC (3-day cooldown)
        view.querySelector('.swipe-pass').addEventListener('click', () => {
            this._setGrindrCooldown(npc.id, day);
            const card = view.querySelector('.swipe-card');
            if (card) card.classList.add('swipe-left');
            setTimeout(() => { body.innerHTML = ''; this._buildGrindrRBrowse(body, title); }, 250);
        });

        // Like button: match with NPC (costs swipeCost Energy — 3 with perk_dating_bonus, else 5)
        const likeBtn = view.querySelector('.swipe-like');
        if (energy < swipeCost) {
            likeBtn.disabled = true;
            likeBtn.style.opacity = '0.5';
            likeBtn.title = L.get('phone.not_enough_energy');
        } else {
            likeBtn.addEventListener('click', () => {
                // Deduct swipeCost energy for swipe
                const curE = story.variablesState['Energy'] || 0;
                syncVar(story, 'Energy', Math.max(0, curE - swipeCost));

                // Set matched var
                syncVar(story, npc.matchedVar, true);

                // +3 Arousal on match
                const curArousal = story.variablesState['Arousal'] || 0;
                syncVar(story, 'Arousal', Math.min(100, curArousal + 3));

                // Show "It's a Match!" screen
                const card = view.querySelector('.swipe-card');
                if (card) card.classList.add('swipe-right');
                setTimeout(() => {
                    this._buildGrindrRMatchScreen(body, title, npc);
                }, 250);
            });
        }

        body.appendChild(view);
    }

    // ── GrindrR: "It's a Match!" Screen ───────────────────────────────

    _buildGrindrRMatchScreen(body, title, npc) {
        if (title) title.textContent = L.get('phone.its_a_match');
        body.innerHTML = '';
        const view = document.createElement('div');
        view.className = 'phone-app-view';
        view.innerHTML = `
            <div style="text-align:center;padding:24px 12px;">
                <div style="font-size:48px;margin-bottom:8px">\u2764\uFE0F</div>
                <div style="font-size:20px;font-weight:700;color:#a060d0;margin-bottom:4px">${L.get('phone.its_a_match')}</div>
                <div class="swipe-photo" style="width:80px;height:80px;border-radius:50%;margin:12px auto;overflow:hidden;">
                    <img src="assets/images/Computer/${npc.img}.png" alt="${npc.name}" style="width:100%;height:100%;object-fit:cover;" onerror="if(this.parentElement)this.parentElement.innerHTML='<span style=font-size:36px>\uD83D\uDC64</span>'">
                </div>
                <div style="font-size:16px;font-weight:600;margin-bottom:4px">${npc.name}, ${npc.age}</div>
                <div style="font-size:12px;color:var(--text-muted);margin-bottom:16px">"${npc.bio}"</div>
                <div style="font-size:11px;color:var(--text-faint);margin-bottom:16px">${L.get('phone.check_connections')}</div>
                <div style="display:flex;gap:8px;justify-content:center;">
                    <button class="grindr-match-continue" style="padding:10px 20px;border-radius:8px;background:rgba(160,96,208,0.15);border:1px solid rgba(160,96,208,0.3);color:#a060d0;font-weight:600;cursor:pointer;">${L.get('phone.keep_browsing')}</button>
                    <button class="grindr-match-connections" style="padding:10px 20px;border-radius:8px;background:#a060d0;border:none;color:white;font-weight:600;cursor:pointer;">${L.get('phone.connections')}</button>
                </div>
            </div>
        `;
        view.querySelector('.grindr-match-continue').addEventListener('click', () => {
            body.innerHTML = '';
            this._buildGrindrRBrowse(body, title);
        });
        view.querySelector('.grindr-match-connections').addEventListener('click', () => {
            body.innerHTML = '';
            this._buildGrindrRConnections(body, title);
        });
        body.appendChild(view);
    }

    // ── GrindrR: Connections Screen (matched NPCs with stages) ────────

    _buildGrindrRConnections(body, title) {
        if (title) title.textContent = L.get('phone.grindr_connections');
        const story = this._story;
        if (!story) return;
        const fem = story.variablesState['Femininity'] || 0;
        const isPatreon = isPat();
        const day = story.variablesState['Day'] || 1;
        // I3 fix (audit 2026-05-24): per-NPC same-day check below per card.
        // Old global `dating_npc_last_day` retained for Ink-side back-compat
        // but no longer drives the "Meet tomorrow" UI lock across all NPCs.
        const energy = story.variablesState['Energy'] || 0;
        const NPCS = this._getGrindrNPCS();

        const view = document.createElement('div');
        view.className = 'phone-app-view';

        let anyVisible = false;

        for (const npc of NPCS) {
            const isPatLocked = npc.patreon && !isPatreon;
            if (isPatLocked) continue;

            const matched = story.variablesState[npc.matchedVar] === true || story.variablesState[npc.matchedVar] === 1;
            const relProgress = story.variablesState[npc.relVar] || 0;

            // Must be matched or have some progress
            if (!matched && relProgress < 1) continue;
            anyVisible = true;

            const currentStageIdx = Math.min(relProgress, npc.stages.length - 1);
            const currentStage = npc.stages[currentStageIdx];
            const allDone = relProgress >= npc.stages.length;

            // Hearts indicator (filled/empty)
            const hearts = npc.stages.map((s, i) =>
                i < relProgress ? '\u2665' : '\u2661'
            ).join(' ');

            // Stage fem gate
            const stageLocked = currentStage && fem < currentStage.reqFem;

            // Cooldown check
            const { onCooldown: npcOnCooldown, daysLeft } = this._getGrindrCooldown(npc.id, day);
            // I3 fix: per-NPC same-day check (replaces global dating_npc_last_day)
            const datedToday = this._getGrindrLastDateDay(npc.id) === day;

            const card = document.createElement('div');
            card.className = 'dating-profile-card';
            card.innerHTML = `
                <div class="dating-avatar"><img src="assets/images/Computer/${npc.img}.png" alt="${npc.name}" onerror="this.style.display='none'"><span class="dating-avatar-letter">${npc.name[0]}</span></div>
                <div class="dating-profile-info">
                    <div class="dating-name">${npc.name}, ${npc.age}</div>
                    <div class="dating-hearts" style="color:#a060d0;font-size:14px;letter-spacing:2px;">${hearts}</div>
                    ${!allDone && currentStage ? `<div class="dating-msg" style="font-size:10px;color:var(--text-muted);font-style:italic;margin-top:2px;">"${currentStage.msg}"</div>` : ''}
                    ${allDone ? `<div class="dating-status" style="font-size:10px;color:#a060d0;">${L.get('dating.repeatable') || 'Repeatable'}</div>` : ''}
                </div>
            `;

            // "Meet Up" button area
            const actionArea = document.createElement('div');
            actionArea.className = 'dating-action-area';

            if (allDone || !stageLocked) {
                const meetBtn = document.createElement('button');
                meetBtn.className = 'dating-meet-btn';
                const stageEnergy = currentStage ? currentStage.energy : 25;

                if (datedToday) {
                    meetBtn.textContent = L.get('phone.meet_tomorrow');
                    meetBtn.disabled = true;
                    meetBtn.classList.add('is-locked');
                } else if (npcOnCooldown) {
                    meetBtn.textContent = L.get('phone.meet.cooldown', 'Meet Up ({0} day{1})')
                        .replace('{0}', String(daysLeft))
                        .replace('{1}', daysLeft > 1 ? L.get('phone.meet.plural_s', 's') : '');
                    meetBtn.disabled = true;
                    meetBtn.classList.add('is-locked');
                } else if (energy < stageEnergy) {
                    meetBtn.textContent = L.get('phone.meet.not_enough_energy', 'Meet Up (E:{0}) - not enough energy').replace('{0}', String(stageEnergy));
                    meetBtn.disabled = true;
                    meetBtn.classList.add('is-locked');
                } else {
                    meetBtn.textContent = L.get('phone.meet.available', 'Meet Up (E:{0} T:{1})')
                        .replace('{0}', String(stageEnergy))
                        .replace('{1}', String(currentStage ? currentStage.time : 1));
                    meetBtn.addEventListener('click', () => {
                        // Masc-route partners (Rachel/Mara/Cole) bounce a caged
                        // or femme-dressed PC into a no-progress decline scene —
                        // mirror that ink gate here so the daily slot + per-NPC
                        // cooldown aren't burned on it (deep-review L-3).
                        let willDecline = false;
                        try {
                            willDecline = ['rachel', 'mara', 'cole'].includes(npc.id)
                                && story
                                && (story.variablesState['wearing_chastity'] === true
                                    || story.variablesState['EquippedFeminine'] === true);
                        } catch (e) { willDecline = false; }
                        if (!willDecline) {
                            if (story) syncVar(story, 'dating_npc_last_day', day);
                            this._setGrindrCooldown(npc.id, day);
                        }
                        // Close BEFORE emit, see DatingApps_QuickHookup note.
                        // Voluntary date — 'PhoneApp' bypasses the SMS throttle.
                        this.close();
                        events.emit('phone:forcedEvent', currentStage.knot, 'PhoneApp');
                    });
                }
                actionArea.appendChild(meetBtn);
            } else {
                // Stage locked by fem
                const lockMsg = document.createElement('div');
                lockMsg.className = 'dating-stage-locked';
                lockMsg.textContent = `${L.get('phone.needs_fem')} ${currentStage.reqFem}`;
                actionArea.appendChild(lockMsg);
            }

            card.appendChild(actionArea);
            view.appendChild(card);
        }

        if (!anyVisible) {
            view.innerHTML = `
                <div style="text-align:center;padding:24px 12px;color:var(--text-muted)">
                    <div style="font-size:36px;margin-bottom:12px">\u2764\uFE0F</div>
                    <div style="font-weight:600;margin-bottom:8px">${L.get('phone.no_connections')}</div>
                    <div style="font-size:11px">${L.get('phone.no_connections_hint')}</div>
                </div>
            `;
        }

        // Back button
        const backBtn = document.createElement('button');
        backBtn.className = 'grindr-home-btn';
        backBtn.style.cssText = 'margin:12px auto;display:block;padding:8px 24px;';
        backBtn.textContent = L.get('phone.back');
        backBtn.addEventListener('click', () => { body.innerHTML = ''; this._buildGrindrR(body, title); });
        view.appendChild(backBtn);

        body.appendChild(view);
    }

    // ── GrindrR: Messages Screen ──────────────────────────────────────

    _buildGrindrRMessages(body, title) {
        if (title) title.textContent = L.get('phone.grindr_messages');
        const story = this._story;
        if (!story) return;
        const profileType = story.variablesState['grindr_profile_type'] || 0;
        const day = story.variablesState['Day'] || 1;
        const matchedCount = this._getGrindrMatchCount();

        const view = document.createElement('div');
        view.className = 'phone-app-view';

        // Match notifications for recently matched NPCs
        const NPCS = this._getGrindrNPCS();
        const isPatreon = isPat();
        let anyMessages = false;

        for (const npc of NPCS) {
            if (npc.patreon && !isPatreon) continue;
            const matched = story.variablesState[npc.matchedVar] === true || story.variablesState[npc.matchedVar] === 1;
            const relProgress = story.variablesState[npc.relVar] || 0;
            if (!matched) continue;

            anyMessages = true;
            const currentStageIdx = Math.min(relProgress, npc.stages.length - 1);
            const currentStage = npc.stages[currentStageIdx];
            const allDone = relProgress >= npc.stages.length;

            const row = document.createElement('div');
            row.className = 'dating-profile-card';
            row.style.cssText = 'cursor:pointer;';
            row.innerHTML = `
                <div class="dating-avatar"><img src="assets/images/Computer/${npc.img}.png" alt="${npc.name}" onerror="this.style.display='none'"><span class="dating-avatar-letter">${npc.name[0]}</span></div>
                <div class="dating-profile-info">
                    <div class="dating-name" style="font-size:12px;">${npc.name}</div>
                    <div class="dating-msg" style="font-size:10px;color:var(--text-muted);">${allDone ? 'the usual? ;)' : currentStage ? currentStage.msg : 'Hey...'}</div>
                </div>
            `;
            row.addEventListener('click', () => {
                body.innerHTML = '';
                this._buildGrindrRConnections(body, title);
            });
            view.appendChild(row);
        }

        // Unsolicited DMs (1-2 per day, seeded by Day)
        if (profileType > 0) {
            const UNSOLICITED_DMS = [
                { sender: 'NoFace99', msg: 'hey', context: 'No photo. No bio. Just "hey."' },
                { sender: 'DaddyMike', msg: 'looking?', context: 'Profile: "DL. Masc4Masc. No fems." ...you\'re on his app anyway.' },
                { sender: 'CuriousBro', msg: 'first time on here... nervous lol', context: 'Probably not his first time.' },
                { sender: 'SirDom42', msg: 'I know what you need, sissy.', context: 'His profile is... detailed.' },
                { sender: 'GymRat88', msg: 'nice pics. you work out?', context: 'He definitely works out.' },
                { sender: 'Unknown', msg: 'ur cute. come over.', context: '0 feet away. That\'s... concerning.' },
                { sender: 'CouplesFun', msg: 'we think you\'d be perfect for us', context: 'Profile shows a couple. She picks, he watches.' },
                { sender: 'BigJ_XXL', msg: 'hey beautiful, u free tonight?', context: 'Subtle.' },
                { sender: 'DiscretePro', msg: 'can you host?', context: 'He cannot, apparently.' },
                { sender: 'SissyHunter', msg: 'found you ;)', context: 'How does he know?' },
            ];

            // Seed-based selection (day-based, 1-2 messages)
            const seed = day * 7919;
            const dmCount = profileType === 2 ? 2 : 1; // Sissy profile gets more DMs
            for (let i = 0; i < dmCount; i++) {
                const dmIdx = (seed + i * 3571) % UNSOLICITED_DMS.length;
                const dm = UNSOLICITED_DMS[dmIdx];
                anyMessages = true;

                const row = document.createElement('div');
                row.className = 'dating-profile-card';
                row.style.cssText = 'opacity:0.7;';
                row.innerHTML = `
                    <div class="dating-avatar"><span class="dating-avatar-letter">${dm.sender[0]}</span></div>
                    <div class="dating-profile-info">
                        <div class="dating-name" style="font-size:11px;color:var(--text-muted);">${dm.sender}</div>
                        <div class="dating-msg" style="font-size:10px;color:var(--text-faint);font-style:italic;">"${dm.msg}"</div>
                        <div style="font-size:9px;color:var(--text-faint);margin-top:2px;">${dm.context}</div>
                    </div>
                `;
                view.appendChild(row);
            }
        }

        if (!anyMessages) {
            view.innerHTML = `
                <div style="text-align:center;padding:24px 12px;color:var(--text-muted)">
                    <div style="font-size:36px;margin-bottom:12px">\uD83D\uDCE9</div>
                    <div style="font-weight:600;margin-bottom:8px">${L.get('phone.no_messages')}</div>
                    <div style="font-size:11px">${L.get('phone.match_to_chat')}</div>
                </div>
            `;
        }

        // Back button
        const backBtn = document.createElement('button');
        backBtn.className = 'grindr-home-btn';
        backBtn.style.cssText = 'margin:12px auto;display:block;padding:8px 24px;';
        backBtn.textContent = L.get('phone.back');
        backBtn.addEventListener('click', () => { body.innerHTML = ''; this._buildGrindrR(body, title); });
        view.appendChild(backBtn);

        body.appendChild(view);
    }

    // ── GrindrR: Profile Screen ───────────────────────────────────────

    _buildGrindrRProfile(body, title) {
        if (title) title.textContent = L.get('phone.grindr_profile');
        const story = this._story;
        if (!story) return;
        const profileType = story.variablesState['grindr_profile_type'] || 0;
        const fem = story.variablesState['Femininity'] || 0;
        const energy = story.variablesState['Energy'] || 0;
        const matchedCount = this._getGrindrMatchCount();
        const isPatreon = isPat();
        const NPCS = this._getGrindrNPCS();

        // Count completed dates
        let datesCompleted = 0;
        for (const npc of NPCS) {
            if (npc.patreon && !isPatreon) continue;
            const rel = story.variablesState[npc.relVar] || 0;
            datesCompleted += rel;
        }

        // MASC_ROUTE audit 2026-06-07: masc PC sees his type-1 profile as
        // "Direct" (same mechanics, masc framing — mirrors the picker card).
        const profileLabel = profileType === 2
            ? (L.get('dating.profile_sissy') || 'Sissy')
            : (((story.variablesState['masc_path'] || 0) >= 1)
                ? (L.get('dating.profile_direct') !== 'dating.profile_direct' ? L.get('dating.profile_direct') : 'Direct')
                : (L.get('dating.profile_discreet') || 'Discreet'));

        const view = document.createElement('div');
        view.className = 'phone-app-view';
        view.innerHTML = `
            <div style="text-align:center;padding:16px 12px;">
                <div style="width:60px;height:60px;border-radius:50%;background:rgba(160,96,208,0.15);margin:0 auto 12px;display:flex;align-items:center;justify-content:center;">
                    <span style="font-size:28px;color:#a060d0">\uD83D\uDC64</span>
                </div>
                <div style="font-size:16px;font-weight:600;color:#a060d0;margin-bottom:4px">${profileLabel} ${L.get('phone.profile')}</div>
                <div style="font-size:11px;color:var(--text-muted);margin-bottom:16px;">${L.get('phone.your_grindr_identity')}</div>
            </div>
            <div style="padding:0 12px;">
                <div style="display:flex;justify-content:space-around;margin-bottom:16px;padding:12px;background:rgba(160,96,208,0.05);border-radius:8px;">
                    <div style="text-align:center;">
                        <div style="font-size:20px;font-weight:700;color:#a060d0">${matchedCount}</div>
                        <div style="font-size:10px;color:var(--text-muted)">${L.get('phone.matches')}</div>
                    </div>
                    <div style="text-align:center;">
                        <div style="font-size:20px;font-weight:700;color:#a060d0">${datesCompleted}</div>
                        <div style="font-size:10px;color:var(--text-muted)">${L.get('phone.dates')}</div>
                    </div>
                </div>
            </div>
        `;

        // Profile update options (costs 5E)
        const updateSection = document.createElement('div');
        updateSection.style.cssText = 'padding:0 12px;';

        const updateTitle = document.createElement('div');
        updateTitle.style.cssText = 'font-size:12px;font-weight:600;color:var(--text-muted);margin-bottom:8px;';
        updateTitle.textContent = L.get('phone.update_photos');
        updateSection.appendChild(updateTitle);

        const PROFILE_OPTIONS = [
            { key: 'subtle',  name: L.get('phone.sissysocial.opt.subtle.name', 'Keep it subtle'), reqFem: 0,  femGain: 0, shameGain: 0 },
            { key: 'skin',    name: L.get('phone.sissysocial.opt.skin.name', 'Show more skin'),    reqFem: 10, femGain: 1, shameGain: 2 },
            { key: 'full',    name: L.get('phone.sissysocial.opt.full.name', 'Full sissy mode'),   reqFem: 20, femGain: 2, shameGain: 3 },
        ];

        for (const opt of PROFILE_OPTIONS) {
            const btn = document.createElement('button');
            btn.style.cssText = 'display:block;width:100%;padding:8px 12px;margin-bottom:6px;border-radius:8px;font-size:11px;text-align:left;cursor:pointer;';

            if (fem < opt.reqFem) {
                btn.textContent = L.get('phone.sissysocial.requires_fem', '{0} (Requires Fem {1})')
                    .replace('{0}', opt.name).replace('{1}', String(opt.reqFem));
                btn.disabled = true;
                btn.style.cssText += 'background:rgba(160,96,208,0.05);border:1px solid rgba(160,96,208,0.1);color:var(--text-faint);cursor:default;opacity:0.5;';
            } else if (energy < 5) {
                btn.textContent = L.get('phone.sissysocial.not_enough_energy', '{0} (E:5) - not enough energy').replace('{0}', opt.name);
                btn.disabled = true;
                btn.style.cssText += 'background:rgba(160,96,208,0.05);border:1px solid rgba(160,96,208,0.1);color:var(--text-faint);cursor:default;opacity:0.5;';
            } else {
                let label = opt.name + ' (E:5)';
                if (opt.femGain > 0) label += ` +${opt.femGain} ${L.get('phone.sissysocial.label.fem', 'Fem')}`;
                if (opt.shameGain > 0) label += ` +${opt.shameGain} ${L.get('phone.sissysocial.label.shame', 'Shame')}`;
                btn.textContent = label;
                btn.style.cssText += 'background:rgba(160,96,208,0.1);border:1px solid rgba(160,96,208,0.2);color:#a060d0;';
                btn.addEventListener('click', () => {
                    const curE = story.variablesState['Energy'] || 0;
                    syncVar(story, 'Energy', Math.max(0, curE - 5));
                    if (opt.femGain > 0) {
                        const curFem = story.variablesState['Femininity'] || 0;
                        syncVar(story, 'Femininity', curFem + opt.femGain);
                    }
                    if (opt.shameGain > 0) {
                        const curShame = story.variablesState['Shame'] || 0;
                        syncVar(story, 'Shame', curShame + opt.shameGain);
                    }
                    body.innerHTML = '';
                    this._buildGrindrRProfile(body, title);
                });
            }
            updateSection.appendChild(btn);
        }

        view.appendChild(updateSection);

        // Change profile type, re-opens the one-time picker so the player is
        // no longer locked into their first pick. The Sissy +2 Fem/+3 Shame
        // bonus is latched (grindr_profile_bonus_claimed), so switching type
        // here never farms stats. foundby VPS report "Can't change grindm profile type".
        const typeSection = document.createElement('div');
        typeSection.style.cssText = 'padding:12px 12px 0;';
        const typeBtn = document.createElement('button');
        typeBtn.style.cssText = 'display:block;width:100%;padding:8px 12px;border-radius:8px;font-size:11px;text-align:center;cursor:pointer;background:rgba(160,96,208,0.08);border:1px solid rgba(160,96,208,0.25);color:#a060d0;';
        typeBtn.textContent = L.get('phone.grindr_change_type', 'Change profile type');
        typeBtn.addEventListener('click', () => {
            body.innerHTML = '';
            this._buildGrindrRProfileSelect(body, title);
        });
        typeSection.appendChild(typeBtn);
        view.appendChild(typeSection);

        // Back button
        const backBtn = document.createElement('button');
        backBtn.className = 'grindr-home-btn';
        backBtn.style.cssText = 'margin:16px auto;display:block;padding:8px 24px;';
        backBtn.textContent = L.get('phone.back');
        backBtn.addEventListener('click', () => { body.innerHTML = ''; this._buildGrindrR(body, title); });
        view.appendChild(backBtn);

        body.appendChild(view);
    }


    // ── JobFinder ─────────────────────────────────────────────────────

    _buildJobFinder(body, title) {
        if (title) title.textContent = 'JobFinder';
        const story = this._story;
        const fem = story ? (story.variablesState['Femininity'] || 0) : 0;
        const iv = (v) => { try { return story ? (story.variablesState[v] || 0) : 0; } catch(e) { return 0; } };
        const ib = (v) => { try { return story ? (story.variablesState[v] === true || story.variablesState[v] === 1) : false; } catch(e) { return false; } };

        const view = document.createElement('div');
        view.className = 'phone-app-view';
        view.innerHTML = '<div class="phone-app-header" style="background:#5cd89c20;border-left:3px solid #5cd89c"><span class="phone-app-view-icon" style="color:#5cd89c">\uD83D\uDCBC</span><div><div class="phone-app-view-name" style="color:#5cd89c">JobFinder</div><div class="phone-app-view-desc">' + L.get('phone.available_positions') + '</div></div></div>';

        // --- Office Card ---
        {
            const discovered = ib('office_discovered');
            const hired = ib('office_hired');
            const stage = iv('office_stage');
            const stageNames = ['Mailroom', 'Front Desk', 'Exec Assistant', 'Personal Assistant'];

            const card = document.createElement('div');
            card.className = 'phone-app-feature-row';
            let statusHtml = '';
            if (hired) {
                const stageName = stageNames[Math.min(stage - 1, stageNames.length - 1)] || 'Hired';
                statusHtml = `<div style="font-size:9px;color:#5cd89c;margin-top:2px">\u2713 ${stageName}</div>`;
            } else if (discovered) {
                statusHtml = `<div style="font-size:9px;color:var(--text-faint);margin-top:2px">${L.get('phone.applied_office')}</div>`;
            }
            card.innerHTML = `
                <span style="font-size:20px">\uD83D\uDCBC</span>
                <div style="flex:1">
                    <div style="font-weight:600">${L.get('phone.job_office')}</div>
                    <div style="font-size:11px;color:var(--text-muted)">${L.get('phone.job_office_desc')} <span style="color:#5cd89c">$50-70/${L.get('phone.shift')}</span></div>
                    ${statusHtml}
                </div>
                ${hired ? '<span style="color:#5cd89c">\u2713</span>' : ''}
            `;
            if (!discovered && !hired && story) {
                const applyBtn = document.createElement('button');
                applyBtn.style.cssText = 'padding:4px 10px;border-radius:6px;background:rgba(92,216,156,0.15);border:1px solid rgba(92,216,156,0.3);color:#5cd89c;font-size:11px;font-weight:600;cursor:pointer;white-space:nowrap;';
                applyBtn.textContent = L.get('phone.apply');
                applyBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    syncVar(story, 'office_discovered', true);
                    body.innerHTML = '';
                    this._buildJobFinder(body, null);
                });
                card.appendChild(applyBtn);
            }
            view.appendChild(card);
        }

        // --- Maid Card (visible at Fem >= 10) ---
        if (fem >= 10) {
            const discovered = ib('maid_discovered');
            const hired = ib('maid_hired');
            const stage = iv('maid_stage');
            const stageNames = ['Standard', 'Premium', 'VIP', 'Personal'];

            const card = document.createElement('div');
            card.className = 'phone-app-feature-row';
            let statusHtml = '';
            if (hired) {
                const stageName = stageNames[Math.min(stage - 1, stageNames.length - 1)] || 'Hired';
                statusHtml = `<div style="font-size:9px;color:#5cd89c;margin-top:2px">\u2713 ${stageName}</div>`;
            } else if (discovered) {
                statusHtml = `<div style="font-size:9px;color:var(--text-faint);margin-top:2px">${L.get('phone.applied_mall')}</div>`;
            }
            card.innerHTML = `
                <span style="font-size:20px">\uD83E\uDDF9</span>
                <div style="flex:1">
                    <div style="font-weight:600">${L.get('phone.job_maid')}</div>
                    <div style="font-size:11px;color:var(--text-muted)">${L.get('phone.job_maid_desc')} <span style="color:#5cd89c">$45-115/${L.get('phone.shift')}</span></div>
                    ${statusHtml}
                </div>
                ${hired ? '<span style="color:#5cd89c">\u2713</span>' : ''}
            `;
            if (!discovered && !hired && story) {
                const applyBtn = document.createElement('button');
                applyBtn.style.cssText = 'padding:4px 10px;border-radius:6px;background:rgba(92,216,156,0.15);border:1px solid rgba(92,216,156,0.3);color:#5cd89c;font-size:11px;font-weight:600;cursor:pointer;white-space:nowrap;';
                applyBtn.textContent = L.get('phone.apply');
                applyBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    syncVar(story, 'maid_discovered', true);
                    body.innerHTML = '';
                    this._buildJobFinder(body, null);
                });
                card.appendChild(applyBtn);
            }
            view.appendChild(card);
        }

        // --- Bar Work (visible if bar_outfit >= 2, floor worker) ---
        if (iv('bar_outfit') >= 2) {
            const card = document.createElement('div');
            card.className = 'phone-app-feature-row';
            card.innerHTML = `
                <span style="font-size:20px">\uD83C\uDF7A</span>
                <div style="flex:1">
                    <div style="font-weight:600">Bar Work</div>
                    <div style="font-size:11px;color:var(--text-muted)">Serve drinks, earn tips. Evening shifts. <span style="color:#5cd89c">$60-75/shift</span></div>
                    <div style="font-size:9px;color:#5cd89c;margin-top:2px">\u2713 Hired</div>
                </div>
                <span style="color:#5cd89c">\u2713</span>
            `;
            view.appendChild(card);
        }

        // --- Club Work (visible if club_job_unlocked) ---
        if (ib('club_job_unlocked')) {
            const card = document.createElement('div');
            card.className = 'phone-app-feature-row';
            card.innerHTML = `
                <span style="font-size:20px">\uD83C\uDF1F</span>
                <div style="flex:1">
                    <div style="font-weight:600">Club Work</div>
                    <div style="font-size:11px;color:var(--text-muted)">Dance, serve VIP. Night shifts only. <span style="color:#5cd89c">$70/shift</span></div>
                    <div style="font-size:9px;color:#5cd89c;margin-top:2px">\u2713 Hired</div>
                </div>
                <span style="color:#5cd89c">\u2713</span>
            `;
            view.appendChild(card);
        }

        // --- Lifeguard (visible if lifeguard hired) ---
        if (iv('lifeguard_stage') >= 1) {
            const card = document.createElement('div');
            card.className = 'phone-app-feature-row';
            card.innerHTML = `
                <span style="font-size:20px">\uD83C\uDFD6\uFE0F</span>
                <div style="flex:1">
                    <div style="font-weight:600">Lifeguard</div>
                    <div style="font-size:11px;color:var(--text-muted)">Watch the beach. Calm shifts. <span style="color:#5cd89c">$40/shift</span></div>
                    <div style="font-size:9px;color:#5cd89c;margin-top:2px">\u2713 Hired</div>
                </div>
                <span style="color:#5cd89c">\u2713</span>
            `;
            view.appendChild(card);
        }

        // --- Locked Placeholder ---
        {
            const card = document.createElement('div');
            card.className = 'phone-app-feature-row dating-locked';
            card.innerHTML = `
                <span style="font-size:20px">\uD83D\uDD12</span>
                <div style="flex:1">
                    <div style="font-weight:600;opacity:0.5">???</div>
                    <div style="font-size:11px;color:var(--text-dim)">More opportunities coming soon...</div>
                </div>
            `;
            view.appendChild(card);
        }

        body.appendChild(view);
    }

    // ── OnlyFems (Content Creator Dashboard) ──────────────────────────

    _buildOnlyFems(body, title) {
        if (title) title.textContent = 'OnlyFems';
        const story = this._story;
        if (!story) return;

        const iv = (v) => { try { return story.variablesState[v] || 0; } catch(e) { return 0; } };
        const ib = (v) => { try { const val = story.variablesState[v]; return val === true || val === 1; } catch(e) { return false; } };
        const setVar = (v, val) => { try { story.variablesState[v] = val; } catch(e) {} };
        const addStat = (v, amount) => {
            const cur = iv(v);
            setVar(v, cur + amount);
            events.emit(Events.STAT_CHANGED, v, cur + amount);
        };

        const subs = iv('onlyfems_subscribers');
        const totalPosts = iv('onlyfems_total_posts');

        // First-time setup screen
        if (subs === 0 && totalPosts === 0) {
            this._buildOnlyFemsSetup(body, title, story, iv, setVar, addStat);
            return;
        }

        // Collect passive income
        const day = iv('Day');
        const lastPayout = iv('onlyfems_last_payout_day');
        const daysPassed = Math.min(3, Math.max(0, day - lastPayout));
        let passiveIncome = 0;
        if (daysPassed > 0 && subs > 0) {
            // Sublinear passive curve (foundby:VPS 0.2.8.4.1 "money from OnlyFems
            // is insanely high"): the old `0.5 * subs` LINEAR formula plus a
            // perverse `subs >= 250` 1.5x accelerator ran to ~$750/day at 1000
            // subs (and kept doubling) against ~$28/day rent, so money stopped
            // mattering. sqrt keeps lategame paying WELL and still rewards growth
            // — ~$156/day at 500 subs, ~$221 at 1000, ~$313 at 2000 — but never
            // runs away. Idle pages also bleed subs overnight (Sleep.ink churn).
            passiveIncome = Math.floor(7 * Math.sqrt(subs) * daysPassed);
            setVar('onlyfems_earnings', iv('onlyfems_earnings') + passiveIncome);
            setVar('onlyfems_last_payout_day', day);
        }

        const earnings = iv('onlyfems_earnings');
        const fem = iv('Femininity');
        const energy = iv('Energy');
        const fame = iv('sissy_fame');
        const chastity = ib('wearing_chastity');
        const hasPhotographer = iv('photographer_shoots') >= 5;

        // Daily post cap (foundby:Alpha Rabbit): unlimited energy-only posts
        // out-earned every job by day 3. Day-keyed counters self-reset on a
        // new Day — no overnight hook needed.
        const today = iv('Day');
        const postsToday = iv('onlyfems_post_day') === today ? iv('onlyfems_posts_today') : 0;
        const postCap = hasPhotographer ? 3 : 2;
        const dailyCapped = postsToday >= postCap;
        // Tips scale with the audience: a fresh page with 5 subs doesn't tip
        // like a 100-sub page. Full range from 100 subs up.
        const subsMult = Math.min(1, 0.3 + (0.7 * subs) / 100);

        // Milestones (L10n'd via phone.onlyfems.milestone.<key>.title|desc)
        const MILESTONES = [
            { threshold: 10,  title: L.get('phone.onlyfems.milestone.first_fans.title',  'First Fans'),    desc: L.get('phone.onlyfems.milestone.first_fans.desc',  'Passive income unlocked!') },
            { threshold: 50,  title: L.get('phone.onlyfems.milestone.rising_star.title', 'Rising Star'),   desc: L.get('phone.onlyfems.milestone.rising_star.desc', 'Custom requests unlocked!') },
            { threshold: 100, title: L.get('phone.onlyfems.milestone.top_creator.title', 'Top Creator'),   desc: L.get('phone.onlyfems.milestone.top_creator.desc', 'Brand deals incoming...') },
            { threshold: 250, title: L.get('phone.onlyfems.milestone.verified.title',    'Verified!'),     desc: L.get('phone.onlyfems.milestone.verified.desc',    'Verified badge, top creator status!') },
        ];
        const nextMilestone = MILESTONES.find(m => subs < m.threshold);

        // Content tiers (matching Unity OnlyFemsData.cs)
        const TIERS = [
            { tier: 1, name: L.get('phone.onlyfems.tier.1.name', 'Cute Selfie'),       desc: L.get('phone.onlyfems.tier.1.desc', 'Soft lighting, subtle filter. Safe and sweet.'),         reqFem: 15, chastity: false, reqSubs: 0,  energy: 10, subsMin: 2,  subsMax: 5,  tipsMin: 10, tipsMax: 20,  fem: 1, shame: 0, sub: 0, arousal: 0, fame: 1,
              narrative: L.get('phone.onlyfems.tier.1.narrative',         'A soft selfie with a hint of makeup. Comments trickle in: "Cute!" "More please!"'),
              narrativeFamous: L.get('phone.onlyfems.tier.1.narrative_famous', 'Even a simple selfie blows up now. Your fans eat up everything you post.') },
            { tier: 2, name: L.get('phone.onlyfems.tier.2.name', 'Outfit of the Day'), desc: L.get('phone.onlyfems.tier.2.desc', 'Full outfit, cute pose. Showing off your style.'),       reqFem: 25, chastity: false, reqSubs: 0,  energy: 10, subsMin: 5,  subsMax: 10, tipsMin: 20, tipsMax: 40,  fem: 1, shame: 1, sub: 0, arousal: 0, fame: 1,
              narrative: L.get('phone.onlyfems.tier.2.narrative',         'Full outfit, posed in the mirror. Skirt, thigh-highs, the works. Tips start rolling in.'),
              narrativeFamous: L.get('phone.onlyfems.tier.2.narrative_famous', 'Your outfit posts are setting trends now.') },
            { tier: 3, name: L.get('phone.onlyfems.tier.3.name', 'Lingerie Tease'),    desc: L.get('phone.onlyfems.tier.3.desc', 'Lace, silk, and not much else. Getting spicy.'),         reqFem: 35, chastity: false, reqSubs: 0,  energy: 15, subsMin: 8,  subsMax: 15, tipsMin: 30, tipsMax: 60,  fem: 2, shame: 2, sub: 0, arousal: 3, fame: 2,
              narrative: L.get('phone.onlyfems.tier.3.narrative',         'Pink lace, suggestive angles, face half-hidden. The tips explode. You feel exposed... and excited.'),
              narrativeFamous: L.get('phone.onlyfems.tier.3.narrative_famous', 'Your lingerie drops are events now. Tips flood in within minutes.') },
            { tier: 4, name: L.get('phone.onlyfems.tier.4.name', 'Cage Content'),      desc: L.get('phone.onlyfems.tier.4.desc', "Show them what's locked away. The community goes wild."), reqFem: 35, chastity: true,  reqSubs: 0,  energy: 15, subsMin: 12, subsMax: 20, tipsMin: 40, tipsMax: 70,  fem: 2, shame: 2, sub: 2, arousal: 3, fame: 3,
              narrative: L.get('phone.onlyfems.tier.4.narrative',         'A close-up of pink steel. Caption: "Locked and loving it." The niche community goes absolutely wild.'),
              narrativeFamous: L.get('phone.onlyfems.tier.4.narrative_famous', 'Your cage content is legendary. Other creators reference you.') },
            { tier: 5, name: L.get('phone.onlyfems.tier.5.name', 'Custom Request'),    desc: L.get('phone.onlyfems.tier.5.desc', 'A subscriber paid extra for something specific...'),     reqFem: 45, chastity: false, reqSubs: 50, energy: 20, subsMin: 5,  subsMax: 8,  tipsMin: 60, tipsMax: 120, fem: 2, shame: 3, sub: 1, arousal: 5, fame: 2,
              narrative: L.get('phone.onlyfems.tier.5.narrative',         'You follow the instructions to the letter. The requester tips generously. "Perfect."'),
              narrativeFamous: L.get('phone.onlyfems.tier.5.narrative_famous', 'Custom requests from whales. The things they ask for... and the money they pay.') },
        ];

        const CUSTOM_REQUESTS = [
            L.get('phone.onlyfems.request.1', '"Wear the pink set and write my name on your thigh."'),
            L.get('phone.onlyfems.request.2', '"I want a video of you putting on lipstick. Slowly."'),
            L.get('phone.onlyfems.request.3', '"Bend over in those stockings. I\'ll tip $50 extra for heels."'),
            L.get('phone.onlyfems.request.4', '"Show me your morning routine. The feminine version."'),
            L.get('phone.onlyfems.request.5', '"Cage check + panty pull. $100 if you moan."'),
            L.get('phone.onlyfems.request.6', '"Dance to this song in lingerie. I\'ll send the playlist."'),
            L.get('phone.onlyfems.request.7', '"Write \'good girl\' on your mirror and pose next to it."'),
            L.get('phone.onlyfems.request.8', '"I want to see you try on three outfits. Rate them for me."'),
        ];

        const view = document.createElement('div');
        view.className = 'phone-app-view';

        // Header + Stats
        view.innerHTML = `
            <div class="phone-app-header" style="background:#c470e020;border-left:3px solid #c470e0">
                <span class="phone-app-view-icon" style="color:#c470e0">\uD83D\uDC8E</span>
                <div>
                    <div class="phone-app-view-name" style="color:#c470e0">OnlyFems ${subs >= 250 ? '\u2713 Verified' : ''}</div>
                    <div class="phone-app-view-desc">Your creator dashboard</div>
                </div>
            </div>
            <div class="dating-stats-grid">
                <div class="dating-stat-card"><div class="dating-stat-value">${subs}</div><div class="dating-stat-label">Subscribers</div></div>
                <div class="dating-stat-card"><div class="dating-stat-value">$${earnings}</div><div class="dating-stat-label">Earnings</div></div>
                <div class="dating-stat-card"><div class="dating-stat-value">${totalPosts}</div><div class="dating-stat-label">Posts</div></div>
                <div class="dating-stat-card"><div class="dating-stat-value">${nextMilestone ? subs + '/' + nextMilestone.threshold : '\u2B50'}</div><div class="dating-stat-label">${nextMilestone ? nextMilestone.title : 'Maxed'}</div></div>
            </div>
            ${passiveIncome > 0 ? `<div class="of-passive-income">+$${passiveIncome} passive income collected</div>` : ''}
            ${hasPhotographer ? '<div style="font-size:10px;color:#c470e0;text-align:center;margin:4px 0;font-style:italic">\uD83D\uDCF7 Professional photographer bonus active (2x subs)</div>' : ''}
        `;

        // Withdraw button
        if (earnings > 0) {
            // Owner skim on withdrawal: mirror of Ink master_cut() — the app
            // payout was the only income source bypassing the 20% cut
            // (foundby:Alpha Rabbit). Label shows the cut up front so the
            // deduction never reads as a save bug.
            const ownerCutActive = iv('master_intensity') >= 3 && iv('MasterRelationship') >= 7 && !ib('moved_out');
            const ownerCutPreview = ownerCutActive && earnings > 0 ? Math.max(1, Math.floor(earnings / 5)) : 0;
            const withdrawBtn = document.createElement('button');
            withdrawBtn.className = 'of-withdraw-btn';
            withdrawBtn.textContent = ownerCutPreview > 0
                ? `Withdraw $${earnings - ownerCutPreview} (${L.get('phone.onlyfems.his_cut', 'his cut')}: $${ownerCutPreview})`
                : `Withdraw $${earnings}`;
            withdrawBtn.addEventListener('click', () => {
                const cur = iv('onlyfems_earnings');
                if (cur <= 0) return;
                let cut = 0;
                if (iv('master_intensity') >= 3 && iv('MasterRelationship') >= 7 && !ib('moved_out')) {
                    cut = Math.max(1, Math.floor(cur / 5));
                    setVar('master_earnings_cut', iv('master_earnings_cut') + cut);
                }
                addStat('Money', cur - cut);
                setVar('onlyfems_earnings', 0);
                body.innerHTML = '';
                this._buildOnlyFems(body, null);
            });
            view.appendChild(withdrawBtn);
        }

        // Post Content section
        const postHeader = document.createElement('div');
        postHeader.className = 'flintr-msg-header';
        postHeader.textContent = L.get('phone.post_content');
        postHeader.style.marginTop = '12px';
        view.appendChild(postHeader);

        // Owner Route soft-unlock for Tier 4 Cage Content: when Master has
        // mandated daily cage posts (master_intensity >= 2) AND the player
        // is currently caged, the Fem-35 gate is bypassed so the player can
        // obey the Master's order without grinding feminization first.
        // Foundby:AKinkyMutt 2026-04-30, without this the rule forces a
        // Fem-fast-track or disobedience choice that the player can't avoid.
        // BUG fix 2026-05-13 (foundby:The Cook): was `ib('master_intensity')`,
        // but `ib` is the boolean reader (val === true || val === 1). For
        // Landlord (=2) / Owner (=3) it returned false, so `false >= 2` = false
        // and the bypass never fired. Use `iv` (integer reader) instead.
        const masterCageMandate = iv('master_intensity') >= 2 && chastity;

        for (const tier of TIERS) {
            const cageContentTier = tier.tier === 4;
            const femGateOk = (cageContentTier && masterCageMandate) || fem >= tier.reqFem;
            const meetsReqs = femGateOk && (!tier.chastity || chastity) && subs >= tier.reqSubs;
            const hasEnergy = energy >= tier.energy;
            const locked = !meetsReqs;
            const disabled = (!hasEnergy || dailyCapped) && meetsReqs;

            const card = document.createElement('div');
            card.className = 'flintr-msg-card' + (locked || disabled ? ' dating-locked' : '');

            let lockReason = '';
            if (!femGateOk) lockReason = `Fem ${tier.reqFem}`;
            else if (tier.chastity && !chastity) lockReason = L.get('phone.wear_chastity');
            else if (tier.reqSubs > 0 && subs < tier.reqSubs) lockReason = `${tier.reqSubs} ${L.get('phone.subs')}`;
            else if (dailyCapped) lockReason = L.get('phone.onlyfems.daily_cap', 'The algorithm buries spam. More tomorrow.');
            else if (disabled) lockReason = `${L.get('phone.not_enough_energy')} (${tier.energy})`;

            const statPreview = [];
            if (tier.fem > 0) statPreview.push(`+${tier.fem} Fem`);
            if (tier.shame > 0) statPreview.push(`+${tier.shame} Shame`);
            if (tier.sub > 0) statPreview.push(`+${tier.sub} Sub`);

            card.innerHTML = `
                <div class="flintr-msg-sender">${tier.name}</div>
                <div class="flintr-msg-preview">${locked || disabled ? lockReason : `E:${tier.energy} | +${tier.subsMin}-${tier.subsMax} subs | $${Math.max(5, Math.round(tier.tipsMin * subsMult))}-${Math.max(5, Math.round(tier.tipsMax * subsMult))}`}</div>
                ${!locked && !disabled ? `<div class="flintr-msg-tier" style="color:#c470e0">${statPreview.join(' ')}</div>` : ''}
            `;

            if (!locked && !disabled) {
                card.style.cursor = 'pointer';
                card.addEventListener('click', () => {
                    // Energy cost
                    const curEnergy = iv('Energy');
                    setVar('Energy', Math.max(0, curEnergy - tier.energy));
                    events.emit(Events.STAT_CHANGED, 'Energy', Math.max(0, curEnergy - tier.energy));

                    // Calculate subscriber gain
                    const fameBonus = fame >= 50 ? 1.5 : 1;
                    const photoBonus = hasPhotographer ? 2 : 1;
                    let subsGain = Math.floor(Math.random() * (tier.subsMax - tier.subsMin + 1)) + tier.subsMin;
                    subsGain = Math.round(subsGain * fameBonus * photoBonus);
                    const rawTips = Math.floor(Math.random() * (tier.tipsMax - tier.tipsMin + 1)) + tier.tipsMin;
                    const tips = Math.max(5, Math.round(rawTips * subsMult));

                    // Daily cap bookkeeping (day-keyed, self-resetting)
                    setVar('onlyfems_post_day', today);
                    setVar('onlyfems_posts_today', postsToday + 1);

                    // Apply gains
                    const newSubs = subs + subsGain;
                    setVar('onlyfems_subscribers', newSubs);
                    setVar('onlyfems_earnings', iv('onlyfems_earnings') + tips);
                    setVar('onlyfems_total_posts', totalPosts + 1);

                    // Track highest tier
                    const currentTier = iv('onlyfems_tier_unlocked');
                    if (tier.tier > currentTier) setVar('onlyfems_tier_unlocked', tier.tier);

                    // Owner-route daily cage post (v0.2.5): Tier 4 "Cage Content"
                    // counts toward Master's daily posting requirement. Streak
                    // increments; overnight in Home.ink checks the flag.
                    if (tier.tier === 4 && ib('wearing_chastity')) {
                        setVar('cage_post_done_today', true);
                        setVar('cage_post_streak', iv('cage_post_streak') + 1);
                    }

                    // Stat changes
                    if (tier.fem > 0) _appAddFem(iv, addStat, tier.fem);
                    if (tier.shame > 0) _appAddShame(iv, addStat, tier.shame);
                    if (tier.sub > 0) _appAddSub(iv, addStat, tier.sub);
                    if (tier.arousal > 0) addStat('Arousal', tier.arousal);
                    if (tier.fame > 0) _appAddFame(iv, setVar, tier.fame);

                    // Show post result
                    body.innerHTML = '';
                    const resultView = document.createElement('div');
                    resultView.className = 'phone-app-view';

                    const narrative = (fame >= 50 && tier.narrativeFamous) ? tier.narrativeFamous : tier.narrative;
                    let customReq = '';
                    if (tier.tier === 5) {
                        customReq = `<div style="font-size:11px;color:var(--text-muted);margin:8px 0;font-style:italic;padding:8px;background:rgba(196,112,224,0.1);border-radius:6px">${CUSTOM_REQUESTS[Math.floor(Math.random() * CUSTOM_REQUESTS.length)]}</div>`;
                    }

                    resultView.innerHTML = `
                        <div style="text-align:center;padding:16px">
                            <div style="font-size:14px;font-weight:600;color:#c470e0;margin-bottom:8px">${L.get('phone.posted')}</div>
                            <div style="font-size:12px;color:var(--text-muted);margin-bottom:12px;line-height:1.5">${narrative}</div>
                            ${customReq}
                            <div class="dating-stats-grid" style="margin:12px 0">
                                <div class="dating-stat-card"><div class="dating-stat-value">+${subsGain}</div><div class="dating-stat-label">${L.get('phone.new_subs')}</div></div>
                                <div class="dating-stat-card"><div class="dating-stat-value">$${tips}</div><div class="dating-stat-label">${L.get('phone.tips')}</div></div>
                            </div>
                            ${hasPhotographer ? '<div style="font-size:10px;color:#c470e0;font-style:italic">\uD83D\uDCF7 Professional bonus applied</div>' : ''}
                        </div>
                    `;

                    const postMoreBtn = document.createElement('button');
                    postMoreBtn.className = 'of-withdraw-btn';
                    postMoreBtn.style.background = 'rgba(196,112,224,0.15)';
                    postMoreBtn.textContent = L.get('phone.post_more');
                    postMoreBtn.addEventListener('click', () => { body.innerHTML = ''; this._buildOnlyFems(body, null); });
                    resultView.appendChild(postMoreBtn);

                    const dashBtn = document.createElement('button');
                    dashBtn.className = 'of-withdraw-btn';
                    dashBtn.style.cssText = 'background:rgba(100,100,100,0.15);margin-top:6px;';
                    dashBtn.textContent = L.get('phone.dashboard');
                    dashBtn.addEventListener('click', () => { body.innerHTML = ''; this._buildOnlyFems(body, null); });
                    resultView.appendChild(dashBtn);

                    body.appendChild(resultView);
                });
            }
            view.appendChild(card);
        }

        // Inbox DMs section (2-4 per day, gated by subscriber count)
        if (subs >= 5) {
            const DM_TEMPLATES = [
                { sender: 'PinkLover99', preview: L.get('phone.onlyfems.dm.pinklover99.preview', "You're literally goals..."), full: L.get('phone.onlyfems.dm.pinklover99.full', "You're literally goals. I started my own page because of you."), minSubs: 0, money: 0, subsGain: 3, fame: 0, shame: 0, fem: 1 },
                { sender: 'SissyFan_xx', preview: L.get('phone.onlyfems.dm.sissyfan_xx.preview', 'Your latest post had me...'), full: L.get('phone.onlyfems.dm.sissyfan_xx.full', "Your latest post had me shaking. You're so pretty it's unfair."), minSubs: 10, money: 0, subsGain: 2, fame: 0, shame: 0, fem: 1 },
                { sender: 'NewHere_2024', preview: L.get('phone.onlyfems.dm.newhere_2024.preview', 'How do you stay so confident?'), full: L.get('phone.onlyfems.dm.newhere_2024.full', 'How do you stay so confident posting this stuff? Any tips?'), minSubs: 5, money: 0, subsGain: 2, fame: 1, shame: 0, fem: 1 },
                { sender: 'GenerousDaddy', preview: L.get('phone.onlyfems.dm.generousdaddy.preview', 'Something for being a good girl'), full: L.get('phone.onlyfems.dm.generousdaddy.full', 'No request. Just wanted to spoil you. Keep being pretty.'), minSubs: 15, money: 40, subsGain: 0, fame: 0, shame: 1, fem: 0 },
                { sender: 'WhaleAlert', preview: L.get('phone.onlyfems.dm.whalealert.preview', 'Big tip incoming...'), full: L.get('phone.onlyfems.dm.whalealert.full', "Your cage content changed my life. Here's $150."), minSubs: 30, money: 75, subsGain: 0, fame: 1, shame: 0, fem: 0 },
                { sender: 'PrivateCollector', preview: L.get('phone.onlyfems.dm.privatecollector.preview', 'I have a special request...'), full: L.get('phone.onlyfems.dm.privatecollector.full', "I'll pay $120 for a custom set. Pink lingerie, kneeling pose."), minSubs: 50, money: 60, subsGain: 3, fame: 1, shame: 2, fem: 1, energyCost: 15 },
                { sender: 'ArtDirector', preview: L.get('phone.onlyfems.dm.artdirector.preview', 'Artistic commission...'), full: L.get('phone.onlyfems.dm.artdirector.full', 'I run an art blog featuring beautiful sissies. $180 for a themed photoset.'), minSubs: 50, money: 90, subsGain: 5, fame: 2, shame: 2, fem: 1, energyCost: 15 },
                { sender: 'TopCreator_Mia', preview: L.get('phone.onlyfems.dm.topcreator_mia.preview', 'Collab? Our audiences would love it'), full: L.get('phone.onlyfems.dm.topcreator_mia.full', 'Hey babe! I have 2K subs. Cross-promote?'), minSubs: 25, money: 0, subsGain: 20, fame: 3, shame: 1, fem: 1 },
                { sender: 'SissyGlam Co.', preview: L.get('phone.onlyfems.dm.sissyglam_co.preview', 'Paid sponsorship opportunity'), full: L.get('phone.onlyfems.dm.sissyglam_co.full', 'Wear our new pink collection, tag us, $300.'), minSubs: 100, money: 150, subsGain: 15, fame: 5, shame: 1, fem: 1 },
                { sender: 'LaceDream Lingerie', preview: L.get('phone.onlyfems.dm.lacedream_lingerie.preview', 'Brand ambassador offer'), full: L.get('phone.onlyfems.dm.lacedream_lingerie.full', 'We want you as brand ambassador, $500 for 3 posts.'), minSubs: 200, money: 250, subsGain: 25, fame: 8, shame: 2, fem: 2 },
            ];

            // Daily DM generation (seeded by day)
            const eligible = DM_TEMPLATES.filter(d => subs >= d.minSubs);
            const dmCount = Math.min(eligible.length, 2 + (day % 3));
            const dayDMs = [];
            const seed = day * 7 + 13;
            for (let i = 0; i < dmCount && i < eligible.length; i++) {
                dayDMs.push(eligible[(seed + i * 3) % eligible.length]);
            }

            // Check which were already accepted today. Namespace by active
            // save slot so daily-DM dedup doesn't bleed across saves that
            // happen to share the same Ink Day.
            const ofSlot = (typeof window !== 'undefined' && window._save) ? window._save.activeSlot : 0;
            const acceptedKey = `PnO_OF_DMs_s${ofSlot}_${day}`;
            const accepted = new Set(JSON.parse(localStorage.getItem(acceptedKey) || '[]'));
            const pending = dayDMs.filter(d => !accepted.has(d.sender));

            if (pending.length > 0) {
                const inboxHeader = document.createElement('div');
                inboxHeader.className = 'flintr-msg-header';
                inboxHeader.textContent = `Inbox (${pending.length})`;
                inboxHeader.style.marginTop = '12px';
                view.appendChild(inboxHeader);

                for (const dm of pending) {
                    const needsEnergy = dm.energyCost > 0;
                    const hasEnoughEnergy = !needsEnergy || iv('Energy') >= dm.energyCost;

                    const dmCard = document.createElement('div');
                    dmCard.className = 'flintr-msg-card' + (!hasEnoughEnergy ? ' dating-locked' : '');
                    dmCard.style.cursor = hasEnoughEnergy ? 'pointer' : 'default';
                    const rewardText = dm.money > 0 ? `$${dm.money}` : dm.subsGain > 0 ? `+${dm.subsGain} subs` : '';
                    dmCard.innerHTML = `
                        <div class="flintr-msg-sender">${dm.sender}</div>
                        <div class="flintr-msg-preview">${dm.preview}${needsEnergy ? ` (E:${dm.energyCost})` : ''}</div>
                        ${!hasEnoughEnergy ? '<div class="flintr-msg-tier" style="color:var(--text-dim)">Not enough energy</div>' : rewardText ? `<div class="flintr-msg-tier" style="color:#c470e0">${rewardText}</div>` : ''}
                    `;
                    if (hasEnoughEnergy) {
                        dmCard.addEventListener('click', () => {
                            // Energy cost for custom requests
                            if (dm.energyCost > 0) {
                                const curE = iv('Energy');
                                setVar('Energy', Math.max(0, curE - dm.energyCost));
                                events.emit(Events.STAT_CHANGED, 'Energy', Math.max(0, curE - dm.energyCost));
                            }
                            if (dm.money > 0) setVar('onlyfems_earnings', iv('onlyfems_earnings') + dm.money);
                            if (dm.subsGain > 0) setVar('onlyfems_subscribers', iv('onlyfems_subscribers') + dm.subsGain);
                            if (dm.shame > 0) _appAddShame(iv, addStat, dm.shame);
                            if (dm.fem > 0) _appAddFem(iv, addStat, dm.fem);
                            if (dm.fame > 0) _appAddFame(iv, setVar, dm.fame);
                            accepted.add(dm.sender);
                            localStorage.setItem(acceptedKey, JSON.stringify([...accepted]));
                            body.innerHTML = '';
                            this._buildOnlyFems(body, null);
                        });
                    }
                    view.appendChild(dmCard);
                }
            }
        }

        // Delete page button
        const deleteBtn = document.createElement('button');
        deleteBtn.style.cssText = 'display:block;margin:16px auto 8px;padding:6px 16px;background:rgba(200,60,60,0.15);border:1px solid rgba(200,60,60,0.3);border-radius:6px;color:#c83c3c;font-size:10px;cursor:pointer;';
        deleteBtn.textContent = L.get('phone.delete_page');
        deleteBtn.addEventListener('click', () => {
            setVar('onlyfems_installed', false);
            setVar('onlyfems_subscribers', 0);
            setVar('onlyfems_total_posts', 0);
            setVar('onlyfems_earnings', 0);
            setVar('onlyfems_tier_unlocked', 0);
            this._showView('home');
        });
        view.appendChild(deleteBtn);

        body.appendChild(view);
    }

    _buildOnlyFemsSetup(body, title, story, iv, setVar, addStat) {
        const view = document.createElement('div');
        view.className = 'phone-app-view';

        const sissyFollowers = iv('sissy_followers');
        const seed = Math.min(Math.floor(sissyFollowers / 10), 5);
        const startSubs = Math.max(1, seed);

        view.innerHTML = `
            <div class="phone-app-header" style="background:#c470e020;border-left:3px solid #c470e0">
                <span class="phone-app-view-icon" style="color:#c470e0">\uD83D\uDC8E</span>
                <div>
                    <div class="phone-app-view-name" style="color:#c470e0">OnlyFems</div>
                    <div class="phone-app-view-desc">${L.get('phone.setup_page')}</div>
                </div>
            </div>
            <div style="padding:16px;text-align:center">
                <div style="font-size:13px;font-weight:600;color:var(--text);margin-bottom:8px">${L.get('phone.create_page')}</div>
                <div style="font-size:11px;color:var(--text-muted);margin-bottom:16px;line-height:1.5">${L.get('phone.onlyfems_desc')}</div>
                ${sissyFollowers > 0 && seed > 0 ? `<div style="font-size:10px;color:#c470e0;margin-bottom:12px;font-style:italic">Your ${sissyFollowers} SissySocial followers = ${seed} starting subscribers!</div>` : ''}
            </div>
        `;

        const goLiveBtn = document.createElement('button');
        goLiveBtn.className = 'of-withdraw-btn';
        goLiveBtn.style.background = 'rgba(196,112,224,0.2)';
        goLiveBtn.textContent = '\uD83D\uDC8E ' + L.get('phone.go_live');
        goLiveBtn.addEventListener('click', () => {
            setVar('onlyfems_subscribers', startSubs);
            setVar('onlyfems_last_payout_day', iv('Day'));
            _appAddShame(iv, addStat, 2);
            _appAddFem(iv, addStat, 1);
            body.innerHTML = '';
            this._buildOnlyFems(body, null);
        });
        view.appendChild(goLiveBtn);

        const notReadyBtn = document.createElement('button');
        notReadyBtn.className = 'of-withdraw-btn';
        notReadyBtn.style.cssText = 'background:rgba(100,100,100,0.15);margin-top:6px;';
        notReadyBtn.textContent = L.get('phone.not_ready');
        notReadyBtn.addEventListener('click', () => this._showView('home'));
        view.appendChild(notReadyBtn);

        body.appendChild(view);
    }

    // ── SissySocial (Social Media Feed) ───────────────────────────────

    _buildSissySocial(body, title) {
        if (title) title.textContent = 'SissySocial';
        const story = this._story;
        if (!story) return;

        const iv = (v) => { try { return story.variablesState[v] || 0; } catch(e) { return 0; } };
        const ib = (v) => { try { const val = story.variablesState[v]; return val === true || val === 1; } catch(e) { return false; } };
        const setVar = (v, val) => { try { story.variablesState[v] = val; } catch(e) {} };
        const addStat = (v, amount) => {
            const cur = iv(v);
            setVar(v, cur + amount);
            events.emit(Events.STAT_CHANGED, v, cur + amount);
        };

        // Migration: old saves with sissy_account_created but no sissysocial_installed
        if (ib('sissy_account_created') && !ib('sissysocial_installed')) {
            setVar('sissysocial_installed', true);
        }

        // First-time setup screen
        if (!ib('sissy_account_created')) {
            this._buildSissySocialSetup(body, title, story, iv, setVar, addStat);
            return;
        }

        const followers = iv('sissy_followers');
        const fame = iv('sissy_fame');
        const posts = iv('sissy_posts');
        const fem = iv('Femininity');
        const energy = iv('Energy');
        const chastity = ib('wearing_chastity');

        // Post types (matching Unity SissySocialData.cs)
        const POST_TYPES = [
            { id: 1, name: L.get('phone.sissysocial.post.mirror_selfie.name', 'Mirror Selfie'), desc: L.get('phone.sissysocial.post.mirror_selfie.desc', 'Soft lighting, subtle makeup. Safe and sweet.'), reqFem: 0, chastity: false, energy: 10,
              followMin: 2, followMax: 5, followMinFamous: 5, followMaxFamous: 12,
              fem: 1, shame: 0, sub: 0, arousal: 0, fame: 0,
              narrative: L.get('phone.sissysocial.post.mirror_selfie.narrative', 'A simple selfie. Subtle makeup. Soft lighting.\nComments: "Cute!" "Goals!" "Looking pretty!"'),
              narrativeFamous: L.get('phone.sissysocial.post.mirror_selfie.narrative_famous', 'Comments flood in. Your growing fame means even a simple post gets attention.') },
            { id: 2, name: L.get('phone.sissysocial.post.ootd.name', 'Outfit of the Day'), desc: L.get('phone.sissysocial.post.ootd.desc', 'Full outfit, cute pose. Showing off your style.'), reqFem: 20, chastity: false, energy: 10,
              followMin: 5, followMax: 12, followMinFamous: 12, followMaxFamous: 25,
              fem: 2, shame: 2, sub: 0, arousal: 0, fame: 0,
              narrative: L.get('phone.sissysocial.post.ootd.narrative', 'Full outfit. Pose. Filter. Post.\nComments flood in: "Slay!" "Where did you get that?!" "Daddy approves"'),
              narrativeFamous: L.get('phone.sissysocial.post.ootd.narrative_famous', 'Your outfit posts set trends now. Everyone screenshots and shares.') },
            { id: 3, name: L.get('phone.sissysocial.post.spicy.name', 'Something Spicy'), desc: L.get('phone.sissysocial.post.spicy.desc', 'Lingerie. Suggestive pose. Face partially hidden.'), reqFem: 30, chastity: false, energy: 10,
              followMin: 8, followMax: 15, followMinFamous: 15, followMaxFamous: 30,
              fem: 2, shame: 3, sub: 0, arousal: 5, fame: 2,
              narrative: L.get('phone.sissysocial.post.spicy.narrative', 'Lingerie. Suggestive pose. Face partially hidden.\nThe post EXPLODES. Likes, shares, saves.'),
              narrativeFamous: L.get('phone.sissysocial.post.spicy.narrative_famous', 'Your spicy posts break the algorithm. The community goes feral.') },
            { id: 4, name: L.get('phone.sissysocial.post.cage_check.name', 'Cage Check'), desc: L.get('phone.sissysocial.post.cage_check.desc', "Show them what's locked away. The community goes wild."), reqFem: 0, chastity: true, energy: 10,
              followMin: 10, followMax: 20, followMinFamous: 15, followMaxFamous: 30,
              fem: 3, shame: 3, sub: 2, arousal: 0, fame: 3,
              narrative: L.get('phone.sissysocial.post.cage_check.narrative', 'A photo of your locked cage. Pink and pathetic.\nThe sissy community goes WILD.'),
              narrativeFamous: L.get('phone.sissysocial.post.cage_check.narrative_famous', 'Your cage content is legendary. Other creators reference you.') },
        ];

        const BROWSE_SNIPPETS = [
            L.get('phone.sissysocial.browse.1', 'Sissies posing, training, serving. Makeup tutorials. Outfit inspiration. Transformation timelines.'),
            L.get('phone.sissysocial.browse.2', '"Rate my outfit!" Comments: 10/10, goals, wife material.'),
            L.get('phone.sissysocial.browse.3', 'A popular creator shares their morning routine. All pink. All pretty.'),
            L.get('phone.sissysocial.browse.4', 'Cage check threads trending. Hundreds of sissies showing off their locks.'),
            L.get('phone.sissysocial.browse.5', '"First time in public en femme!" The courage. The comments. The validation.'),
        ];

        const BROWSE_DEEP_SNIPPETS = [
            L.get('phone.sissysocial.browse_deep.1', 'Training videos. Hypno edits. Cage checks.\n"Real sissies lock up!" "Obey your superior!" "Girls don\'t cum!"'),
            L.get('phone.sissysocial.browse_deep.2', 'A sissy maid shares her daily routine. Every task assigned by her owner.'),
            L.get('phone.sissysocial.browse_deep.3', 'Obedience challenges trending. Each level more humiliating than the last.'),
            L.get('phone.sissysocial.browse_deep.4', 'A dom posts rules for his sissies. Strict but... the comments are all hearts.'),
            L.get('phone.sissysocial.browse_deep.5', '"If you\'re not locked, are you even trying?" 5K likes. You keep scrolling.'),
        ];

        const FEED_POSTS = [
            { user: 'PinkiePrincess', text: L.get('phone.sissysocial.feed.1.text', 'New outfit day! Feeling cute \u2728'), likes: 42 },
            { user: 'SissyFan_xx', text: L.get('phone.sissysocial.feed.2.text', 'Who else can\'t stop scrolling? \uD83D\uDE0D'), likes: 18 },
            { user: 'TopSissy_Mia', text: L.get('phone.sissysocial.feed.3.text', 'Just hit 10k followers!! Thank you all \u2764\uFE0F'), likes: 234 },
            { user: 'NewHere_2024', text: L.get('phone.sissysocial.feed.4.text', 'First post ever... be gentle \uD83D\uDE33'), likes: 7 },
            { user: 'GlamQueen', text: L.get('phone.sissysocial.feed.5.text', 'Cage check \uD83D\uDD12 Day 14 and counting...'), likes: 89 },
        ];

        const view = document.createElement('div');
        view.className = 'phone-app-view';
        view.innerHTML = `
            <div class="phone-app-header" style="background:#e88cb820;border-left:3px solid #e88cb8">
                <span class="phone-app-view-icon" style="color:#e88cb8">\uD83E\uDD33</span>
                <div>
                    <div class="phone-app-view-name" style="color:#e88cb8">SissySocial</div>
                    <div class="phone-app-view-desc">${followers} followers \u00B7 ${posts} posts${fame > 0 ? ` \u00B7 ${fame} fame` : ''}</div>
                </div>
            </div>
            ${fame >= 50 ? '<div style="font-size:10px;color:#e88cb8;text-align:center;margin:4px 0;font-style:italic">You\'re becoming well-known in sissy circles.</div>' : ''}
        `;

        // Navigation buttons
        const navGrid = document.createElement('div');
        navGrid.style.cssText = 'display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;padding:8px 12px;';

        const createNavBtn = (label, icon, onClick) => {
            const btn = document.createElement('button');
            btn.style.cssText = 'padding:8px 4px;border-radius:8px;background:rgba(232,140,184,0.1);border:1px solid rgba(232,140,184,0.2);color:#e88cb8;font-size:11px;font-weight:600;cursor:pointer;text-align:center;';
            btn.innerHTML = `<div style="font-size:16px">${icon}</div><div>${label}</div>`;
            btn.addEventListener('click', onClick);
            return btn;
        };

        navGrid.appendChild(createNavBtn(L.get('phone.browse'), '\uD83D\uDCF1', () => {
            body.innerHTML = '';
            this._buildSissySocialBrowse(body, title, story, iv, setVar, addStat, BROWSE_SNIPPETS, BROWSE_DEEP_SNIPPETS);
        }));
        navGrid.appendChild(createNavBtn(L.get('phone.post'), '\uD83D\uDCDD', () => {
            body.innerHTML = '';
            this._buildSissySocialPost(body, title, story, iv, ib, setVar, addStat, POST_TYPES);
        }));
        navGrid.appendChild(createNavBtn(L.get('phone.dms'), '\uD83D\uDCE9', () => {
            body.innerHTML = '';
            this._buildSissySocialDMs(body, title, story, iv, setVar, addStat);
        }));
        view.appendChild(navGrid);

        // Feed section
        const feedHeader = document.createElement('div');
        feedHeader.className = 'flintr-msg-header';
        feedHeader.textContent = L.get('phone.feed');
        feedHeader.style.marginTop = '8px';
        view.appendChild(feedHeader);

        for (const post of FEED_POSTS) {
            const postEl = document.createElement('div');
            postEl.className = 'social-feed-post';
            postEl.innerHTML = `
                <div class="social-post-header"><span class="social-post-user">@${post.user}</span></div>
                <div class="social-post-text">${post.text}</div>
                <div class="social-post-footer"><span>\u2764\uFE0F ${post.likes}</span></div>
            `;
            view.appendChild(postEl);
        }

        // Delete account button
        const deleteBtn = document.createElement('button');
        deleteBtn.style.cssText = 'display:block;margin:16px auto 8px;padding:6px 16px;background:rgba(200,60,60,0.15);border:1px solid rgba(200,60,60,0.3);border-radius:6px;color:#c83c3c;font-size:10px;cursor:pointer;';
        deleteBtn.textContent = L.get('phone.delete_account');
        deleteBtn.addEventListener('click', () => {
            setVar('sissysocial_installed', false);
            setVar('sissy_account_created', false);
            setVar('sissy_followers', 0);
            setVar('sissy_posts', 0);
            setVar('sissy_fame', 0);
            this._showView('home');
        });
        view.appendChild(deleteBtn);

        body.appendChild(view);
    }

    _buildSissySocialSetup(body, title, story, iv, setVar, addStat) {
        const view = document.createElement('div');
        view.className = 'phone-app-view';
        view.innerHTML = `
            <div class="phone-app-header" style="background:#e88cb820;border-left:3px solid #e88cb8">
                <span class="phone-app-view-icon" style="color:#e88cb8">\uD83E\uDD33</span>
                <div>
                    <div class="phone-app-view-name" style="color:#e88cb8">SissySocial</div>
                    <div class="phone-app-view-desc">${L.get('phone.create_account_desc')}</div>
                </div>
            </div>
            <div style="padding:16px;text-align:center">
                <div style="font-size:13px;font-weight:600;color:var(--text);margin-bottom:8px">${L.get('phone.create_account')}</div>
                <div style="font-size:11px;color:var(--text-muted);margin-bottom:16px;line-height:1.5">${L.get('phone.sissysocial_desc')}</div>
            </div>
        `;

        const createBtn = document.createElement('button');
        createBtn.className = 'of-withdraw-btn';
        createBtn.style.background = 'rgba(232,140,184,0.2)';
        createBtn.textContent = '\uD83E\uDD33 ' + L.get('phone.create_account');
        createBtn.addEventListener('click', () => {
            setVar('sissy_account_created', true);
            setVar('sissysocial_installed', true);
            setVar('sissy_followers', 5);
            _appAddFem(iv, addStat, 1);
            _appAddShame(iv, addStat, 2);
            body.innerHTML = '';
            this._buildSissySocial(body, null);
        });
        view.appendChild(createBtn);

        const notYetBtn = document.createElement('button');
        notYetBtn.className = 'of-withdraw-btn';
        notYetBtn.style.cssText = 'background:rgba(100,100,100,0.15);margin-top:6px;';
        notYetBtn.textContent = L.get('phone.not_yet');
        notYetBtn.addEventListener('click', () => this._showView('home'));
        view.appendChild(notYetBtn);

        body.appendChild(view);
    }

    _buildSissySocialBrowse(body, title, story, iv, setVar, addStat, snippets, deepSnippets) {
        if (title) title.textContent = 'SissySocial';
        const energy = iv('Energy');

        const view = document.createElement('div');
        view.className = 'phone-app-view';

        if (energy < 10) {
            view.innerHTML = `
                <div style="padding:24px;text-align:center">
                    <div style="font-size:12px;color:var(--text-muted)">You're too tired to scroll right now...</div>
                </div>
            `;
            const backBtn = document.createElement('button');
            backBtn.className = 'of-withdraw-btn';
            backBtn.style.cssText = 'background:rgba(100,100,100,0.15);';
            backBtn.textContent = L.get('phone.back');
            backBtn.addEventListener('click', () => { body.innerHTML = ''; this._buildSissySocial(body, null); });
            view.appendChild(backBtn);
            body.appendChild(view);
            return;
        }

        // Deduct energy
        const newEnergy = Math.max(0, energy - 10);
        setVar('Energy', newEnergy);
        events.emit(Events.STAT_CHANGED, 'Energy', newEnergy);

        // Track if scrolling deep (stored per-call via closure)
        const isDeep = this._ssScrolledDeep || false;

        if (isDeep) {
            _appAddFem(iv, addStat, 1);
            _appAddSub(iv, addStat, 1);
            addStat('Arousal', 4);
        } else {
            _appAddFem(iv, addStat, 1);
            addStat('Arousal', 3);
        }

        const gain = isDeep
            ? 2 + Math.floor(Math.random() * 4)  // 2-5
            : 1 + Math.floor(Math.random() * 3);  // 1-3
        setVar('sissy_followers', iv('sissy_followers') + gain);

        const snippetList = isDeep ? deepSnippets : snippets;
        const snippet = snippetList[Math.floor(Math.random() * snippetList.length)];

        view.innerHTML = `
            <div style="padding:16px">
                <div style="font-size:12px;color:var(--text-muted);line-height:1.6;margin-bottom:12px;white-space:pre-line">${snippet}</div>
                <div style="font-size:11px;color:#e88cb8;text-align:center;margin-bottom:8px">+${gain} followers${isDeep ? ' \u00B7 +1 Sub' : ''}</div>
                <div style="font-size:10px;color:var(--text-faint);text-align:center;font-style:italic">A few new followers from engaging with content.</div>
            </div>
        `;

        // Keep scrolling button (if enough energy)
        if (iv('Energy') >= 10) {
            const keepBtn = document.createElement('button');
            keepBtn.className = 'of-withdraw-btn';
            keepBtn.style.background = 'rgba(232,140,184,0.2)';
            keepBtn.textContent = L.get('phone.keep_scrolling') + ' (E:10)';
            keepBtn.addEventListener('click', () => {
                this._ssScrolledDeep = true;
                body.innerHTML = '';
                this._buildSissySocialBrowse(body, null, story, iv, setVar, addStat, snippets, deepSnippets);
            });
            view.appendChild(keepBtn);
        }

        const backBtn = document.createElement('button');
        backBtn.className = 'of-withdraw-btn';
        backBtn.style.cssText = 'background:rgba(100,100,100,0.15);margin-top:6px;';
        backBtn.textContent = L.get('phone.back_to_feed');
        backBtn.addEventListener('click', () => {
            this._ssScrolledDeep = false;
            body.innerHTML = '';
            this._buildSissySocial(body, null);
        });
        view.appendChild(backBtn);

        body.appendChild(view);
    }

    _buildSissySocialPost(body, title, story, iv, ib, setVar, addStat, postTypes) {
        if (title) title.textContent = 'SissySocial';
        const fem = iv('Femininity');
        const energy = iv('Energy');
        const fame = iv('sissy_fame');
        const followers = iv('sissy_followers');
        const chastity = ib('wearing_chastity');
        // Daily post cap — same anti-spam rule as OnlyFems (foundby:Alpha
        // Rabbit): the ungated Mirror Selfie was a +1-Fem-per-10-Energy pump.
        const today = iv('Day');
        const postsToday = iv('sissysocial_post_day') === today ? iv('sissysocial_posts_today') : 0;
        const dailyCapped = postsToday >= 2;

        const view = document.createElement('div');
        view.className = 'phone-app-view';

        const postHeader = document.createElement('div');
        postHeader.className = 'flintr-msg-header';
        postHeader.textContent = L.get('phone.post_content');
        postHeader.style.marginTop = '8px';
        view.appendChild(postHeader);

        for (const pt of postTypes) {
            const meetsReqs = fem >= pt.reqFem && (!pt.chastity || chastity);
            const hasEnergy = energy >= pt.energy;
            const locked = !meetsReqs;
            const disabled = (!hasEnergy || dailyCapped) && meetsReqs;

            let lockReason = '';
            if (fem < pt.reqFem) lockReason = `Fem ${pt.reqFem}`;
            else if (pt.chastity && !chastity) lockReason = L.get('phone.wear_chastity');
            else if (dailyCapped) lockReason = L.get('phone.onlyfems.daily_cap', 'The algorithm buries spam. More tomorrow.');
            else if (disabled) lockReason = `${L.get('phone.not_enough_energy')} (${pt.energy})`;

            const statPreview = [];
            if (pt.fem > 0) statPreview.push(`+${pt.fem} Fem`);
            if (pt.shame > 0) statPreview.push(`+${pt.shame} Shame`);
            if (pt.sub > 0) statPreview.push(`+${pt.sub} Sub`);

            const card = document.createElement('div');
            card.className = 'flintr-msg-card' + (locked || disabled ? ' dating-locked' : '');
            card.innerHTML = `
                <div class="flintr-msg-sender">${pt.name}</div>
                <div class="flintr-msg-preview">${locked || disabled ? lockReason : `E:${pt.energy} | +${pt.followMin}-${pt.followMax} followers`}</div>
                ${!locked && !disabled ? `<div class="flintr-msg-tier" style="color:#e88cb8">${statPreview.join(' ')}</div>` : ''}
            `;

            if (!locked && !disabled) {
                card.style.cursor = 'pointer';
                card.addEventListener('click', () => {
                    // Energy cost
                    const curEnergy = iv('Energy');
                    setVar('Energy', Math.max(0, curEnergy - pt.energy));
                    events.emit(Events.STAT_CHANGED, 'Energy', Math.max(0, curEnergy - pt.energy));

                    // Follower gain (fame bonus for famous)
                    const isFamous = fame >= 50;
                    const fMin = isFamous ? pt.followMinFamous : pt.followMin;
                    const fMax = isFamous ? pt.followMaxFamous : pt.followMax;
                    const gain = Math.floor(Math.random() * (fMax - fMin + 1)) + fMin;

                    setVar('sissy_followers', iv('sissy_followers') + gain);
                    setVar('sissy_posts', iv('sissy_posts') + 1);
                    setVar('sissysocial_post_day', today);
                    setVar('sissysocial_posts_today', postsToday + 1);

                    // Stat changes
                    if (pt.fem > 0) _appAddFem(iv, addStat, pt.fem);
                    if (pt.shame > 0) _appAddShame(iv, addStat, pt.shame);
                    if (pt.sub > 0) _appAddSub(iv, addStat, pt.sub);
                    if (pt.arousal > 0) addStat('Arousal', pt.arousal);
                    if (pt.fame > 0) _appAddFame(iv, setVar, pt.fame);

                    // Show result
                    body.innerHTML = '';
                    const resultView = document.createElement('div');
                    resultView.className = 'phone-app-view';

                    const narrative = (isFamous && pt.narrativeFamous) ? pt.narrativeFamous : pt.narrative;

                    resultView.innerHTML = `
                        <div style="text-align:center;padding:16px">
                            <div style="font-size:14px;font-weight:600;color:#e88cb8;margin-bottom:8px">${L.get('phone.posted')}</div>
                            <div style="font-size:12px;color:var(--text-muted);margin-bottom:12px;line-height:1.5;white-space:pre-line">${narrative}</div>
                            <div style="font-size:13px;color:#e88cb8;font-weight:600">+${gain} followers</div>
                        </div>
                    `;

                    const postMoreBtn = document.createElement('button');
                    postMoreBtn.className = 'of-withdraw-btn';
                    postMoreBtn.style.background = 'rgba(232,140,184,0.15)';
                    postMoreBtn.textContent = L.get('phone.post_more');
                    postMoreBtn.addEventListener('click', () => {
                        body.innerHTML = '';
                        this._buildSissySocialPost(body, null, story, iv, ib, setVar, addStat, postTypes);
                    });
                    resultView.appendChild(postMoreBtn);

                    const feedBtn = document.createElement('button');
                    feedBtn.className = 'of-withdraw-btn';
                    feedBtn.style.cssText = 'background:rgba(100,100,100,0.15);margin-top:6px;';
                    feedBtn.textContent = L.get('phone.back_to_feed');
                    feedBtn.addEventListener('click', () => { body.innerHTML = ''; this._buildSissySocial(body, null); });
                    resultView.appendChild(feedBtn);

                    body.appendChild(resultView);
                });
            }
            view.appendChild(card);
        }

        // Back button
        const backBtn = document.createElement('button');
        backBtn.className = 'of-withdraw-btn';
        backBtn.style.cssText = 'background:rgba(100,100,100,0.15);margin-top:12px;';
        backBtn.textContent = L.get('phone.back');
        backBtn.addEventListener('click', () => { body.innerHTML = ''; this._buildSissySocial(body, null); });
        view.appendChild(backBtn);

        body.appendChild(view);
    }

    _buildSissySocialDMs(body, title, story, iv, setVar, addStat) {
        if (title) title.textContent = 'SissySocial';
        const followers = iv('sissy_followers');
        const fame = iv('sissy_fame');

        const view = document.createElement('div');
        view.className = 'phone-app-view';

        if (followers < 10) {
            view.innerHTML = '<div style="padding:24px;text-align:center"><div style="font-size:12px;color:var(--text-muted)">' + L.get('phone.no_messages_followers') + '</div></div>';
            const backBtn = document.createElement('button');
            backBtn.className = 'of-withdraw-btn';
            backBtn.style.cssText = 'background:rgba(100,100,100,0.15);';
            backBtn.textContent = L.get('phone.back');
            backBtn.addEventListener('click', () => { body.innerHTML = ''; this._buildSissySocial(body, null); });
            view.appendChild(backBtn);
            body.appendChild(view);
            return;
        }

        // SS_DM text fields (preview/full/accept/result) are L10n'd via
        // `phone.ss_dm.<slug>.{preview,full,accept,result}` keys. EN fallbacks
        // are inlined here so the data still reads cleanly in source.
        const t = (slug, field, fallback) => {
            const key = `phone.ss_dm.${slug}.${field}`;
            const v = L.get(key);
            return (v && v !== key) ? v : fallback;
        };
        const SS_DMS = [
            { _slug: 'pinkie_princess', sender: 'PinkiePrincess', preview: t('pinkie_princess', 'preview', "OMG you're so pretty!"), full: t('pinkie_princess', 'full', "OMG you're so pretty! How long have you been a sissy? Any tips?"), minFollow: 0, minFame: 0, energyCost: 10, followGain: 3, money: 0, fame: 0, fem: 1, shame: 0, sub: 0, arousal: 0, acceptText: t('pinkie_princess', 'accept', 'Chat with them'), resultText: t('pinkie_princess', 'result', "You swap tips, experiences, struggles. It's nice having sissy friends.") },
            { _slug: 'sissy_fan_xx', sender: 'SissyFan_xx', preview: t('sissy_fan_xx', 'preview', 'Your latest post had me...'), full: t('sissy_fan_xx', 'full', 'Your latest post had me shaking. You\'re so pretty it\'s unfair.'), minFollow: 10, minFame: 0, energyCost: 0, followGain: 2, money: 0, fame: 0, fem: 1, shame: 0, sub: 0, arousal: 0, acceptText: t('sissy_fan_xx', 'accept', 'Reply with thanks'), resultText: t('sissy_fan_xx', 'result', 'A quick heart emoji reply. They repost your content.') },
            { _slug: 'new_here_2024', sender: 'NewHere_2024', preview: t('new_here_2024', 'preview', 'How do you walk in heels?'), full: t('new_here_2024', 'full', 'How do you walk in heels? I keep falling over lol'), minFollow: 5, minFame: 0, energyCost: 0, followGain: 2, money: 0, fame: 1, fem: 1, shame: 0, sub: 0, arousal: 0, acceptText: t('new_here_2024', 'accept', 'Share your wisdom'), resultText: t('new_here_2024', 'result', 'You give detailed tips. "Thank you so much! You\'re like a sissy mentor!"') },
            { _slug: 'sir_controls_you', sender: 'SirControlsYou', preview: t('sir_controls_you', 'preview', "I've been watching your progress..."), full: t('sir_controls_you', 'full', "I've been watching your progress. You need an owner.\n\nDaily tasks. Photo assignments. Accountability. I expect obedience."), minFollow: 15, minFame: 0, energyCost: 15, followGain: 0, money: 0, fame: 5, fem: 1, shame: 0, sub: 3, arousal: 5, acceptText: t('sir_controls_you', 'accept', 'I accept...'), resultText: t('sir_controls_you', 'result', '"Good girl. Your first task: post a photo in panties within 24 hours. Tag me."') },
            { _slug: 'top_sissy_mia', sender: 'TopSissy_Mia', preview: t('top_sissy_mia', 'preview', 'Want to do a collab?'), full: t('top_sissy_mia', 'full', 'Hey! Want to do a collab? Our followers would love it!\nMatching outfits. Cute poses. A joint photoshoot (virtual).'), minFollow: 20, minFame: 0, energyCost: 0, followGain: 35, money: 0, fame: 10, fem: 2, shame: 3, sub: 0, arousal: 0, acceptText: t('top_sissy_mia', 'accept', 'Sure!'), resultText: t('top_sissy_mia', 'result', 'You do a joint photoshoot. Matching outfits. Their followers discover you.') },
            { _slug: 'anonymous_user', sender: 'Anonymous_User', preview: t('anonymous_user', 'preview', 'I know who you are.'), full: t('anonymous_user', 'full', 'I know who you are. Nice try hiding your face. Pay up or I share everything.\n\nYou check their profile, 2 followers, no posts. Obvious scam.'), minFollow: 10, minFame: 0, energyCost: 0, followGain: 0, money: 0, fame: 0, fem: 0, shame: 0, sub: 0, arousal: 0, acceptText: t('anonymous_user', 'accept', 'Block and report'), resultText: t('anonymous_user', 'result', 'Blocked. Reported. Probably a bot anyway.\nYour heart rate slowly returns to normal.') },
            { _slug: 'sissyglam_co', sender: 'SissyGlam Co.', preview: t('sissyglam_co', 'preview', 'We LOVE your content!'), full: t('sissyglam_co', 'full', 'We\'d like to sponsor a post for $300 for a photo featuring our product. Interested?'), minFollow: 30, minFame: 50, energyCost: 0, followGain: 22, money: 300, fame: 5, fem: 1, shame: 0, sub: 0, arousal: 0, acceptText: t('sissyglam_co', 'accept', 'Send me the details'), resultText: t('sissyglam_co', 'result', 'A package arrives. Cute pink lingerie set with a branded tag. Payment sent.') },
            { _slug: 'velvet_rose_talent', sender: 'Velvet Rose Talent', preview: t('velvet_rose_talent', 'preview', 'Your engagement metrics are impressive.'), full: t('velvet_rose_talent', 'full', 'We manage online personalities in the alternative lifestyle space.\n15% of bookings, premium clients, event invitations, and protection from doxxing.'), minFollow: 50, minFame: 75, energyCost: 10, followGain: 0, money: 0, fame: 8, fem: 2, shame: 0, sub: 0, arousal: 0, acceptText: t('velvet_rose_talent', 'accept', 'Set up a call'), resultText: t('velvet_rose_talent', 'result', 'The call is real. "Once you sign, your online career goes to the next level."') },
        ];

        const day = iv('Day');
        const eligible = SS_DMS.filter(d => followers >= d.minFollow && fame >= d.minFame);
        const dmCount = Math.min(eligible.length, 2 + (day % 3));
        const seed = day * 11 + 7;
        const dayDMs = [];
        for (let i = 0; i < dmCount && i < eligible.length; i++) {
            dayDMs.push(eligible[(seed + i * 5) % eligible.length]);
        }

        // Namespace by active save slot so daily-DM dedup doesn't bleed
        // across saves that share the same Ink Day.
        const ssSlot = (typeof window !== 'undefined' && window._save) ? window._save.activeSlot : 0;
        const acceptedKey = `PnO_SS_DMs_s${ssSlot}_${day}`;
        // Corrupt/truncated localStorage JSON must not blank the DMs view —
        // body.innerHTML was already cleared by the nav button before this runs.
        let acceptedArr = [];
        try {
            acceptedArr = JSON.parse(localStorage.getItem(acceptedKey) || '[]');
        } catch (e) {
            console.warn('[Phone] SS DMs accepted-set corrupt, resetting:', e?.message);
            try { localStorage.removeItem(acceptedKey); } catch (e2) { /* storage unavailable */ }
        }
        const accepted = new Set(Array.isArray(acceptedArr) ? acceptedArr : []);
        const pending = dayDMs.filter(d => !accepted.has(d.sender));

        const tUi = (key, fallback) => {
            const v = L.get(key);
            return (v && v !== key) ? v : fallback;
        };
        if (pending.length === 0) {
            const empty = document.createElement('div');
            empty.style.cssText = 'padding:24px;text-align:center';
            empty.innerHTML = `<div style="font-size:12px;color:var(--text-muted)">${tUi('phone.ss_dm.no_messages', 'No new messages today. Check back tomorrow!')}</div>`;
            view.appendChild(empty);
        } else {
            const dmHeader = document.createElement('div');
            dmHeader.className = 'flintr-msg-header';
            dmHeader.textContent = `${tUi('phone.ss_dm.dms_header', 'DMs')} (${pending.length})`;
            dmHeader.style.marginTop = '8px';
            view.appendChild(dmHeader);

            for (const dm of pending) {
                const needsEnergy = dm.energyCost > 0;
                const hasEnoughEnergy = !needsEnergy || iv('Energy') >= dm.energyCost;

                const dmCard = document.createElement('div');
                dmCard.className = 'flintr-msg-card' + (!hasEnoughEnergy ? ' dating-locked' : '');
                dmCard.style.cursor = hasEnoughEnergy ? 'pointer' : 'default';
                const reward = dm.followGain > 0 ? `+${dm.followGain}` : dm.money > 0 ? `$${dm.money}` : '';
                dmCard.innerHTML = `
                    <div class="flintr-msg-sender">${dm.sender}</div>
                    <div class="flintr-msg-preview">${dm.preview}${needsEnergy ? ` (E:${dm.energyCost})` : ''}</div>
                    ${!hasEnoughEnergy ? `<div class="flintr-msg-tier" style="color:var(--text-dim)">${tUi('phone.ss_dm.not_enough_energy', 'Not enough energy')}</div>` : reward ? `<div class="flintr-msg-tier" style="color:#e88cb8">${reward}</div>` : ''}
                `;

                if (hasEnoughEnergy) {
                    dmCard.addEventListener('click', () => {
                        // Energy cost
                        if (dm.energyCost > 0) {
                            const curE = iv('Energy');
                            setVar('Energy', Math.max(0, curE - dm.energyCost));
                            events.emit(Events.STAT_CHANGED, 'Energy', Math.max(0, curE - dm.energyCost));
                        }
                        // Apply rewards
                        if (dm.followGain > 0) setVar('sissy_followers', iv('sissy_followers') + dm.followGain);
                        // Owner skim: mirror of OnlyFems withdrawal cut so
                        // SissySocial sponsorship income doesn't bypass the
                        // 20% Owner cut (same gate: deep Owner route, not
                        // moved out). master_earnings_cut tracks the running
                        // total like the OnlyFems path.
                        let netMoney = dm.money, ownerCut = 0;
                        if (dm.money > 0) {
                            if (iv('master_intensity') >= 3 && iv('MasterRelationship') >= 7 && !iv('moved_out')) {
                                ownerCut = Math.max(1, Math.floor(dm.money / 5));
                                setVar('master_earnings_cut', iv('master_earnings_cut') + ownerCut);
                            }
                            netMoney = dm.money - ownerCut;
                            addStat('Money', netMoney);
                        }
                        if (dm.fame > 0) _appAddFame(iv, setVar, dm.fame);
                        if (dm.fem > 0) _appAddFem(iv, addStat, dm.fem);
                        if (dm.shame > 0) _appAddShame(iv, addStat, dm.shame);
                        if (dm.sub > 0) _appAddSub(iv, addStat, dm.sub);
                        if (dm.arousal > 0) addStat('Arousal', dm.arousal);

                        accepted.add(dm.sender);
                        localStorage.setItem(acceptedKey, JSON.stringify([...accepted]));

                        // Show result
                        body.innerHTML = '';
                        const resultView = document.createElement('div');
                        resultView.className = 'phone-app-view';
                        resultView.innerHTML = `
                            <div style="padding:16px">
                                <div style="font-size:12px;color:var(--text-muted);line-height:1.5;white-space:pre-line;margin-bottom:12px">${dm.resultText}</div>
                                ${dm.followGain > 0 || dm.money > 0 ? `<div style="font-size:11px;color:#e88cb8;text-align:center">${dm.followGain > 0 ? `+${dm.followGain} followers` : ''}${dm.followGain > 0 && dm.money > 0 ? '  |  ' : ''}${dm.money > 0 ? `+$${netMoney}${ownerCut > 0 ? ` (${L.get('phone.onlyfems.his_cut', 'his cut')}: $${ownerCut})` : ''}` : ''}</div>` : ''}
                            </div>
                        `;
                        const backToDms = document.createElement('button');
                        backToDms.className = 'of-withdraw-btn';
                        backToDms.style.background = 'rgba(232,140,184,0.15)';
                        backToDms.textContent = L.get('phone.back_to_dms');
                        backToDms.addEventListener('click', () => { body.innerHTML = ''; this._buildSissySocialDMs(body, null, story, iv, setVar, addStat); });
                        resultView.appendChild(backToDms);

                        const feedBtn = document.createElement('button');
                        feedBtn.className = 'of-withdraw-btn';
                        feedBtn.style.cssText = 'background:rgba(100,100,100,0.15);margin-top:6px;';
                        feedBtn.textContent = L.get('phone.back_to_feed');
                        feedBtn.addEventListener('click', () => { body.innerHTML = ''; this._buildSissySocial(body, null); });
                        resultView.appendChild(feedBtn);

                        body.appendChild(resultView);
                    });
                }
                view.appendChild(dmCard);
            }
        }

        // Back button
        const backBtn = document.createElement('button');
        backBtn.className = 'of-withdraw-btn';
        backBtn.style.cssText = 'background:rgba(100,100,100,0.15);margin-top:12px;';
        backBtn.textContent = L.get('phone.back');
        backBtn.addEventListener('click', () => { body.innerHTML = ''; this._buildSissySocial(body, null); });
        view.appendChild(backBtn);

        body.appendChild(view);
    }

    // ── DareMe App ────────────────────────────────────────────────────

    _buildDareMe(body, title) {
        if (title) title.textContent = L.get('dareme.title') || 'DareMe';
        const story = this._story;
        if (!story) return;

        const iv = (v) => { try { return story.variablesState[v] || 0; } catch(e) { return 0; } };
        const ib = (v) => { try { const val = story.variablesState[v]; return val === true || val === 1; } catch(e) { return false; } };
        const setVar = (v, val) => { try { story.variablesState[v] = val; } catch(e) {} };
        const addStat = (v, amount) => {
            const cur = iv(v);
            setVar(v, cur + amount);
            events.emit(Events.STAT_CHANGED, v, cur + amount);
        };

        const fem = iv('Femininity');
        const dareActive = iv('dare_active');
        // v0.2.7 audit M2: 1-day cooldown between accepts. dare_skip_day was
        // already set on reject; now also set on accept/completion in DareMe.ink
        // so the speed-run path (11 dares in 3-4 days) is capped.
        const dareSkipDay = iv('dare_skip_day');
        const currentDay = iv('Day');
        const cooldownOk = (typeof currentDay !== 'number' || typeof dareSkipDay !== 'number')
            ? true
            : (currentDay - dareSkipDay >= 1);
        const daresCompleted = iv('dares_completed');

        // Dare definitions
        const DARES = [
            // Tier 1, Getting Started
            { id: 1,  tier: 1, loc: 'bar',   locIcon: '\uD83C\uDF78', title: 'dareme.dare1.title',  desc: 'dareme.dare1.desc',  rewards: [{ stat: 'Fem', val: '+2' }, { stat: 'Shame', val: '+2' }, { stat: '$', val: '+15' }] },
            { id: 2,  tier: 1, loc: 'park',  locIcon: '\uD83C\uDF33', title: 'dareme.dare2.title',  desc: 'dareme.dare2.desc',  rewards: [{ stat: 'Fem', val: '+2' }, { stat: 'Shame', val: '+2' }, { stat: 'Fame', val: '+1' }] },
            { id: 3,  tier: 1, loc: 'club',  locIcon: '\uD83E\uDE69', title: 'dareme.dare3.title',  desc: 'dareme.dare3.desc',  rewards: [{ stat: 'Fem', val: '+2' }, { stat: 'Sub', val: '+1' }, { stat: 'Shame', val: '+2' }, { stat: 'Fame', val: '+1' }] },
            { id: 4,  tier: 1, loc: 'beach', locIcon: '\uD83C\uDFD6\uFE0F', title: 'dareme.dare4.title',  desc: 'dareme.dare4.desc',  rewards: [{ stat: 'Fem', val: '+2' }, { stat: 'Shame', val: '+2' }, { stat: 'Arousal', val: '+5' }] },
            // Tier 2, Pushing Limits
            { id: 5,  tier: 2, loc: 'bar',   locIcon: '\uD83C\uDF78', title: 'dareme.dare5.title',  desc: 'dareme.dare5.desc',  rewards: [{ stat: 'Fem', val: '+2' }, { stat: 'Sub', val: '+2' }, { stat: 'Shame', val: '+3' }, { stat: 'Arousal', val: '+5' }] },
            { id: 6,  tier: 2, loc: 'club',  locIcon: '\uD83E\uDE69', title: 'dareme.dare6.title',  desc: 'dareme.dare6.desc',  rewards: [{ stat: 'Fem', val: '+2' }, { stat: 'Sub', val: '+1' }, { stat: 'Shame', val: '+3' }, { stat: 'Arousal', val: '+8' }] },
            { id: 7,  tier: 2, loc: 'beach', locIcon: '\uD83C\uDFD6\uFE0F', title: 'dareme.dare7.title',  desc: 'dareme.dare7.desc',  rewards: [{ stat: 'Fem', val: '+3' }, { stat: 'Shame', val: '+4' }, { stat: 'Arousal', val: '+5' }] },
            // Tier 3, No Limits
            { id: 8,  tier: 3, loc: 'bar',   locIcon: '\uD83C\uDF78', title: 'dareme.dare8.title',  desc: 'dareme.dare8.desc',  rewards: [{ stat: 'Fem', val: '+2' }, { stat: 'Sub', val: '+3' }, { stat: 'Shame', val: '+4' }, { stat: 'Arousal', val: '+15' }, { stat: '$', val: '+20' }] },
            { id: 9,  tier: 3, loc: 'park',  locIcon: '\uD83C\uDF33', title: 'dareme.dare9.title',  desc: 'dareme.dare9.desc',  rewards: [{ stat: 'Fem', val: '+2' }, { stat: 'Sub', val: '+3' }, { stat: 'Shame', val: '+5' }, { stat: 'Arousal', val: '+15' }] },
            { id: 10, tier: 3, loc: 'club',  locIcon: '\uD83E\uDE69', title: 'dareme.dare10.title', desc: 'dareme.dare10.desc', rewards: [{ stat: 'Fem', val: '+2' }, { stat: 'Sub', val: '+3' }, { stat: 'Shame', val: '+5' }, { stat: 'Arousal', val: '+15' }] },
            { id: 11, tier: 3, loc: 'beach', locIcon: '\uD83C\uDFD6\uFE0F', title: 'dareme.dare11.title', desc: 'dareme.dare11.desc', rewards: [{ stat: 'Fem', val: '+3' }, { stat: 'Sub', val: '+3' }, { stat: 'Shame', val: '+5' }, { stat: 'Arousal', val: '+20' }] },
        ];

        const TIERS = [
            { id: 1, l10n: 'dareme.tier1', color: '#5cd89c', bgColor: 'rgba(92,216,156,0.12)', borderColor: 'rgba(92,216,156,0.3)', unlocked: true },
            { id: 2, l10n: 'dareme.tier2', color: '#e8a040', bgColor: 'rgba(232,160,64,0.12)', borderColor: 'rgba(232,160,64,0.3)', unlocked: fem >= 35 && daresCompleted >= 2 },
            { id: 3, l10n: 'dareme.tier3', color: '#e06878', bgColor: 'rgba(224,104,120,0.12)', borderColor: 'rgba(224,104,120,0.3)', unlocked: fem >= 50 && daresCompleted >= 5 },
        ];

        const LOC_NAMES = { bar: 'loc.bar', park: 'loc.park', club: 'loc.club', beach: 'loc.beach' };

        // ── Main view ──
        const view = document.createElement('div');
        view.className = 'phone-app-view';

        // Header
        view.innerHTML = `
            <div class="phone-app-header" style="background:rgba(232,88,40,0.12);border-left:3px solid #e85828">
                <span class="phone-app-view-icon" style="color:#e85828">\uD83D\uDD25</span>
                <div>
                    <div class="phone-app-view-name" style="color:#e85828">${L.get('dareme.title') || 'DareMe'}</div>
                    <div class="phone-app-view-desc">${L.get('dareme.desc') || 'Anonymous dares from strangers in your city.'}</div>
                </div>
            </div>
        `;

        // Stats row
        const statsRow = document.createElement('div');
        statsRow.style.cssText = 'display:flex;justify-content:space-between;padding:8px 14px;font-size:11px;color:var(--text-muted);';
        const activeDare = dareActive > 0 ? DARES.find(d => d.id === dareActive) : null;
        const activeLabel = activeDare
            ? `<span style="color:#e8a040;font-weight:600">${L.get(activeDare.title)}</span>`
            : `<span style="opacity:0.5">${L.get('dareme.none_active') || 'No active dare'}</span>`;
        statsRow.innerHTML = `
            <span><strong style="color:var(--text)">${L.get('dareme.completed') || 'Completed'}:</strong> ${daresCompleted}/11</span>
            <span><strong style="color:var(--text)">${L.get('dareme.active') || 'Active'}:</strong> ${activeLabel}</span>
        `;
        view.appendChild(statsRow);

        // Dare list grouped by tier
        const listContainer = document.createElement('div');
        listContainer.style.cssText = 'padding:4px 10px 16px;display:flex;flex-direction:column;gap:8px;overflow-y:auto;';

        for (const tier of TIERS) {
            const tierDares = DARES.filter(d => d.tier === tier.id);
            const tierLocked = !tier.unlocked;
            const patreonLocked = false; // v0.2.2: all tiers now free

            // Tier header
            const tierHeader = document.createElement('div');
            tierHeader.style.cssText = `display:flex;align-items:center;gap:8px;padding:6px 10px;background:${tier.bgColor};border:1px solid ${tier.borderColor};border-radius:8px;margin-top:4px;`;
            let headerHTML = `<span style="font-weight:700;font-size:12px;color:${tier.color}">${L.get(tier.l10n)}</span>`;
            if (patreonLocked) {
                headerHTML += `<span style="font-size:9px;background:rgba(232,200,96,0.2);color:#e8c860;padding:1px 6px;border-radius:4px;font-weight:600">${L.get('dareme.patreon_locked') || 'Patreon'}</span>`;
            } else if (tierLocked) {
                const reqText = tier.id === 2 ? 'Fem \u226535, 2+ done' : 'Fem \u226550, 5+ done';
                headerHTML += `<span style="font-size:9px;color:var(--text-faint);margin-left:auto">\uD83D\uDD12 ${reqText}</span>`;
            }
            tierHeader.innerHTML = headerHTML;
            listContainer.appendChild(tierHeader);

            // Dare cards
            for (const dare of tierDares) {
                const isDone = ib('dare_done_' + dare.id);
                const isActive = dareActive === dare.id;
                const isLocked = tierLocked || patreonLocked;
                const canAccept = !isLocked && !isDone && !isActive && dareActive === 0 && cooldownOk;

                const card = document.createElement('div');
                card.className = 'dareme-card' + (isActive ? ' dareme-active' : '') + (isDone ? ' dareme-done' : '') + (isLocked ? ' dareme-locked' : '');
                card.style.cssText = `display:flex;flex-direction:column;gap:4px;padding:10px 12px;border-radius:8px;border:1px solid ${isActive ? '#e8a040' : isDone ? 'rgba(92,216,156,0.3)' : isLocked ? 'rgba(100,100,100,0.2)' : 'rgba(46,34,66,0.4)'};background:${isActive ? 'rgba(232,160,64,0.08)' : isDone ? 'rgba(92,216,156,0.06)' : isLocked ? 'rgba(30,25,40,0.3)' : 'rgba(20,15,30,0.5)'};${isActive ? 'box-shadow:0 0 8px rgba(232,160,64,0.2);' : ''}${isLocked ? 'opacity:0.55;' : ''}`;

                // Top row: location + title + status
                const topRow = document.createElement('div');
                topRow.style.cssText = 'display:flex;align-items:center;gap:8px;';

                const locSpan = document.createElement('span');
                locSpan.style.cssText = 'font-size:16px;';
                locSpan.textContent = dare.locIcon;

                const titleSpan = document.createElement('span');
                titleSpan.style.cssText = 'font-size:12px;font-weight:600;color:var(--text);flex:1;';
                titleSpan.textContent = L.get(dare.title);

                topRow.appendChild(locSpan);
                topRow.appendChild(titleSpan);

                // Status / action
                if (isDone) {
                    const check = document.createElement('span');
                    check.style.cssText = 'color:#5cd89c;font-size:14px;';
                    check.textContent = '\u2705';
                    topRow.appendChild(check);
                } else if (isActive) {
                    const activeTag = document.createElement('span');
                    activeTag.style.cssText = 'font-size:9px;background:rgba(232,160,64,0.2);color:#e8a040;padding:2px 8px;border-radius:4px;font-weight:600;white-space:nowrap;';
                    const locName = L.get(LOC_NAMES[dare.loc]) || dare.loc;
                    activeTag.textContent = (L.get('dareme.go_to') || 'Go to {0}').replace('{0}', locName);
                    topRow.appendChild(activeTag);
                } else if (isLocked) {
                    const lock = document.createElement('span');
                    lock.style.cssText = 'color:var(--text-faint);font-size:12px;';
                    lock.textContent = '\uD83D\uDD12';
                    topRow.appendChild(lock);
                } else {
                    const acceptBtn = document.createElement('button');
                    acceptBtn.style.cssText = `padding:3px 10px;border-radius:6px;border:none;font-size:10px;font-weight:600;cursor:pointer;color:white;background:${canAccept ? '#e85828' : 'rgba(100,100,100,0.3)'};${canAccept ? '' : 'opacity:0.4;cursor:not-allowed;'}`;
                    acceptBtn.textContent = L.get('dareme.accept') || 'Accept';
                    acceptBtn.disabled = !canAccept;
                    if (canAccept) {
                        acceptBtn.addEventListener('click', () => {
                            setVar('dare_active', dare.id);
                            setVar('dare_accepted', true);
                            // v0.2.7 audit M2: arm the cooldown the moment a dare
                            // is accepted so the next pick is gated to tomorrow.
                            setVar('dare_skip_day', currentDay);
                            const locName = L.get(LOC_NAMES[dare.loc]) || dare.loc;
                            const toastMsg = (L.get('dareme.dare_active_toast') || 'Dare accepted! Go to {0} to complete it.').replace('{0}', locName);
                            events.emit(Events.TOAST_REQUEST, toastMsg, 'toast-info');
                            // Rebuild to refresh state
                            body.innerHTML = '';
                            this._buildDareMe(body, null);
                        });
                    } else if (dareActive > 0) {
                        acceptBtn.title = L.get('dareme.already_active') || 'Complete your current dare first';
                    } else if (!cooldownOk) {
                        acceptBtn.title = L.get('dareme.cooldown') || 'New dares only once a day';
                    }
                    topRow.appendChild(acceptBtn);
                }

                card.appendChild(topRow);

                // Description
                const descEl = document.createElement('div');
                descEl.style.cssText = `font-size:10px;color:var(--text-muted);line-height:1.4;${isLocked ? 'filter:blur(2px);user-select:none;' : ''}`;
                descEl.textContent = L.get(dare.desc);
                card.appendChild(descEl);

                // Reward badges
                if (!isLocked) {
                    const badges = document.createElement('div');
                    badges.style.cssText = 'display:flex;gap:4px;flex-wrap:wrap;margin-top:2px;';
                    for (const r of dare.rewards) {
                        const badge = document.createElement('span');
                        let bg, color;
                        if (r.stat === 'Fem') { bg = 'rgba(232,140,184,0.15)'; color = '#e88cb8'; }
                        else if (r.stat === 'Sub') { bg = 'rgba(140,100,200,0.15)'; color = '#8c64c8'; }
                        else if (r.stat === 'Shame') { bg = 'rgba(180,140,100,0.15)'; color = '#b48c64'; }
                        else if (r.stat === 'Arousal') { bg = 'rgba(200,80,80,0.15)'; color = '#c85050'; }
                        else if (r.stat === 'Fame') { bg = 'rgba(92,192,216,0.15)'; color = '#5cc0d8'; }
                        else if (r.stat === '$') { bg = 'rgba(92,216,156,0.15)'; color = '#5cd89c'; }
                        else { bg = 'rgba(100,100,100,0.15)'; color = '#888'; }
                        badge.style.cssText = `font-size:9px;padding:1px 6px;border-radius:4px;font-weight:600;background:${bg};color:${color};`;
                        badge.textContent = `${r.stat} ${r.val}`;
                        badges.appendChild(badge);
                    }
                    card.appendChild(badges);
                }

                listContainer.appendChild(card);
            }
        }

        view.appendChild(listContainer);
        body.appendChild(view);
    }

    // ── HypnoMind App (Feminization Conditioning, 0.2.9, Patreon) ──────
    // Read-only via story.variablesState (like DareMe). DOM cards ONLY — no
    // inline <video> — so it inherits the §91 audio-zombie protection via the
    // FORCED_EVENT_STARTED → close() at construction. Session buttons close the
    // phone BEFORE emitting phone:forcedEvent (phone-forced-event-cleanup invariant).
    // 0.2.9 Phase C: in-fiction consent screen for HypnoMind's uncancellable
    // public events. Yes -> hypnomind_consented=true (permanent) + render app.
    // No -> back to home, NO flag stored, so it re-prompts next open.
    _buildHypnoMindConsent(body, title) {
        if (title) title.textContent = L.get('hypnomind.title') || 'HypnoMind';
        const story = this._story;
        const view = document.createElement('div');
        view.className = 'phone-app-view';
        view.innerHTML = `
            <div class="phone-app-header" style="background:rgba(196,112,224,0.12);border-left:3px solid #c470e0">
                <span class="phone-app-view-icon" style="color:#c470e0">🌀</span>
                <div>
                    <div class="phone-app-view-name" style="color:#c470e0">${L.get('hypnomind.title') || 'HypnoMind'}</div>
                    <div class="phone-app-view-desc">${L.get('hypnomind.consent.sub') || 'Before you let the voice in.'}</div>
                </div>
            </div>
            <div style="padding:16px 16px 8px;font-size:12px;line-height:1.6;color:var(--text-muted)">
                <p style="margin:0 0 10px">${L.get('hypnomind.consent.body') || "This isn't the laptop. The conditioning here doesn't stay on the screen, it follows you out into the world."}</p>
                <p style="margin:0 0 10px;color:#e88cb8">${L.get('hypnomind.consent.warn') || "It will trigger moments in public you cannot stop, cannot refuse, cannot take back. Once you say yes, you keep saying yes."}</p>
                <p style="margin:0">${L.get('hypnomind.consent.ask') || 'Do you let HypnoMind in?'}</p>
            </div>
        `;
        const btnRow = document.createElement('div');
        btnRow.style.cssText = 'display:flex;flex-direction:column;gap:8px;padding:12px 16px 20px;';
        const yes = document.createElement('button');
        yes.style.cssText = 'padding:11px;border-radius:8px;border:none;font-size:13px;font-weight:700;cursor:pointer;color:white;background:#c470e0;';
        yes.textContent = L.get('hypnomind.consent.yes') || 'I Accept';
        yes.addEventListener('click', () => {
            try { story.variablesState['hypnomind_consented'] = true; } catch (e) {}
            body.innerHTML = '';
            this._buildHypnoMind(body, title);
        });
        const no = document.createElement('button');
        no.style.cssText = 'padding:11px;border-radius:8px;border:1px solid rgba(150,150,150,0.4);font-size:13px;font-weight:600;cursor:pointer;color:var(--text-muted);background:transparent;';
        no.textContent = L.get('hypnomind.consent.no') || 'Not yet.';
        no.addEventListener('click', () => { this._showView('home'); });
        btnRow.appendChild(yes); btnRow.appendChild(no);
        view.appendChild(btnRow);
        body.appendChild(view);
    }

    _buildHypnoMind(body, title) {
        if (title) title.textContent = L.get('hypnomind.title') || 'HypnoMind';
        const story = this._story;
        if (!story) return;

        const iv = (v) => { try { return story.variablesState[v] || 0; } catch(e) { return 0; } };

        const trance = Math.max(0, Math.min(100, iv('hypnomind_depth')));
        const streak = iv('hypno_streak');
        const stage = iv('hypnomind_stage');
        const sessionsDone = iv('hypnomind_sessions_done');

        // 0.2.9.0.7: daily session cap so the 6 courses can't be spammed in one
        // day. Counter (hypnomind_sessions_today) is incremented Ink-side in
        // apply_hypnomind_session and reset overnight in Sleep.ink. Tunable here.
        const HYPNO_SESSIONS_PER_DAY = 2;
        const sessionsToday = iv('hypnomind_sessions_today');
        const capReached = sessionsToday >= HYPNO_SESSIONS_PER_DAY;
        // Shared lock hint for the 6 courses: before the intro it's "do the
        // welcome first"; after the intro the only thing that locks them is the
        // daily cap, so it's "come back tomorrow".
        const catLockHint = stage < 1
            ? (L.get('hypnomind.cat.lock') || 'Do the welcome session first')
            : (L.get('hypnomind.cap.lock') || "You've conditioned enough today. Come back tomorrow.");

        // 0.2.9 Phase C: one-time consent gate. No 'declined' flag is stored —
        // pressing No just leaves the app, so the warning re-appears on the next
        // open (misclick-safe). Only Yes sets hypnomind_consented permanently.
        const consented = iv('hypnomind_consented');
        if (!(consented === true || consented === 1)) {
            this._buildHypnoMindConsent(body, title);
            return;
        }

        // Session catalogue. `available` decides Open-button vs 🔒+hint.
        const SESSIONS = [
            { id: 'intro', knot: 'HypnoMind_Intro',
              title: L.get('hypnomind.s_intro.title') || "The Voice's Welcome",
              desc: L.get('hypnomind.s_intro.desc') || "The voice's first session. It walks you down for the first time.",
              energy: 10, time: 1, rewards: [{ s: 'Fem', v: '+1' }, { s: 'Sub', v: '+1' }],
              available: stage < 1, done: stage >= 1 },
            // 0.2.9 Phase D2: six pickable conditioning categories (replace the old
            // Daily/Deepening/Surrender). Available once the Intro is done; each
            // opens its HMCat_<X> forced event, deepens via hypnomind_depth.
            { id: 'fem', knot: 'HMCat_Fem',
              title: L.get('hypnomind.cat_fem.title') || 'Feminization',
              desc: L.get('hypnomind.cat_fem.desc') || 'PRETTY. SOFT. The voice washes the boy off you one layer at a time.',
              energy: 15, time: 1, rewards: [{ s: 'Fem', v: '+2' }, { s: 'Sub', v: '+1' }],
              available: stage >= 1 && !capReached, repeatable: true,
              lockHint: catLockHint },
            { id: 'obey', knot: 'HMCat_Obey',
              title: L.get('hypnomind.cat_obey.title') || 'Obedience',
              desc: L.get('hypnomind.cat_obey.desc') || 'OBEY. Kneel for the screen. Stop deciding things.',
              energy: 15, time: 1, rewards: [{ s: 'Sub', v: '+2' }, { s: 'Fem', v: '+1' }],
              available: stage >= 1 && !capReached, repeatable: true,
              lockHint: catLockHint },
            { id: 'oral', knot: 'HMCat_Oral',
              title: L.get('hypnomind.cat_oral.title') || 'Oral Devotion',
              desc: L.get('hypnomind.cat_oral.desc') || 'The voice teaches your mouth what it is for. It remembers in public too.',
              energy: 15, time: 1, rewards: [{ s: 'Sub', v: '+2' }, { s: 'Fem', v: '+1' }],
              available: stage >= 1 && !capReached, repeatable: true,
              lockHint: catLockHint },
            { id: 'bimbo', knot: 'HMCat_Bimbo',
              title: L.get('hypnomind.cat_bimbo.title') || 'Bimbo',
              desc: L.get('hypnomind.cat_bimbo.desc') || 'EMPTY. Put the thinking down. Dumb, pink, happy.',
              energy: 15, time: 1, rewards: [{ s: 'Fem', v: '+2' }, { s: 'Sub', v: '+1' }],
              available: stage >= 1 && !capReached, repeatable: true,
              lockHint: catLockHint },
            { id: 'exhib', knot: 'HMCat_Exhib',
              title: L.get('hypnomind.cat_exhib.title') || 'Exhibition',
              desc: L.get('hypnomind.cat_exhib.desc') || 'You want to be seen. The voice makes sure you will be.',
              energy: 15, time: 1, rewards: [{ s: 'Shame', v: '+2' }, { s: 'Fem', v: '+1' }],
              available: stage >= 1 && !capReached, repeatable: true,
              lockHint: catLockHint },
            { id: 'anal', knot: 'HMCat_Anal',
              title: L.get('hypnomind.cat_anal.title') || 'Anal Training',
              desc: L.get('hypnomind.cat_anal.desc') || 'Relax. Open up. Trained to want it filled.',
              energy: 15, time: 1, rewards: [{ s: 'Sub', v: '+2' }, { s: 'Fem', v: '+1' }],
              available: stage >= 1 && !capReached, repeatable: true,
              lockHint: catLockHint },
        ];

        const view = document.createElement('div');
        view.className = 'phone-app-view';

        // Header — Sasha-coded purple, matches her SMS bubble colour.
        view.innerHTML = `
            <div class="phone-app-header" style="background:rgba(196,112,224,0.12);border-left:3px solid #c470e0">
                <span class="phone-app-view-icon" style="color:#c470e0">🌀</span>
                <div>
                    <div class="phone-app-view-name" style="color:#c470e0">${L.get('hypnomind.title') || 'HypnoMind'}</div>
                    <div class="phone-app-view-desc">${L.get('hypnomind.subtitle') || 'Feminization conditioning. The voice in your ear, every day.'}</div>
                </div>
            </div>
        `;

        // Conditioning gauge + streak + stage row.
        const gauge = document.createElement('div');
        gauge.style.cssText = 'padding:10px 14px 4px;';
        const stageLabels = ['Not started', 'Conditioning', 'Deepening', 'Surrendered'];
        const stageTxt = L.get('hypnomind.stage' + stage) || stageLabels[Math.max(0, Math.min(3, stage))];
        const _cats = ['fem', 'obey', 'oral', 'bimbo', 'exhib', 'anal'];
        const catsTried = _cats.filter((c) => iv('hypnomind_tried_' + c) === true).length;
        // Streak depth bonus surfaced in the UI (the buff already exists in
        // apply_hypnomind_session: +2 at streak >=3, +4 at >=7; decay halved at >=5).
        const streakBonus = streak >= 7 ? 4 : (streak >= 3 ? 2 : 0);
        gauge.innerHTML = `
            <div style="display:flex;justify-content:space-between;font-size:11px;color:var(--text-muted);margin-bottom:4px;">
                <span><strong style="color:var(--text)">${L.get('hypnomind.depth') || 'Conditioning'}</strong> <span style="color:var(--text-faint);font-weight:600">${trance}/100</span></span>
                <span style="color:#c470e0;font-weight:600">${stageTxt}</span>
            </div>
            <div style="height:8px;border-radius:5px;background:rgba(196,112,224,0.15);overflow:hidden;">
                <div style="height:100%;width:${trance}%;background:linear-gradient(90deg,#9b59b6,#c470e0);border-radius:5px;"></div>
            </div>
            <div style="display:flex;justify-content:space-between;font-size:10px;color:var(--text-faint);margin-top:5px;">
                <span>🔥 ${(L.get('hypnomind.streak') || '{0} day streak').replace('{0}', streak)}${streakBonus ? ` <span style="color:#9bc05a;font-weight:600">+${streakBonus}</span>` : ''}</span>
                <span>🌀 ${(L.get('hypnomind.cats_tried') || '{0}/6 lessons').replace('{0}', catsTried)}</span>
                <span style="${capReached ? 'color:#c89090;font-weight:600;' : ''}">📅 ${(L.get('hypnomind.today') || '{0}/{1} today').replace('{0}', sessionsToday).replace('{1}', HYPNO_SESSIONS_PER_DAY)}</span>
            </div>
        `;
        view.appendChild(gauge);

        // State-aware status banner — ALWAYS shown (the old first-open tutorial
        // vanished after session 1, right before the out-of-app events arm at
        // stage 2). Before Deepening it states the threshold; once armed it tells
        // the player the conditioning now fires on its own out in the world, names
        // the locations, and that it scales with depth — i.e. go visit places.
        {
            const banner = document.createElement('div');
            banner.style.cssText = 'margin:4px 14px 0;padding:9px 11px;border-radius:8px;background:rgba(196,112,224,0.10);border:1px solid rgba(196,112,224,0.25);font-size:10px;line-height:1.5;color:var(--text-muted);';
            if (stage >= 2) {
                banner.innerHTML = `<strong style="color:#c470e0">${L.get('hypnomind.active.title') || 'Loose in the world'}</strong><br>${L.get('hypnomind.active.body') || "It isn't only the app anymore. The conditioning surfaces on its own when you go out, the bar, the club, the mall, the gym, the park, the street, work, the cafe, the salon. The deeper you are, the more often it happens."}`;
            } else {
                banner.innerHTML = `<strong style="color:#c470e0">${L.get('hypnomind.tut.title') || 'How it works'}</strong><br>${L.get('hypnomind.tut.body') || 'Pick a category below to start a session. Each one deepens the gauge above. After your third session (Deepening) the conditioning starts following you out into the world, surfacing on its own when you visit places around the city.'}`;
            }
            view.appendChild(banner);
        }

        // Session GRID — 2-per-row image cards in the thumb-choice style: the
        // WHOLE card is the button (no separate Start button), so all 6 category
        // cards + the full-width intro fit on screen with little/no scrolling.
        const listContainer = document.createElement('div');
        listContainer.style.cssText = 'padding:8px 10px 16px;display:grid;grid-template-columns:1fr 1fr;gap:8px;';

        for (const s of SESSIONS) {
            const locked = !s.available;
            const fullW = (s.id === 'intro');
            const card = document.createElement('button');
            card.type = 'button';
            card.className = 'hm-session-card' + (locked ? ' locked' : '');
            card.style.cssText = `${fullW ? 'grid-column:1 / -1;' : ''}display:flex;flex-direction:column;padding:0;overflow:hidden;border-radius:10px;border:1px solid ${locked ? 'rgba(100,100,100,0.25)' : 'rgba(196,112,224,0.35)'};background:${locked ? 'rgba(30,25,40,0.4)' : 'rgba(40,25,55,0.5)'};text-align:left;font:inherit;color:inherit;${locked ? 'opacity:0.55;cursor:default;' : 'cursor:pointer;'}`;

            // Thumbnail (top). Intro / missing art → spiral gradient.
            const thumbName = (s.id && s.id !== 'intro') ? `hmcat_${s.id}_thumb` : null;
            const thumb = document.createElement('div');
            thumb.style.cssText = `position:relative;width:100%;height:${fullW ? '54' : '82'}px;overflow:hidden;display:flex;align-items:center;justify-content:center;font-size:26px;background:linear-gradient(135deg,#9b59b6,#c470e0);`;
            if (thumbName) {
                const timg = document.createElement('img');
                const _r = window._pno_resolveImage;
                timg.src = (_r && _r(thumbName)) || `assets/images/UI/${thumbName}.png`;
                timg.alt = '';
                timg.style.cssText = `width:100%;height:100%;object-fit:cover;filter:${locked ? 'grayscale(0.6) brightness(0.5)' : 'brightness(0.82)'};`;
                timg.onerror = function () { this.remove(); thumb.textContent = '🌀'; };
                thumb.appendChild(timg);
            } else {
                thumb.textContent = '🌀';
            }
            if (locked) {
                const lk = document.createElement('span');
                lk.textContent = '🔒';
                lk.style.cssText = 'position:absolute;top:6px;right:8px;font-size:14px;text-shadow:0 1px 3px #000;';
                thumb.appendChild(lk);
            }
            card.appendChild(thumb);

            // Body: title + meta (or lock hint).
            const cardBody = document.createElement('div');
            cardBody.style.cssText = 'padding:7px 9px 9px;display:flex;flex-direction:column;gap:4px;';
            const title = document.createElement('div');
            title.style.cssText = 'font-size:12px;font-weight:700;color:var(--text);line-height:1.2;';
            title.textContent = s.title + (s.done ? ' ✓' : '');
            cardBody.appendChild(title);

            if (locked) {
                const hint = document.createElement('div');
                hint.style.cssText = 'font-size:9px;color:var(--text-faint);line-height:1.3;';
                hint.textContent = s.lockHint || s.desc;
                cardBody.appendChild(hint);
            } else {
                const meta = document.createElement('div');
                meta.style.cssText = 'display:flex;gap:4px;flex-wrap:wrap;align-items:center;';
                const cost = document.createElement('span');
                cost.style.cssText = 'font-size:9px;padding:1px 6px;border-radius:4px;font-weight:600;background:rgba(120,160,90,0.15);color:#9bc05a;';
                cost.textContent = `⚡${s.energy} · 🕐${s.time}`;
                meta.appendChild(cost);
                for (const r of s.rewards) {
                    const badge = document.createElement('span');
                    let bg, color;
                    if (r.s === 'Fem') { bg = 'rgba(232,140,184,0.15)'; color = '#e88cb8'; }
                    else if (r.s === 'Sub') { bg = 'rgba(140,100,200,0.15)'; color = '#8c64c8'; }
                    else { bg = 'rgba(100,100,100,0.15)'; color = '#888'; }
                    badge.style.cssText = `font-size:9px;padding:1px 6px;border-radius:4px;font-weight:600;background:${bg};color:${color};`;
                    badge.textContent = `${r.s} ${r.v}`;
                    meta.appendChild(badge);
                }
                cardBody.appendChild(meta);
            }
            card.appendChild(cardBody);

            if (locked) {
                card.disabled = true;
            } else {
                card.addEventListener('click', () => {
                    // Close phone BEFORE emitting (forced-event cleanup invariant).
                    // 'PhoneApp' source = voluntary session, bypasses the SMS 2/day
                    // throttle (else it defers until next day, foundby:Niya Weiss).
                    this.close();
                    events.emit('phone:forcedEvent', s.knot, 'PhoneApp');
                });
            }

            listContainer.appendChild(card);
        }

        // HypnoMind FREED for 0.3 (owner 2026-07-07); Patreon upsell and the
        // v029 kill-switch are both gone — install flag is the only gate.

        view.appendChild(listContainer);
        body.appendChild(view);
    }

    deserialize(saved) {
        this._threads.clear();
        this._unread.clear();
        if (!saved) { this._updateBadge(); return; }

        if (saved.threads) {
            for (const [npcId, msgs] of Object.entries(saved.threads)) {
                this._threads.set(npcId, msgs.map(m => ({
                    ...m,
                    id: Date.now() + Math.random(),
                    timestamp: Date.now(),
                })));
            }
        }
        if (saved.unread) {
            for (const id of saved.unread) this._unread.add(id);
        }
        if (saved.pendingReplies) {
            for (const [npcId, replies] of Object.entries(saved.pendingReplies)) {
                const thread = this._threads.get(npcId);
                if (thread) thread._pendingReplies = replies;
            }
        }
        if (saved.replyQueues) {
            for (const [npcId, queue] of Object.entries(saved.replyQueues)) {
                const thread = this._threads.get(npcId);
                if (thread && Array.isArray(queue) && queue.length > 0) thread._replyQueue = queue;
            }
        }
        this._updateBadge();
    }

}

/** @type {PhoneUI} */
export const phoneUI = new PhoneUI();
